import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Subject, Observable, forkJoin } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { HttpService } from '@app/@core/services/http.service';
import { AlertService } from '@app/@core/services/alert.service';
import { MaterialUIModule } from '@app/shared/modules/material-ui/material-ui.module';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { GridActionsComponent, GridAction, ColumnConfig } from '@app/shared/custom-component/grid-actions/grid-actions.component';
import { headersToColumnConfigs } from '../../lookup-grid-columns.util';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { EducatorDashboardDataRow } from '../../educator-dashboard-data.model';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import { modalButtonTitles, modalContent, modalHeading, ModalConfig } from '@app/shared/constants/modal.model';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { CommonService } from '@app/shared/services/common.service';
import { LoaderService } from '@app/@core/services/loader.service';
import { ManageProfileService } from '@app/pages/manage-profile/manage-profile.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { LocalService } from '@app/@core/services/local.service';
import { patternMatch } from '@app/shared/constants/validators.model';
import { PrimengModule } from '@app/shared/modules/primeng.module';
import moment from 'moment';


console.log('[LookupUserLicenseTab] Module loading started');

@Component({
  selector: 'lookup-user-license-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialUIModule,
    DataGridComponent,
    GridActionsComponent,
    MultiSelectModule,
    CalendarModule,
    DropdownModule,
    TooltipModule,
    PrimengModule
  ],
  templateUrl: './lookup-user-license-tab.component.html',
  styleUrls: ['./lookup-user-license-tab.component.scss']
})
export class LookupUserLicenseTabComponent implements OnInit, OnChanges, OnDestroy {
  licenseFee: any;
  getLicenseKindEndorsementsData: any;
  showCurrentLKExtendBtn: any;
  static {
    console.log('[LookupUserLicenseTab] Class definition loaded');
  }

  @Input() personId: string | null = null;
  @Output() requestDashboardRefresh = new EventEmitter<void>();

  @ViewChild('currentLicenseKindsGrid') currentLicenseKindsGridRef?: DataGridComponent;
  @ViewChild('licenseKindsHistoryGrid') licenseKindsHistoryGridRef?: DataGridComponent;
  @ViewChild('licenseProvisionsGrid') licenseProvisionsGridRef?: DataGridComponent;
  @ViewChild('endorsementProvisionsGrid') endorsementProvisionsGridRef?: DataGridComponent;
  @ViewChild('combinedProvisionsGrid') combinedProvisionsGridRef?: DataGridComponent;
  @ViewChild('licenseRequirementsGrid') licenseRequirementsGridRef?: DataGridComponent;
  @ViewChild('editLicenseKindModal') editLicenseKindModal: any;
  @ViewChild('editLicenseProvisionModal') editLicenseProvisionModal: any;
  @ViewChild('editEndorsementProvisionModal') editEndorsementProvisionModal: any;
  @ViewChild('editCombinedProvisionModal') editCombinedProvisionModal: any;
  @ViewChild('editLicenseRequirementModal') editLicenseRequirementModal: any;
  @ViewChild('reverseExtensionBtn') reverseExtensionBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('sendLicenseByEmailModal') sendLicenseByEmailModal: any
  @ViewChild(DataGridComponent) dataGridComponent!: DataGridComponent;
  /** When parent already loaded educator dashboard (profile card), pass row to avoid duplicate HTTP. */
  @Input() set row(value: EducatorDashboardDataRow | null) {
    this._rowInput = value;
    if (value) {
      this.educatorDashboardRow = value;
      this.showReverseExtersion = value?.p_license_dtls?.some((dtl: any) => dtl.showreversebtn == true) ?? false;
      let parsedDate: Date;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      parsedDate = new Date(value?.p_license_dtls?.[0]?.expiry_date);
      if (parsedDate < today) {
        this.showCurrentLKExtendBtn = false;
        return;
      }

      this.showCurrentLKExtendBtn = this.currentLicenseKindsGridConfig?.data?.some(
        (f) =>
          this.getExtensionEligibilityStatus(value?.p_license_dtls?.[0]?.expiry_date) === 'Eligible' ||
          this.getExtensionEligibilityStatus(f.expirationDate) === 'Eligible'
      );
    }
  }

  private _rowInput: EducatorDashboardDataRow | null = null;

  educatorDashboardRow: EducatorDashboardDataRow | null = null;

  // Panel toggles
  showLicenseKindsPanel = false;
  showProvisionsPanel = false;
  showLicenseRequirementsPanel = false;



  // Grid configurations
  currentLicenseKindsGridConfig: DataGridVO = new DataGridVO();
  licenseKindsHistoryGridConfig: DataGridVO = new DataGridVO();
  licenseProvisionsGridConfig: DataGridVO = new DataGridVO();
  endorsementProvisionsGridConfig: DataGridVO = new DataGridVO();
  combinedProvisionsGridConfig: DataGridVO = new DataGridVO();
  licenseRequirementsGridConfig: DataGridVO = new DataGridVO();

  // Column chooser visibility
  showCurrentLicenseKindsColumnChooser = false;
  currentLicenseKindsColumnVisibility: Record<string, boolean> = {
    licenseType: true,
    licenseKind: true,
    endorsementGrades: true,
    endorsementPathway: true,
    originalEffectiveDate: true,
    effectiveDate: true,
    expirationDate: true,
    endorsementStatus: true,
    extensionStatus: true
  };

  showLicenseKindsHistoryColumnChooser = false;
  licenseKindsHistoryColumnVisibility: Record<string, boolean> = {
    licenseType: true,
    licenseKind: true,
    endorsementGrade: true,
    endorsementPathway: true,
    originalEffectiveDate: true,
    effectiveDate: true,
    expirationDate: true,
    endorsementStatus: true,
    status: true,
    extensionStatus: true
  };

  showLicenseProvisionsColumnChooser = false;
  licenseProvisionsColumnVisibility: Record<string, boolean> = {
    provision: true,
    licenseKind: true,
    dueDate: true,
    resolvedDate: true,
    resolutionNotes: true,
    additionalText: true,
    status: true,
    extensionStatus: true
  };

  showEndorsementProvisionsColumnChooser = false;
  endorsementProvisionsColumnVisibility: Record<string, boolean> = {
    provision: true,
    endorsement: true,
    dueDate: true,
    resolvedDate: true,
    resolutionNotes: true,
    additionalText: true,
    status: true,
    extensionStatus: true
  };

  showCombinedProvisionsColumnChooser = false;
  combinedProvisionsColumnVisibility: Record<string, boolean> = {
    category: true,
    provision: true,
    source: true,
    dueDate: true,
    resolvedDate: true,
    resolutionNotes: true,
    additionalText: true,
    status: true,
    extensionStatus: true,
      updatedBy: true  
  };

  showLicenseRequirementsColumnChooser = false;
  licenseRequirementsColumnVisibility: Record<string, boolean> = {
    requirement: true,
    type: true,
    source: true,
    dueDate: true,
    resolvedDate: true,
    resolutionNotes: true,
    status: true,
    extensionStatus: true,
      updatedBy: true  
  };

  // Grid actions for each section
  readonly currentLicenseKindsActions: GridAction[] = [
    { type: 'export', id: 'current_license_kinds_export', title: 'Export', tooltip: 'Export' },
    {
      type: 'columnChooser',
      id: 'current_license_kinds_column_chooser',
      title: 'Column Chooser',
      tooltip: 'Column Chooser'
    }
  ];

  readonly licenseKindsHistoryActions: GridAction[] = [
    { type: 'export', id: 'license_kinds_history_export', title: 'Export', tooltip: 'Export' },
    {
      type: 'columnChooser',
      id: 'license_kinds_history_column_chooser',
      title: 'Column Chooser',
      tooltip: 'Column Chooser'
    }
  ];

  readonly licenseProvisionsActions: GridAction[] = [
    { type: 'export', id: 'license_provisions_export', title: 'Export', tooltip: 'Export' },
    {
      type: 'columnChooser',
      id: 'license_provisions_column_chooser',
      title: 'Column Chooser',
      tooltip: 'Column Chooser'
    }
  ];

  readonly endorsementProvisionsActions: GridAction[] = [
    { type: 'export', id: 'endorsement_provisions_export', title: 'Export', tooltip: 'Export' },
    {
      type: 'columnChooser',
      id: 'endorsement_provisions_column_chooser',
      title: 'Column Chooser',
      tooltip: 'Column Chooser'
    }
  ];

  readonly combinedProvisionsActions: GridAction[] = [
    { type: 'export', id: 'combined_provisions_export', title: 'Export', tooltip: 'Export' },
    {
      type: 'columnChooser',
      id: 'combined_provisions_column_chooser',
      title: 'Column Chooser',
      tooltip: 'Column Chooser'
    }
  ];

  readonly licenseRequirementsActions: GridAction[] = [
    { type: 'export', id: 'license_requirements_export', title: 'Export', tooltip: 'Export' },
    {
      type: 'columnChooser',
      id: 'license_requirements_column_chooser',
      title: 'Column Chooser',
      tooltip: 'Column Chooser'
    }
  ];

  combinedProvisionsAllColumns: ColumnConfig[] = [];

  private readonly combinedProvisionsExcelColumns = [
    { label: 'Category', key: 'category', dataType: 'string' as const },
    { label: 'Provision', key: 'provision', dataType: 'string' as const },
    { label: 'Source', key: 'source', dataType: 'string' as const },
    { label: 'Due Date', key: 'dueDate', dataType: 'date' as const },
    { label: 'Resolved Date', key: 'resolvedDate', dataType: 'date' as const },
    { label: 'Resolution Notes', key: 'resolutionNotes', dataType: 'string' as const },
    { label: 'Additional Text', key: 'additionalText', dataType: 'string' as const },
    { label: 'Status', key: 'status', dataType: 'string' as const },
    { label: 'Extension Status', key: 'extensionStatus', dataType: 'string' as const },
     { label: 'Updated By', key: 'updatedBy', dataType: 'string' as const } 
  ];

  // Column configurations for each section
  currentLicenseKindsAllColumns: ColumnConfig[] = [];
  licenseKindsHistoryAllColumns: ColumnConfig[] = [];
  licenseProvisionsAllColumns: ColumnConfig[] = [];
  endorsementProvisionsAllColumns: ColumnConfig[] = [];
  licenseRequirementsAllColumns: ColumnConfig[] = [];

  // Forms for adding/editing
  addEndorsementForm!: FormGroup;
  showAddEndorsementForm = false;

  editLicenseKindForm!: FormGroup;
  showEditLicenseKindModal = false;
  selectedLicenseKind: any = null;
  editLicenseKindIndex: number = -1;
  customModal: any = null;

  addProvisionForm!: FormGroup;
  addEndorsementProvisionForm!: FormGroup;
  showAddLicenseProvisionForm = false;
  showAddEndorsementProvisionForm = false;

  addCombinedProvisionForm!: FormGroup;
  showAddProvisionForm = false;
  provisionCategoryOptions = [
    { label: 'License Kind', value: 'License Kind' },
    { label: 'Endorsement', value: 'Endorsement' }
  ];

  editLicenseProvisionForm!: FormGroup;
  showEditLicenseProvisionModal = false;
  selectedLicenseProvision: any = null;
  editLicenseProvisionIndex: number = -1;
  licenseProvisionModal: any = null;

  editEndorsementProvisionForm!: FormGroup;
  showEditEndorsementProvisionModal = false;
  selectedEndorsementProvision: any = null;
  editEndorsementProvisionIndex: number = -1;
  endorsementProvisionModal: any = null;

  editCombinedProvisionForm!: FormGroup;
  selectedCombinedProvision: any = null;
  combinedProvisionModal: any = null;

  addRequirementForm!: FormGroup;
  showAddLicenseRequirementForm = false;
  showLicenseKindField = false;  // Controls visibility of License Kind field
  showEndorsementField = false;  // Controls visibility of Endorsement field
  autoCalculatedRequirementDueDate: string = '';  // Store auto-calculated due date for comparison

  editLicenseRequirementForm!: FormGroup;
  showEditLicenseRequirementModal = false;
  selectedLicenseRequirement: any = null;
  editLicenseRequirementIndex: number = -1;
  licenseRequirementModal: any = null;

  // Lookup dropdown options
  licenseTypeOptions: any[] = [];
  licenseKindOptions: any[] = [];  // For Add Endorsement form (all lookups from API)
  endorsementTypeOptions: any[] = [];  // For Add Endorsement form (all lookups from API)
  endorsementPathwayOptions: any[] = [];

  // Dropdown options for Provisions and Requirements (from Active licenses only)
  provisionLicenseKindOptions: any[] = [];
  provisionEndorsementOptions: any[] = [];
  requirementLicenseKindOptions: any[] = [];
  requirementEndorsementOptions: any[] = [];

  // Provision type data
  licenseProvTypedata: any[] = [];
  licenseEndoProvTypedata: any[] = [];

  licenseProvTypedataCopy: any = [];
  licenseEndoProvTypedataCopy: any[] = [];


  // Requirement type data
  requirementOptions: any[] = [];
  requirementTypeOptions: any[] = [];

  // Delete modal state
  deleteItemData: any = null;
  deleteItemType: 'license-kind' | 'license-provision' | 'endorsement-provision' | 'license-requirement' | null = null;

  // Due Date warning modal state
  showDueDateWarningModal = false;

  // Extend license properties
  extendLicenseModal!: NgbModalRef;
  extensionDuration: any = '';
  lookupGeneric: any = {};
  lookupToast: any = {};
  placeholderMessage: any = {};
  errorMessages: any = {};
  showReverseExtersion: boolean = false;
  private destroy$ = new Subject<void>();

  // Status color overrides for Provision/Requirement grids
  readonly provReqStatusColorOverrides: Record<string, string> = {
    'Active': 'status-badge-yellow',
    'pending': 'status-badge-yellow',
    'requested': 'status-badge-orange',
    'resolved': 'status-badge-green',
    'past due': 'status-badge-red',
    'inactive': 'status-badge-red',
    'not eligible': 'status-badge-red',
    'Not Eligible': 'status-badge-red',
  };

  private static readonly LICENSE_HEADERS_JSON = '../assets/metadata/lookup-user-license-headers.json';
  private licenseMetadataHeaders: Record<string, any[]> = {};
  private metadataHeadersLoaded = false;

  sendLicenseByEmailForm!: FormGroup;

  readonly overviewEnDash = '\u2013';
  expirationDate: string;

  // Date formatter for grid display
  private formatDateForGrid(dateValue: any): string {
    if (!dateValue) return '';

    try {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return dateValue; // Return original if invalid

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}/${day}/${year}`; // MM/DD/YYYY
    } catch {
      return dateValue; // Return original on error
    }
  }

  private readonly currentLicenseKindsExcelColumns = [
    { label: 'License Type', key: 'licenseType', dataType: 'string' as const },
    { label: 'License Kind', key: 'licenseKind', dataType: 'string' as const },
    { label: 'Endorsement - Grade', key: 'endorsementGrade', dataType: 'string' as const },
    { label: 'Endorsement Pathway', key: 'endorsementPathway', dataType: 'string' as const },
    { label: 'Original Effective Date', key: 'originalEffectiveDate', dataType: 'date' as const },
    { label: 'Effective Date', key: 'effectiveDate', dataType: 'date' as const },
    { label: 'Expiration Date', key: 'expirationDate', dataType: 'date' as const },
    { label: 'Endorsement Status', key: 'endorsementStatus', dataType: 'string' as const },
    { label: 'Extension Status', key: 'extensionStatus', dataType: 'string' as const }
  ];

  private readonly licenseKindsHistoryExcelColumns = [
    { label: 'License Type', key: 'licenseType', dataType: 'string' as const },
    { label: 'License Kind', key: 'licenseKind', dataType: 'string' as const },
    { label: 'Endorsement - Grade', key: 'endorsementGrade', dataType: 'string' as const },
    { label: 'Endorsement Pathway', key: 'endorsementPathway', dataType: 'string' as const },
    { label: 'Original Effective Date', key: 'originalEffectiveDate', dataType: 'date' as const },
    { label: 'Effective Date', key: 'effectiveDate', dataType: 'date' as const },
    { label: 'Expiration Date', key: 'expirationDate', dataType: 'date' as const },
    { label: 'Endorsement Status', key: 'endorsementStatus', dataType: 'string' as const },
    { label: 'Status', key: 'status', dataType: 'string' as const },
    { label: 'Extension Status', key: 'extensionStatus', dataType: 'string' as const }
  ];

  private readonly licenseProvisionsExcelColumns = [
    { label: 'Provision', key: 'provision', dataType: 'string' as const },
    { label: 'License Kind', key: 'licenseKind', dataType: 'string' as const },
    { label: 'Due Date', key: 'dueDate', dataType: 'date' as const },
    { label: 'Resolved Date', key: 'resolvedDate', dataType: 'date' as const },
    { label: 'Resolution Notes', key: 'resolutionNotes', dataType: 'string' as const },
    { label: 'Additional Text', key: 'additionalText', dataType: 'string' as const },
    { label: 'Status', key: 'status', dataType: 'string' as const },
    { label: 'Extension Status', key: 'extensionStatus', dataType: 'string' as const }
  ];
  private readonly licenseRequirementsExcelColumns = [
    { label: 'Category', key: 'type', dataType: 'string' as const },
    { label: 'Source', key: 'source', dataType: 'string' as const },
    { label: 'Requirement', key: 'requirement', dataType: 'string' as const },
    { label: 'Due Date', key: 'dueDate', dataType: 'date' as const },
    { label: 'Resolved Date', key: 'resolvedDate', dataType: 'date' as const },
    { label: 'Resolution Notes', key: 'resolutionNotes', dataType: 'string' as const },
    { label: 'Status', key: 'status', dataType: 'string' as const },
    { label: 'Extension Status', key: 'extensionStatus', dataType: 'string' as const },
      { label: 'Updated By', key: 'updatedBy', dataType: 'string' as const }
  ];

  minDate: Date = new Date();
  showModal: boolean = false;
  warningDateFieldLabel: 'Due Date';
  warningTitleLabel: 'Due Date Notice';
  private allRequirementOptions: any[] = [];

  constructor(
    private httpService: HttpService,
    private fb: FormBuilder,
    private alertService: AlertService,
    private modalService: NgbModal,
    private cd: ChangeDetectorRef,
    private commonService: CommonService,
    private loaderService: LoaderService,
    private manageProfileService: ManageProfileService,
    private sessionStorage: LocalService,
  ) {
    console.log('[LookupUserLicenseTab] Constructor called - Component is being instantiated');
    console.log('[LookupUserLicenseTab] Constructor personId:', this.personId);
    console.log('[LookupUserLicenseTab] Constructor _rowInput:', this._rowInput);

    this.loadLookupData();

    this.addEndorsementForm = this.fb.group({
      licenseType: [null, Validators.required],
      licenseKind: [null, Validators.required],
      endorsementGrade: [null, Validators.required],
      endorsementPathway: [null, Validators.required],
      effectiveDate: [null, Validators.required]
    });

    this.editLicenseKindForm = this.fb.group({
      licenseType: [''],
      licenseKind: [''],
      endorsementGrade: [''],
      endorsementPathway: ['']
    });

    this.addProvisionForm = this.fb.group({
      provision: [null, Validators.required],
      selectedLicenseKinds: [[], Validators.required],
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]],
      additionalText: ['', [Validators.required]]
    });

    this.addEndorsementProvisionForm = this.fb.group({
      provision: [null, Validators.required],
      selectedEndorsements: [[], Validators.required],
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]],
      additionalText: ['', [Validators.required]]
    });

    this.addCombinedProvisionForm = this.fb.group({
      category: [null, Validators.required],
      provision: [null, Validators.required],
      source: [[]],
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]],
      additionalText: ['', [Validators.required]]
    });

    this.editLicenseProvisionForm = this.fb.group({
      provision: [null, Validators.required],
      licenseKind: [[], Validators.required],
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]],
      additionalText: ['', [Validators.required]]
    });

    this.editEndorsementProvisionForm = this.fb.group({
      provision: [null, Validators.required],
      endorsement: [[], Validators.required],
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]],
      additionalText: ['', [Validators.required]]
    });

    this.editCombinedProvisionForm = this.fb.group({
      category: [{ value: null, disabled: true }],
      provision: [null, Validators.required],
      source: [[]],
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]],
      additionalText: ['', [Validators.required]]
    });

    this.addRequirementForm = this.fb.group({
      requirement: [null, Validators.required],
      type: [null, Validators.required],
      licenseKind: [[], Validators.required],  // Multiselect array for P0017
      endorsement: [[], Validators.required],  // Multiselect array for P0018
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]],
      additionalText: ['']
    });

    this.editLicenseRequirementForm = this.fb.group({
      requirement: [null, Validators.required],
      type: [null, Validators.required],
      licenseKind: [null, Validators.required],
      endorsement: [null, Validators.required],
      dueDate: ['', [Validators.required, this.strictUsDateValidator.bind(this)]]
    });

    this.sendLicenseByEmailForm = new FormGroup({
      cansendtootheremailstext: new FormControl('', [Validators.required, Validators.maxLength(255), Validators.pattern(patternMatch.registrationEmail)])
    });


    console.log('[LookupUserLicenseTab] All forms initialized in constructor');
  }
  ProvisionData: any;
  EndorsementData: any;

  ngOnInit(): void {
    console.log('[LookupUserLicenseTab] ngOnInit called with personId:', this.personId);
    console.log('[LookupUserLicenseTab] ngOnInit _rowInput:', this._rowInput);
    this.showReverseExtersion = this.educatorDashboardRow?.p_license_dtls?.some((item) => item.showreversebtn == true) ?? false;
    // console.log(this.showReverseExtersion,this.educatorDashboardRow.p_license_dtls[0]?.cd_license_status);    this.initializeGridConfigs();    console.log('[LookupUserLicenseTab] Grid configs initialized');
    this.loadLicenseHeaderMetadata();
    this.getExtensionFees();
    this.getLicenseKindEndorsementsByPersonId()
    this.getLicenseProvisionByPersonId();

    console.log('[LookupUserLicenseTab] Started loading lookup data');

    this.loadExtensionDuration();
  }

  fetchprovisions(value: any): Observable<any> {
    return this.httpService.get(`/application-flow/provtype?lookupcatid=${value}`);
  }

  private loadProvisionData(): void {
    console.log('[LookupUserLicenseTab] loadProvisionData() - Loading provisions for categories 414 and 415');

    // Load License Provisions (category 414)
    this.fetchprovisions(414).subscribe({
      next: (response) => {
        console.log('[LookupUserLicenseTab] License Provision (414) API Response:', response);

        let data = response?.data || response?.rows || response || [];
        if (!Array.isArray(data)) {
          if (response?.result && Array.isArray(response.result)) {
            data = response.result;
          } else if (response?.items && Array.isArray(response.items)) {
            data = response.items;
          }
        }

        this.licenseProvTypedata = data
          .filter((item: any) => {
            const categoryId = item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id || item.lookup_category_id;
            return categoryId == 414;
          })
          .map((item: any) => ({
            label: item.value || item.text || item.name || item.description || item.label,
            value: item.key || item.id || item.code,
            lookupcategoryid: item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id,
            isactive: item.isactive || item.is_active || item.active
          }));

        this.licenseProvTypedataCopy = data
          .filter((item: any) => {
            const categoryId = item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id || item.lookup_category_id;
            return categoryId == 414;
          })
          .map((item: any) => ({
            label: item.value || item.text || item.name || item.description || item.label,
            value: item.key || item.id || item.code,
            lookupcategoryid: item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id,
            isactive: item.isactive || item.is_active || item.active
          }));

        console.log('[LookupUserLicenseTab] License Provision data loaded:', this.licenseProvTypedata.length, 'items');
        console.log('[LookupUserLicenseTab] License Provision sample items:', this.licenseProvTypedata.slice(0, 5));
        this.cd.detectChanges();
      },
      error: (error) => {
        console.error('[LookupUserLicenseTab] Error loading License Provision data (414):', error);
        this.licenseProvTypedata = [];
      }
    });

    // Load Endorsement Provisions (category 415)
    this.fetchprovisions(415).subscribe({
      next: (response) => {
        console.log('[LookupUserLicenseTab] Endorsement Provision (415) API Response:', response);

        let data = response?.data || response?.rows || response || [];
        if (!Array.isArray(data)) {
          if (response?.result && Array.isArray(response.result)) {
            data = response.result;
          } else if (response?.items && Array.isArray(response.items)) {
            data = response.items;
          }
        }

        this.licenseEndoProvTypedata = data
          .filter((item: any) => {
            const categoryId = item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id || item.lookup_category_id;
            return categoryId == 415;
          })
          .map((item: any) => ({
            label: item.value || item.text || item.name || item.description || item.label,
            value: item.key || item.id || item.code,
            lookupcategoryid: item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id,
            isactive: item.isactive || item.is_active || item.active
          }));

        this.licenseEndoProvTypedataCopy = data
          .filter((item: any) => {
            const categoryId = item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id || item.lookup_category_id;
            return categoryId == 415;
          })
          .map((item: any) => ({
            label: item.value || item.text || item.name || item.description || item.label,
            value: item.key || item.id || item.code,
            lookupcategoryid: item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id,
            isactive: item.isactive || item.is_active || item.active
          }));

        console.log('[LookupUserLicenseTab] Endorsement Provision data loaded:', this.licenseEndoProvTypedata.length, 'items');
        console.log('[LookupUserLicenseTab] Endorsement Provision sample items:', this.licenseEndoProvTypedata.slice(0, 5));
        this.cd.detectChanges();
      },
      error: (error) => {
        console.error('[LookupUserLicenseTab] Error loading Endorsement Provision data (415):', error);
        this.licenseEndoProvTypedata = [];
      }
    });
  }

  provtypeOnChange(value: any): Promise<void> {
    return new Promise((resolve, reject) => {
      // Call the API to get provision data
      this.fetchprovisions(value).subscribe({
        next: (response) => {
          console.log('Provision API Response:', response);

          // Handle different possible response structures more robustly
          let data = response?.data || response?.rows || response || [];

          // If it's not an array, try to extract array from common response patterns
          if (!Array.isArray(data)) {
            if (response?.result && Array.isArray(response.result)) {
              data = response.result;
            } else if (response?.items && Array.isArray(response.items)) {
              data = response.items;
            } else {
              console.warn('Expected array but got:', typeof data, data);
              console.log('Full response structure:', response);
              reject(new Error('Invalid data format received'));
              return;
            }
          }

          console.log('Processing provision data:', data);

          // Filter and map data for both categories with more flexible field matching
          // Map to same structure as other lookup dropdowns: label (display) and value (key to send)
          this.licenseProvTypedata = data
            .filter((item: any) => {
              // Try multiple possible field names for category ID
              const categoryId = item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id || item.lookup_category_id;
              return categoryId == 414;
            })
            .map((item: any) => ({
              // label = display text, value = key/ID to send to backend
              label: item.value || item.text || item.name || item.description || item.label,
              value: item.key || item.id || item.code,
              lookupcategoryid: item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id,
              isactive: item.isactive || item.is_active || item.active
            }));

          if (this.licenseProvisionsGridConfig.data?.length > 0) {
            const provisionSet = new Set(
              this.licenseProvisionsGridConfig.data.map(a2 => a2.provision)
            );

            this.licenseProvTypedata = this.licenseProvTypedata.filter(a1 =>
              !provisionSet.has(a1.label)
            );

          }
          else {
            this.licenseProvTypedata = this.licenseProvTypedataCopy;
          }



          this.licenseEndoProvTypedata = data
            .filter((item: any) => {
              // Try multiple possible field names for category ID
              const categoryId = item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id || item.lookup_category_id;
              return categoryId == 415;
            })
            .map((item: any) => ({
              // label = display text, value = key/ID to send to backend
              label: item.value || item.text || item.name || item.description || item.label,
              value: item.key || item.id || item.code,
              lookupcategoryid: item.lookupcategoryid || item.lookupcatid || item.categoryId || item.category_id,
              isactive: item.isactive || item.is_active || item.active
            }));

          if (this.endorsementProvisionsGridConfig.data?.length > 0) {
            const provisionSet = new Set(
              this.endorsementProvisionsGridConfig.data.map(a2 => a2.provision)
            );

            this.licenseEndoProvTypedata = this.licenseEndoProvTypedata.filter(a1 =>
              !provisionSet.has(a1.label)
            );

          }
          else {
            this.licenseEndoProvTypedata = this.licenseEndoProvTypedataCopy;
          }

          console.log('Filtered License Provision data (414):', this.licenseProvTypedata);
          console.log('Sample provision item:', this.licenseProvTypedata[0]);
          console.log('Filtered Endorsement Provision data (415):', this.licenseEndoProvTypedata);
          console.log('Sample endorsement provision item:', this.licenseEndoProvTypedata[0]);

          this.cd.detectChanges();
          resolve();
        },
        error: (error) => {
          console.error('Error getting provision type data:', error);
          console.log('Error details:', error?.error || error);

          // Clear both arrays on error
          this.licenseProvTypedata = [];
          this.licenseEndoProvTypedata = [];

          this.cd.detectChanges();
          reject(error);
        }
      });
    });
  }

  private loadLookupData(): void {
    console.log('[LookupUserLicenseTab] loadLookupData() started');

    // Load provision data for both license and endorsement provisions
    console.log('[LookupUserLicenseTab] Loading provision data (categories 414 and 415)');
    this.loadProvisionData();

    // Load License Kind options (for Add Endorsement form - all lookups)
    console.log('Calling License Kind API:', '/platform/master/lookup/category/LICENSE-ENDORSEMENT');
    this.httpService.get('/platform/master/lookup/category/LICENSE-ENDORSEMENT').subscribe({
      next: (resp: any) => {
        if (resp && resp.data && Array.isArray(resp.data)) {
          this.licenseKindOptions = resp.data.map((item: any) => ({
            label: item.value,
            value: item.key
          }));
          console.log('License Kind Options Loaded (for Add Endorsement):', this.licenseKindOptions.length, this.licenseKindOptions);
        }
        this.cd.detectChanges();
      },
      error: (error) => {
        console.error('[LookupUserLicenseTab] Error loading Endorsement Provision data (415):', error);
        this.licenseKindOptions = [];
      }
    });

    // Load Endorsement options (for Add Endorsement form - all lookups)
    console.log('Calling Endorsement API:', '/domain/lookup?requestType=ENDORSEMENT');
    this.httpService.get('/domain/lookup?requestType=ENDORSEMENT').subscribe({
      next: (resp: any) => {
        if (resp && resp.data && Array.isArray(resp.data)) {
          this.endorsementTypeOptions = resp.data.map((item: any) => ({
            label: item.value,
            value: item.key
          }));
          console.log('Endorsement Options Loaded (for Add Endorsement):', this.endorsementTypeOptions.length, this.endorsementTypeOptions);
        } else {
          this.endorsementTypeOptions = [];
          console.warn('Endorsement: No data in response', resp);
        }
        this.cd.detectChanges();
      },
      error: (err) => {
        console.error('Endorsement API Error:', err);
        this.endorsementTypeOptions = [];
      }
    });

    // Load License Type options
    console.log('Calling License Type API:', API_ENDPOINTS.LOOKUP_LICENSE_TYPES);
    this.httpService.get(API_ENDPOINTS.LOOKUP_LICENSE_TYPES).subscribe({
      next: (resp: any) => {
        if (resp && resp.data && Array.isArray(resp.data)) {
          this.licenseTypeOptions = resp.data.map((item: any) => ({
            label: item.value,
            value: item.key
          }));
          console.log('License Type Options Loaded:', this.licenseTypeOptions.length, this.licenseTypeOptions);
        } else {
          this.licenseTypeOptions = [];
          console.warn('License Type: No data in response', resp);
        }
        this.cd.detectChanges();
      },
      error: (err) => {
        console.error('License Type API Error:', err);
        this.licenseTypeOptions = [];
      }
    });

    // Load Requirements options (LICENSE-REQUIREMENT-CATEGORY)
    this.httpService.get('/domain/lookup?requestType=LICENSE-REQUIREMENT-CATEGORY').subscribe({
      next: (resp: any) => {
        if (resp && resp.data && Array.isArray(resp.data)) {
          this.requirementOptions = resp.data
            .map((item: any) => ({
              label: item.value,
              value: item.key
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
            this.allRequirementOptions = [...this.requirementOptions];
          console.log('Requirement Options Loaded:', this.requirementOptions.length, this.requirementOptions);
        } else {
          this.requirementOptions = [];
          console.warn('Requirements: No data in response', resp);
        }
        this.cd.detectChanges();
      },
      error: (err) => {
        console.error('Requirements API Error:', err);
        this.requirementOptions = [];
      }
    });

    // Load Requirement Types (License Kind and Endorsement only)
    this.requirementTypeOptions = [
      { label: 'License Kind', value: 'LICENSE' },
      { label: 'Endorsement', value: 'ENDORSEMENT' }
    ];

    // Load Endorsement Pathway options
    console.log('[LookupUserLicenseTab] Loading Endorsement Pathway options from: /domain/lookup?requestType=ENDO-PATHWAY');
    this.httpService.get('/domain/lookup?requestType=ENDO-PATHWAY').subscribe({
      next: (resp: any) => {
        if (resp && resp.data && Array.isArray(resp.data)) {
          this.endorsementPathwayOptions = resp.data
            .map((item: any) => ({
              label: item.value,
              value: item.key
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          console.log('Endorsement Pathway Options Loaded:', this.endorsementPathwayOptions.length, this.endorsementPathwayOptions);
        } else {
          this.endorsementPathwayOptions = [];
          console.warn('Endorsement Pathway: No data in response', resp);
        }
        this.cd.detectChanges();
      },
      error: (err) => {
        console.error('Endorsement Pathway API Error:', err);
        this.endorsementPathwayOptions = [];
      }
    });

    this.getToastMessages()
    this.getGenericLookup();
    this.getplaceholderMessageLookup();
    this.getMessages();
  }

  getToastMessages(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpService.get(API_ENDPOINTS.TOAST_MESSAGES).subscribe({
        next: (data: any) => {
          resolve();
          console.log(data?.data);
          const transformLookupArray = (arr: any[]): { [key: string]: string } => {
            const obj: { [key: string]: string } = {};
            if (Array.isArray(arr)) {
              arr.forEach(lookupItem => {
                if (lookupItem.key && lookupItem.value) {
                  obj[lookupItem.key] = lookupItem.value;
                }
              });
            }
            return obj;
          };
          this.lookupToast = transformLookupArray(data?.data);
        },
        error: (error) => {
          console.error('Error getting person data:', error);
          reject(error);
        }
      });
    });
  }

  getGenericLookup(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpService.get(API_ENDPOINTS.GENERIC_MESSAGES).subscribe({
        next: (data: any) => {
          resolve();
          console.log(data?.data);
          const transformLookupArray = (arr: any[]): { [key: string]: string } => {
            const obj: { [key: string]: string } = {};
            if (Array.isArray(arr)) {
              arr.forEach(lookupItem => {
                if (lookupItem.key && lookupItem.value) {
                  obj[lookupItem.key] = lookupItem.value;
                }
              });
            }
            return obj;
          };
          this.lookupGeneric = transformLookupArray(data?.data);
        },
        error: (error) => {
          console.error('Error getting person data:', error);
          reject(error);
        }
      });
    });
  }

  getplaceholderMessageLookup(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpService.get(API_ENDPOINTS.PLACEHOLDER_MESSAGES).subscribe({
        next: (data: any) => {
          resolve();
          console.log(data?.data);
          const transformLookupArray = (arr: any[]): { [key: string]: string } => {
            const obj: { [key: string]: string } = {};
            if (Array.isArray(arr)) {
              arr.forEach(lookupItem => {
                if (lookupItem.key && lookupItem.value) {
                  obj[lookupItem.key] = lookupItem.value;
                }
              });
            }
            return obj;
          };
          this.placeholderMessage = transformLookupArray(data?.data);
        },
        error: (error) => {
          console.error('Error getting person data:', error);
          reject(error);
        }
      });
    });
  }

  getMessages(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpService.get(API_ENDPOINTS.MESSAGES).subscribe({
        next: (data: any) => {
          resolve();
          console.log(data?.data);
          const transformLookupArray = (arr: any[]): { [key: string]: string } => {
            const obj: { [key: string]: string } = {};
            if (Array.isArray(arr)) {
              arr.forEach(lookupItem => {
                if (lookupItem.key && lookupItem.value) {
                  obj[lookupItem.key] = lookupItem.value;
                }
              });
            }
            return obj;
          };
          this.errorMessages = transformLookupArray(data?.data);
        },
        error: (error) => {
          console.error('Error getting person data:', error);
          reject(error);
        }
      });
    });
  }






  ngOnChanges(changes: SimpleChanges): void {
    console.log('[LookupUserLicenseTab] ngOnChanges called with changes:', changes);

    if (changes['personId']) {
      console.log('[LookupUserLicenseTab] personId changed:', {
        previousValue: changes['personId'].previousValue,
        currentValue: changes['personId'].currentValue,
        firstChange: changes['personId'].firstChange
      });
    }

    if (changes['personId'] && !changes['personId'].firstChange && !this._rowInput) {
      console.log('[LookupUserLicenseTab] personId changed (not first change) and no _rowInput - may need to reload data');
      // Reload data if needed
    }
  }

  ngOnDestroy(): void {
    console.log('[LookupUserLicenseTab] ngOnDestroy called - cleaning up subscriptions');
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeGridConfigs(): void {
    this.currentLicenseKindsGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: true,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'license',
      showQueueActions: false
    });

    this.licenseKindsHistoryGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: true,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'license',
      showQueueActions: false
    });

    this.licenseProvisionsGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: true,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'license',
      showQueueActions: false
    });

    this.endorsementProvisionsGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: true,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'license',
      showQueueActions: false
    });

    this.combinedProvisionsGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: true,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'license',
      showQueueActions: false
    });

    this.licenseRequirementsGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: true,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'license',
      showQueueActions: false
    });
  }

  private loadLicenseHeaderMetadata(): void {
    const url = LookupUserLicenseTabComponent.LICENSE_HEADERS_JSON;
    forkJoin({
      currentLicenseKinds: this.commonService.getHeaderData('currentLicenseKinds', url),
      licenseKindsHistory: this.commonService.getHeaderData('licenseKindsHistory', url),
      licenseProvisions: this.commonService.getHeaderData('licenseProvisions', url),
      endorsementProvisions: this.commonService.getHeaderData('endorsementProvisions', url),
      combinedProvisions: this.commonService.getHeaderData('combinedProvisions', url),
      licenseRequirements: this.commonService.getHeaderData('licenseRequirements', url)
    }).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (headers: any) => {
        this.licenseMetadataHeaders = headers || {};
        this.metadataHeadersLoaded = true;
        this.buildColumnConfigs();
        this.buildLicenseGrids();
      },
      error: () => {
        this.metadataHeadersLoaded = true;
        this.buildLicenseGrids();
      }
    });
  }

  private buildColumnConfigs(): void {
    this.currentLicenseKindsAllColumns = headersToColumnConfigs(this.licenseMetadataHeaders['currentLicenseKinds'] || []).map(col => ({ ...col, disabled: col.key === 'licenseKind' }));
    this.licenseKindsHistoryAllColumns = headersToColumnConfigs(this.licenseMetadataHeaders['licenseKindsHistory'] || []).map(col => ({ ...col, disabled: col.key === 'licenseKind' }));
    this.licenseProvisionsAllColumns = headersToColumnConfigs(this.licenseMetadataHeaders['licenseProvisions'] || []).map(col => ({ ...col, disabled: col.key === 'provision' }));
    this.endorsementProvisionsAllColumns = headersToColumnConfigs(this.licenseMetadataHeaders['endorsementProvisions'] || []).map(col => ({ ...col, disabled: col.key === 'provision' }));
    this.combinedProvisionsAllColumns = headersToColumnConfigs(this.licenseMetadataHeaders['combinedProvisions'] || []).map(col => ({ ...col, disabled: col.key === 'provision' }));
    this.licenseRequirementsAllColumns = headersToColumnConfigs(this.licenseMetadataHeaders['licenseRequirements'] || []).map(col => ({ ...col, disabled: col.key === 'requirement' }));
  }

  private buildLicenseGrids(): void {
    console.log('[LookupUserLicenseTab] buildLicenseGrids() called with educatorDashboardRow:', this.educatorDashboardRow);



    // Build Current License Kinds grid
    console.log('[LookupUserLicenseTab] Building Current License Kinds grid');
    // this.buildCurrentLicenseKindsGrid();

    // Build License Kinds History grid
    console.log('[LookupUserLicenseTab] Building License Kinds History grid');
    // this.buildLicenseKindsHistoryGrid();

    // Build License Provisions grid
    console.log('[LookupUserLicenseTab] Building License Provisions grid');
    this.buildLicenseProvisionsGrid();

    // Build Endorsement Provisions grid
    console.log('[LookupUserLicenseTab] Building Endorsement Provisions grid');
    this.buildEndorsementProvisionsGrid();

    // Build Combined Provisions grid
    this.buildCombinedProvisionsGrid();

    // Build License Requirements grid
    console.log('[LookupUserLicenseTab] Building License Requirements grid');
    // this.buildLicenseRequirementsGrid();
    this.fetchLicenseRequirementsByPersonId();

    console.log('[LookupUserLicenseTab] All grids built successfully');
  }

  /** Grid display: MM/DD/YYYY from YYYY-MM-DD, ISO, or existing US-formatted strings. */
  private formatDateForLicenseGrid(value: unknown): string {
    const d = this.parseLicenseDateValue(value);
    if (!d) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(d);
  }

  private parseLicenseDateValue(value: unknown): Date | null {
    if (value == null || value === '') return null;
    const s = String(value).trim();
    if (!s) return null;
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (isoMatch) {
      const y = +isoMatch[1];
      const m = +isoMatch[2] - 1;
      const day = +isoMatch[3];
      const d = new Date(y, m, day);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const usMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
    if (usMatch) {
      const month = +usMatch[1] - 1;
      const day = +usMatch[2];
      const y = +usMatch[3];
      const d = new Date(y, month, day);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const parsed = new Date(s);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private buildCurrentLicenseKindsGrid(): void {
    const headers = this.licenseMetadataHeaders['currentLicenseKinds'] || [];

    // Get real data from educator-dashboard-data API response
    const licenseKinds = this.educatorDashboardRow?.p_licensekind || [];
    const licenseDetails = this.educatorDashboardRow?.p_license_dtls || [];

    // Map API data to grid rows and filter for Active endorsement status only
    const rows = Array.isArray(licenseKinds) ? licenseKinds
      .filter((item: any) => {
        // Only show items with 'Active' endorsement status in Current grid
        const status = (item.cd_endo_status || '').toString().toLowerCase();
        return status === 'active';
      })
      .map((item: any, index: number) => {
        // Match license details by application_id or license_number if available
        // Otherwise fall back to first active license
        let licenseInfo = null;
        if (Array.isArray(licenseDetails) && licenseDetails.length > 0) {
          // Try to find matching license by application_id
          licenseInfo = licenseDetails.find((lic: any) =>
            item.application_id && lic.application_id === item.application_id
          ) || licenseDetails.find((lic: any) => lic.is_active === true) || licenseDetails[0];
        }

        return {
          id: item.license_id || item.id || index + 1,  // Use actual DB primary key
          licenseKindId: item.license_id || item.id,    // Store primary key (license_id from DB)
          applicationId: item.application_id,                // Store application_id
          licenseType: item.license_type || '',
          licenseKind: item.license_kind || '',
          endorsementGrade: item.endorsement || '',
          endorsementPathway: item.endorsement_pathway || '',
          originalEffectiveDate: this.formatDateForGrid(item.original_effective_date || licenseInfo?.issue_date || ''),
          effectiveDate: this.formatDateForGrid(item.effective_date || licenseInfo?.issue_date || ''),
          expirationDate: this.formatDateForGrid(item.expiry_date || licenseInfo?.expiry_date || ''),
          endorsementStatus: item.cd_endo_status || '',
          extensionStatus: this.getExtensionEligibilityStatus(item.expiry_date || licenseInfo?.expiry_date)
        };
      }) : [];

    this.currentLicenseKindsGridConfig = this.currentLicenseKindsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.currentLicenseKindsColumnVisibility,
      _changeKey: Date.now()
    });

    // Populate Provisions and Requirements dropdowns from ACTIVE licenses only
    // This ensures these dropdowns only show values from current active licenses
    this.populateProvisionAndRequirementDropdowns(rows);
  }

  private buildLicenseKindsHistoryGrid(): void {
    const headers = this.licenseMetadataHeaders['licenseKindsHistory'] || [];

    // Get real data from educator-dashboard-data API response
    const licenseKinds = this.educatorDashboardRow?.p_licensekind || [];
    const licenseDetails = this.educatorDashboardRow?.p_license_dtls || [];

    // Map API data to grid rows and filter for non-Active endorsement status (History/Pending)
    const rows = Array.isArray(licenseKinds) ? licenseKinds
      .filter((item: any) => {
        // Show all items with endorsement status NOT 'Active' (Pending, Expired, etc.)
        const status = (item.cd_endo_status || '').toString().toLowerCase();
        return status !== 'active';
      })
      .map((item: any, index: number) => {
        // Match license details by application_id or license_number if available
        let licenseInfo = null;
        if (Array.isArray(licenseDetails) && licenseDetails.length > 0) {
          licenseInfo = licenseDetails.find((lic: any) =>
            item.application_id && lic.application_id === item.application_id
          ) || licenseDetails.find((lic: any) => lic.is_active === true) || licenseDetails[0];
        }

        return {
          id: item.license_id || item.id || index + 1,
          licenseKindId: item.license_id || item.id,
          applicationId: item.application_id,
          licenseType: item.license_type || '',
          licenseKind: item.license_kind || '',
          endorsementGrade: item.endorsement || '',
          endorsementPathway: item.endorsement_pathway || '',
          originalEffectiveDate: this.formatDateForGrid(item.original_effective_date || licenseInfo?.issue_date || ''),
          effectiveDate: this.formatDateForGrid(item.effective_date || licenseInfo?.issue_date || ''),
          expirationDate: this.formatDateForGrid(item.expiry_date || licenseInfo?.expiry_date || ''),
          endorsementStatus: item.cd_endo_status || '',
          status: item.status || item.cd_license_status || '',
          extensionStatus: this.getExtensionEligibilityStatus(item.expiry_date || licenseInfo?.expiry_date)
        };
      }) : [];

    this.licenseKindsHistoryGridConfig = this.licenseKindsHistoryGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.licenseKindsHistoryColumnVisibility,
      _changeKey: Date.now()
    });
  }

  /**
   * Populate License Kind and Endorsement dropdown options from Current License Kinds grid data
   */
  private populateProvisionAndRequirementDropdowns(activeGridData: any[]): void {
    console.log('[populateProvisionAndRequirementDropdowns] Populating Provision/Requirement dropdowns from active licenses:', activeGridData);

    // Ensure activeGridData is an array
    if (!Array.isArray(activeGridData) || activeGridData.length === 0) {
      console.warn('[populateProvisionAndRequirementDropdowns] No active license data - Provision/Requirement dropdowns will be empty');
      this.provisionLicenseKindOptions = [];
      this.provisionEndorsementOptions = [];
      this.requirementLicenseKindOptions = [];
      this.requirementEndorsementOptions = [];
      return;
    }

    // Extract unique License Kind values (non-empty) from ACTIVE licenses
    const uniqueLicenseKinds = [...new Set(
      activeGridData
        .map(item => item.licenseKind)
        .filter(kind => kind && kind.trim() !== '')
    )].sort();

    // Extract unique Endorsement - Grade values (non-empty) from ACTIVE licenses
    const uniqueEndorsements = [...new Set(
      activeGridData
        .map(item => item.endorsementGrade)
        .filter(endorsement => endorsement && endorsement.trim() !== '')
    )].sort();

    // Create dropdown options for Provisions (License Kind)
    this.provisionLicenseKindOptions = uniqueLicenseKinds.map(kind => ({
      label: kind,
      value: kind
    }));

    // Create dropdown options for Provisions (Endorsement)
    this.provisionEndorsementOptions = uniqueEndorsements.map(endorsement => ({
      label: endorsement,
      value: endorsement
    }));

    // Create dropdown options for Requirements (License Kind) - same as Provisions
    this.requirementLicenseKindOptions = uniqueLicenseKinds.map(kind => ({
      label: kind,
      value: kind
    }));

    // Create dropdown options for Requirements (Endorsement) - same as Provisions
    this.requirementEndorsementOptions = uniqueEndorsements.map(endorsement => ({
      label: endorsement,
      value: endorsement
    }));

    console.log('[populateProvisionAndRequirementDropdowns] Provision License Kind Options:', this.provisionLicenseKindOptions);
    console.log('[populateProvisionAndRequirementDropdowns] Provision Endorsement Options:', this.provisionEndorsementOptions);
    console.log('[populateProvisionAndRequirementDropdowns] Requirement License Kind Options:', this.requirementLicenseKindOptions);
    console.log('[populateProvisionAndRequirementDropdowns] Requirement Endorsement Options:', this.requirementEndorsementOptions);

    this.cd.detectChanges();
  }

  private buildLicenseProvisionsGrid(): void {
    //const headers = this.licenseMetadataHeaders['licenseProvisions'] || [];
    const headers = (this.licenseMetadataHeaders['licenseProvisions'] || []).map((h: any) =>
      h.dataField === 'licenseKind'
        ? { ...h, cssClass: 'lulsource-wrap-column' }
        : h
    );
    // Get real data from p_license_provision
    const licenseProvisions = this.ProvisionData || [];

    console.log('[LookupUserLicenseTab] License Provisions data:', licenseProvisions);
    console.log('[LookupUserLicenseTab] License Provisions count:', Array.isArray(licenseProvisions) ? licenseProvisions.length : 'Not an array');

    // Map API data to   grid rows
    const rows = Array.isArray(licenseProvisions) ? licenseProvisions.map((item: any, index: number) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDateObj = new Date(item.license_due_date);
      dueDateObj.setHours(0, 0, 0, 0);

      return {
        id: item.person_license_provision_id || index + 1,  // Use actual DB primary key from API
        provisionId: item.person_license_provision_id,      // Store primary key separately
        person_license_provision_id: item.person_license_provision_id,
        applicationId: item.old_id,              // Store application_id
        provision: this.licenseProvTypedataCopy?.find((opt: any) => opt.value == item.cd_license_provision)?.label || '',
        licenseKind: item.cd_license_kind
          ?.map((id: number) => this.licensekindName(id))
          .join(', '),
        dueDate: this.formatDateForGrid(item.license_due_date || ''),
        resolvedDate: this.formatDateForGrid(item.license_resolved_date || item.endorsement_resolved_date || item.resolved_date || ''),
        resolutionNotes: item.resolution_notes || '',
        additionalText: item.license_additional_text || item.additional_text || '',
        status: (() => {
          const formatted = this.formatStatusText(item.cd_status);
          return formatted?.toLowerCase() === 'pending' ? 'Active' : formatted;
        })(),
        extensionStatus: item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.license_due_date)
      }
    }) : [];

    if (rows?.length > 0) {
      const provisionSet = new Set(
        rows.map(a2 => a2.provision)
      );

      this.licenseProvTypedata = this.licenseProvTypedata.filter(a1 =>
        !provisionSet.has(a1.label)
      );

    }
    else {
      this.licenseProvTypedata = this.licenseProvTypedataCopy;
    }


    this.licenseProvisionsGridConfig = this.licenseProvisionsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.licenseProvisionsColumnVisibility,
      _changeKey: Date.now()
    });
  }

  private buildEndorsementProvisionsGrid(): void {
    const headers = (this.licenseMetadataHeaders['endorsementProvisions'] || []).map((h: any) =>
      h.dataField === 'endorsement' ? { ...h, cssClass: 'lulsource-wrap-column' } : h
    );
    // Get real data from p_end_provision
    const endorsementProvisions = this.EndorsementData || [];

    // Map API data to grid rows
    const rows = Array.isArray(endorsementProvisions) ? endorsementProvisions.map((item: any, index: number) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDateObj = new Date(item.endorsement_due_date);
      dueDateObj.setHours(0, 0, 0, 0);
      return {
        id: item.person_license_provision_id || index + 1,  // Use actual DB primary key from API
        provisionId: item.person_license_provision_id,           // Store primary key separately
        applicationId: item.application_id,
        person_license_provision_id: item.person_license_provision_id,
        provision: this.licenseEndoProvTypedataCopy?.find((opt: any) => opt.value == item.cd_endor_provision)?.label || '',
        endorsement: item.cd_endoresement
          ?.map((id: number) => this.endorsementOptions(id.toString()))
          .join(', '),
        endorsementIds: item.cd_endoresement || [],
        dueDate: this.formatDateForGrid(item.endorsement_due_date || ''),
        resolvedDate: this.formatDateForGrid(item.endorsement_resolved_date || item.license_resolved_date || item.resolved_date || ''),
        resolutionNotes: item.resolution_notes || '',
        additionalText: item.endorsement_additional_text || item.additional_text || '',
        status: (() => {
          const formatted = this.formatStatusText(item.cd_status);
          return formatted?.toLowerCase() === 'pending' ? 'Active' : formatted;
        })(),
        extensionStatus: item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.endorsement_due_date)
      }
    }) : [];


    if (rows?.length > 0) {
      const provisionSet = new Set(
        rows.map(a2 => a2.provision)
      );

      this.licenseEndoProvTypedata = this.licenseEndoProvTypedata.filter(a1 =>
        !provisionSet.has(a1.label)
      );

    }
    else {
      this.licenseEndoProvTypedata = this.licenseEndoProvTypedataCopy;
    }


    this.endorsementProvisionsGridConfig = this.endorsementProvisionsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.endorsementProvisionsColumnVisibility,
      _changeKey: Date.now()
    });
  }

  private buildCombinedProvisionsGrid(): void {
    const headers = (this.licenseMetadataHeaders['combinedProvisions'] || []).map((h: any) =>
      h.dataField === 'source'
        ? { ...h, cssClass: 'lulsource-wrap-column' }
        : h
    );

    const licenseRows = Array.isArray(this.ProvisionData) ? this.ProvisionData.map((item: any, index: number) => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const dueDateObj = new Date(item.license_due_date); dueDateObj.setHours(0, 0, 0, 0);
      return {
        id: item.person_license_provision_id || index + 1,
        provisionId: item.person_license_provision_id,
        person_license_provision_id: item.person_license_provision_id,
        applicationId: item.old_id,
        category: 'License Kind',
        provision: this.licenseProvTypedataCopy?.find((opt: any) => opt.value == item.cd_license_provision)?.label || '',
        source: item.cd_license_kind?.map((id: number) => this.licensekindName(id)).filter((s: string) => s).join(', ') || '',
        dueDate: this.formatDateForGrid(item.license_due_date || ''),
        resolvedDate: this.formatDateForGrid(item.license_resolved_date || item.endorsement_resolved_date || item.resolved_date || ''),
        resolutionNotes: item.resolution_notes || '',
        additionalText: item.license_additional_text || item.additional_text || '',
       // status: dueDateObj < today ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending',
       // extensionStatus: this.getExtensionEligibilityStatus(item.license_due_date)
        status: item.cd_status?.toLowerCase() === 'inactive'
          ? 'Inactive'
          : (dueDateObj < today ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending'),
        extensionStatus: item.cd_status?.toLowerCase() === 'inactive' || item.cd_status?.toLowerCase() === 'resolved'  ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.license_due_date),
        updatedBy: item.name || ''   
        // status: (() => {
        //   const formatted = this.formatStatusText(item.cd_status);
        //   return formatted?.toLowerCase() === 'pending' ? 'Active' : formatted;
        // })(),
      //  extensionStatus: item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.license_due_date)
      };
    }) : [];

    const endorsementRows = Array.isArray(this.EndorsementData) ? this.EndorsementData.map((item: any, index: number) => {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const dueDateObj = new Date(item.endorsement_due_date); dueDateObj.setHours(0, 0, 0, 0);
      return {
        id: item.person_license_provision_id || index + 1,
        provisionId: item.person_license_provision_id,
        person_license_provision_id: item.person_license_provision_id,
        applicationId: item.application_id,
        category: 'Endorsement',
        categoryDisplay: 'Endorsement',
        provision: this.licenseEndoProvTypedataCopy?.find((opt: any) => opt.value == item.cd_endor_provision)?.label || '',
        source: item.cd_endoresement?.map((id: number) => this.endorsementOptions(id.toString())).filter((s: string) => s).join(', ') || '',
        dueDate: this.formatDateForGrid(item.endorsement_due_date || ''),
        resolvedDate: this.formatDateForGrid(item.endorsement_resolved_date || item.license_resolved_date || item.resolved_date || ''),
        resolutionNotes: item.resolution_notes || '',
        additionalText: item.endorsement_additional_text || item.additional_text || '',
        //status: dueDateObj < today ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending',
        //extensionStatus: this.getExtensionEligibilityStatus(item.endorsement_due_date)
        status: item.cd_status?.toLowerCase() === 'inactive'
          ? 'Inactive'
          : (dueDateObj < today ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending'),
        extensionStatus: item.cd_status?.toLowerCase() === 'inactive' || item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.endorsement_due_date),
         updatedBy: item.name || ''
   
        // status: (() => {
        //   const formatted = this.formatStatusText(item.cd_status);
        //   return formatted?.toLowerCase() === 'pending' ? 'Active' : formatted;
        // })(),
       // extensionStatus: item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.endorsement_due_date)
      };
    }) : [];

    const rows = [...licenseRows, ...endorsementRows];

    this.combinedProvisionsGridConfig = this.combinedProvisionsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.combinedProvisionsColumnVisibility,
      _changeKey: Date.now()
    });
  }

  onCombinedProvisionsDownload(): void {
    const grid = this.combinedProvisionsGridRef;
    if (!grid || typeof grid.exportFilteredDataToExcel !== 'function') {
      this.alertService.warn('Grid is not ready to export.');
      return;
    }
    const cols = this.combinedProvisionsExcelColumns.filter(
      c => this.combinedProvisionsColumnVisibility[c.key] !== false
    );
    grid.exportFilteredDataToExcel(cols, 'Provisions.xlsx');
  }

  toggleCombinedProvisionsColumnChooser(): void {
    this.showCombinedProvisionsColumnChooser = !this.showCombinedProvisionsColumnChooser;
  }

  closeCombinedProvisionsColumnChooser(): void {
    this.showCombinedProvisionsColumnChooser = false;
  }

  onCombinedProvisionsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.combinedProvisionsColumnVisibility[ev.key] !== false;
    this.combinedProvisionsColumnVisibility = {
      ...this.combinedProvisionsColumnVisibility,
      [ev.key]: !cur
    };
    this.combinedProvisionsGridConfig = this.combinedProvisionsGridConfig.updateConfig({
      visibleColumns: { ...this.combinedProvisionsColumnVisibility },
      _changeKey: Date.now()
    });
  }

  /**
   * Transform requirement type code to user-friendly label
   * LICENSE -> License Kind
   * ENDORSEMENT -> Endorsement
   */
  private getRequirementTypeLabel(typeCode: string): string {
    const option = this.requirementTypeOptions.find(opt => opt.value.toLowerCase() === typeCode.toLowerCase());
    return option ? option.label : typeCode;
  }

  private getRequirementOptionsLabel(typeCode: string): string {
    const option = this.requirementOptions.find(opt => opt.value === typeCode)  || this.allRequirementOptions.find(opt => opt.value === typeCode);
    return option ? option.label : typeCode;
  }

  private buildLicenseRequirementsGrid(): void {
    const headers = this.licenseMetadataHeaders['licenseRequirements'] || [];

    // Get real data from p_license_requirements
    const licenseRequirements = this.educatorDashboardRow?.p_license_requirements || [];

    console.log('[LookupUserLicenseTab] License Requirements data:', licenseRequirements);
    console.log('[LookupUserLicenseTab] License Requirements count:', Array.isArray(licenseRequirements) ? licenseRequirements.length : 'Not an array');

    // Map API data to grid rows
    // const rows = Array.isArray(licenseRequirements) ? licenseRequirements.map((item: any, index: number) => ({
    //   id: item.application_license_requirement_id || item.id || index + 1,  // Use actual DB primary key
    //   requirementId: item.application_license_requirement_id || item.id,     // Store primary key separately
    //   applicationId: item.application_id,                        // Store application_id
    //   requirement: item.requirement || '',
    //   type: this.getRequirementTypeLabel(item.le_type || ''),    // Transform code to label
    //   dueDate: this.formatDateForGrid(item.due_date || ''),
    //   resolvedDate: this.formatDateForGrid(item.resolved_date || ''),
    //   additionalText: item.additional_text || '',
    //  // status: item.cd_status || '',
    //     status: new Date(item.due_date) < new Date()
    //                        ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending',
    //   extensionStatus: this.getExtensionEligibilityStatus(item.due_date)
    // })) : [];

    const rows = Array.isArray(licenseRequirements)
      ? licenseRequirements.map((item: any, index: number) => {

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const dueDateObj = new Date(item.due_date);
        dueDateObj.setHours(0, 0, 0, 0);

        return {
          id: item.application_license_requirement_id || item.id || index + 1,
          requirementId: item.application_license_requirement_id || item.id,
          applicationId: item.application_id,
          requirement: item.req_name || '',
          type: this.getRequirementTypeLabel(item.req_category || ''),
          dueDate: this.formatDateForGrid(item.due_date || ''),
          resolvedDate: this.formatDateForGrid(item.resolved_date || ''),
          additionalText: item.additional_text || '',
          status: (() => {
          const formatted = this.formatStatusText(item.cd_status);
          return formatted?.toLowerCase() === 'pending' ? 'Active' : formatted;
        })(),

          extensionStatus: item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.due_date)
        };
      })
      : [];

    this.licenseRequirementsGridConfig = this.licenseRequirementsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.licenseRequirementsColumnVisibility,
      _changeKey: Date.now()
    });
  }

  private buildLicenseRequirementsGridFromData(licenseRequirements: any[]): void {
    const headers = this.licenseMetadataHeaders['licenseRequirements'] || [];

    console.log('[LookupUserLicenseTab] buildLicenseRequirementsGridFromData count:', licenseRequirements.length);

    // const rows = licenseRequirements.map((item: any, index: number) => ({
    //   id: item.person_license_requirement_id || item.application_license_requirement_id || item.id || index + 1,
    //   requirementId: item.person_license_requirement_id || item.application_license_requirement_id || item.id,
    //   applicationId: item.application_id,
    //   requirement: item.requirement || item.cd_requirement || '',
    //   type: this.getRequirementTypeLabel(item.le_type || ''),
    //   leType: item.le_type || '',
    //   leSource: item.le_source || [],
    //   dueDate: this.formatDateForGrid(item.due_date || ''),
    //   resolvedDate: this.formatDateForGrid(item.resolved_date || ''),
    //   additionalText: item.additional_text || '',
    //   status: item.cd_status || '',
    //   extensionStatus: this.getExtensionEligibilityStatus(item.due_date)
    // }));
    // const rows = licenseRequirements.map((item: any, index: number) => {
    //   const leType = item.le_type || '';
    //   const leSource: any[] = Array.isArray(item.le_source) ? item.le_source : [];
    //   return {
    //     id: item.person_license_requirement_id || item.application_license_requirement_id || item.id || index + 1,
    //     requirementId: item.person_license_requirement_id || item.application_license_requirement_id || item.id,
    //     applicationId: item.application_id,
    //     requirement: item.requirement || item.cd_requirement || '',
    //     type: this.getRequirementTypeLabel(leType),
    //     leType: leType,
    //     leSource: leSource,
    //     dueDate: this.formatDateForGrid(item.due_date || ''),
    //     resolvedDate: this.formatDateForGrid(item.resolved_date || ''),
    //     additionalText: item.additional_text || '',
    //     status: item.cd_status || '',
    //     extensionStatus: this.getExtensionEligibilityStatus(item.due_date)
    //   };
    // });
    const rows = licenseRequirements.map((item: any, index: number) => {
      // const today = new Date();
      // today.setHours(0, 0, 0, 0);

      // const dueDateObj = new Date(item.due_date);
      // dueDateObj.setHours(0, 0, 0, 0);

      return {
        id: item.person_license_requirement_id || item.application_license_requirement_id || item.id || index + 1,
        requirementId: item.person_license_requirement_id || item.application_license_requirement_id || item.id,
        applicationId: item.application_id,
        requirement: this.getRequirementOptionsLabel(item.cd_requirement || ''),
        cdRequirement: item.cd_requirement || '',
        type: this.getRequirementTypeLabel(item.le_type || ''),
        source: Array.isArray(item.le_source)
          ? item.le_source.map((id: any) =>
              item.le_type?.toLowerCase() === 'endorsement'
                ? (this.endorsementTypeOptions.find((o: any) => o.value == id)?.label || id)
                : (this.licenseKindOptions.find((o: any) => o.value == id)?.label || id)
            ).filter((s: any) => s).join(', ')
          : '',
        leType: item.le_type?.toLowerCase() === 'license kind' ? 'LICENSE' : (item.le_type || ''),
        leSource: item.le_source || [],
        dueDate: this.formatDateForGrid(item.due_date || ''),
        resolvedDate: this.formatDateForGrid(item.resolved_date || ''),
        resolutionNotes: item.resolution_notes || '',
        additionalText: item.additional_text || '',
        // status: item.cd_status?.toLowerCase() === 'inactive'
        //   ? 'Inactive'
        //   : (dueDateObj < today ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending'),
        status: item.cd_status || '',
        extensionStatus: item.cd_status?.toLowerCase() === 'inactive' || item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.due_date),
         updatedBy: item.name || ''   
        // status: (() => {
        //   const formatted = this.formatStatusText(item.cd_status);
        //   return formatted?.toLowerCase() === 'pending' ? 'Active' : formatted;
        // })(),
        // extensionStatus: item.cd_status?.toLowerCase() === 'resolved' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.due_date)
      }
    });

    console.log('[LookupUserLicenseTab] buildLicenseRequirementsGridFromData mapped rows:', rows);

    this.licenseRequirementsGridConfig = this.licenseRequirementsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.licenseRequirementsColumnVisibility,
      _changeKey: Date.now()
    });

    this.cd.detectChanges();
  }

  // Panel toggle methods
  toggleLicenseKindsPanel(): void {
    this.showLicenseKindsPanel = !this.showLicenseKindsPanel;
    if (this.showLicenseKindsPanel) {
      this.getLicenseKindEndorsementsByPersonId();
    }
  }

  toggleProvisionsPanel(): void {
    this.buildLicenseGrids();
    this.showProvisionsPanel = !this.showProvisionsPanel;
  }

  toggleLicenseRequirementsPanel(): void {
    this.showLicenseRequirementsPanel = !this.showLicenseRequirementsPanel;
    if (this.showLicenseKindsPanel) {
      this.getLicenseKindEndorsementsByPersonId();
    }
  }

  // Current License Kinds actions
  onCurrentLicenseKindsDownload(): void {
    const grid = this.currentLicenseKindsGridRef;
    if (!grid || typeof grid.exportFilteredDataToExcel !== 'function') {
      this.alertService.warn('Grid is not ready to export.');
      return;
    }
    const cols = this.currentLicenseKindsExcelColumns.filter(
      c => this.currentLicenseKindsColumnVisibility[c.key] !== false
    );
    grid.exportFilteredDataToExcel(cols, 'CurrentLicenseKinds.xlsx');
  }

  toggleCurrentLicenseKindsColumnChooser(): void {
    this.showCurrentLicenseKindsColumnChooser = !this.showCurrentLicenseKindsColumnChooser;
  }

  closeCurrentLicenseKindsColumnChooser(): void {
    this.showCurrentLicenseKindsColumnChooser = false;
  }

  onCurrentLicenseKindsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.currentLicenseKindsColumnVisibility[ev.key] !== false;
    this.currentLicenseKindsColumnVisibility = {
      ...this.currentLicenseKindsColumnVisibility,
      [ev.key]: !cur
    };
    const prev = this.currentLicenseKindsGridConfig;
    this.currentLicenseKindsGridConfig = prev.updateConfig({
      visibleColumns: { ...this.currentLicenseKindsColumnVisibility },
      _changeKey: Date.now()
    });
  }

  onLicenseKindsHistoryDownload(): void {
    const grid = this.licenseKindsHistoryGridRef;
    if (!grid || typeof grid.exportFilteredDataToExcel !== 'function') {
      this.alertService.warn('Grid is not ready to export.');
      return;
    }
    const cols = this.licenseKindsHistoryExcelColumns.filter(
      c => this.licenseKindsHistoryColumnVisibility[c.key] !== false
    );
    grid.exportFilteredDataToExcel(cols, 'LicenseKindsHistory.xlsx');
  }

  toggleLicenseKindsHistoryColumnChooser(): void {
    this.showLicenseKindsHistoryColumnChooser = !this.showLicenseKindsHistoryColumnChooser;
  }

  closeLicenseKindsHistoryColumnChooser(): void {
    this.showLicenseKindsHistoryColumnChooser = false;
  }

  onLicenseKindsHistoryColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.licenseKindsHistoryColumnVisibility[ev.key] !== false;
    this.licenseKindsHistoryColumnVisibility = {
      ...this.licenseKindsHistoryColumnVisibility,
      [ev.key]: !cur
    };
    const prev = this.licenseKindsHistoryGridConfig;
    this.licenseKindsHistoryGridConfig = prev.updateConfig({
      visibleColumns: { ...this.licenseKindsHistoryColumnVisibility },
      _changeKey: Date.now()
    });
  }

  onReverseExtension(): void {

    // const licenseInfo = [this.educatorDashboardRow?.p_license_dtls.find((item)=> !!item.license_number)] || [];
    const licenseInfo = this.educatorDashboardRow?.p_license_dtls?.filter((item) => !!item.license_number) || [];
    // const licenseInfo = this.getLicenseKindEndorsementsData;
    console.log(licenseInfo, this.educatorDashboardRow?.p_license_dtls);
    if (!Array.isArray(licenseInfo) || licenseInfo.length === 0) {
      this.alertService.warn('No license information available to reverse extension.');
      return;
    }
    // Calculate new expiry date (180 days before current expiry)
    const expiryDate = licenseInfo[0]?.expiry_date;
    if (!expiryDate) {
      this.alertService.warn('No expiry date found for license.');
      return;
    }

    const date = new Date(expiryDate);
    date.setDate(date.getDate() - 180);
    const reverseExpiryDate = date.toISOString().slice(0, 10);

    // Open modal
    const modalRef = this.modalService.open(ModalComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md'
    });

    const modalConfig: ModalConfig = {
      buttons: [
        { text: modalButtonTitles.cancel, code: 'N', btnOutline: true },
        { text: 'Proceed', code: 'Y', btnOutline: false }
      ],
      heading: 'Reverse Extension',
      align: true,
      modalContent: 'Are you sure you want to reverse all active extensions for this license? This will restore the previous effective and expiration dates for any License Kinds, Endorsements, Provisions, or License Requirements that were extended.'
    };

    modalRef.componentInstance.modalConfig = modalConfig;
    // Remove focus after modal closes (for both Cancel and Proceed)
    modalRef.result.finally(() => {
      setTimeout(() => {
        this.reverseExtensionBtn?.nativeElement?.blur();
      }, 100);
    });
    modalRef.componentInstance.emitData.subscribe((data: any) => {
      if (data.confirmed) {
        const licenseData = this.getLicenseData();





        // ── Current license : eligible rows due dates ──
        //     const eligibleRows = this.currentLicenseKindsGridConfig?.data?.filter(
        //       (row: any) => row.extensionStatus?.toLowerCase() === 'eligible'
        //     );

        // let licenseDueDate: string | null = null;
        // if (eligibleRows?.length > 0) {
        //   const parsedDates = eligibleRows
        //     .map((row: any) => this.parseLicenseDateValue(row.expirationDate))
        //     .filter((d): d is Date => d !== null);

        //       if (parsedDates.length > 0) {
        //         const earliestDate = new Date(Math.min(...parsedDates.map(d => d.getTime())));

        //         const month = String(earliestDate.getMonth() + 1).padStart(2, '0');
        //         const day = String(earliestDate.getDate()).padStart(2, '0');
        //         const year = earliestDate.getFullYear();

        //         licenseDueDate = `${month}/${day}/${year}`;
        //       }
        //     }


        // // ── License Provisions: eligible rows due dates ──
        // const eligibleLicenseProvisionRows = this.licenseProvisionsGridConfig?.data?.filter(
        //   (row: any) => row.extensionStatus?.toLowerCase() === 'eligible'
        // );

        // // ── Endorsement Provisions: eligible rows due dates ──
        // const eligibleEndorsementProvisionRows = this.endorsementProvisionsGridConfig?.data?.filter(
        //   (row: any) => row.extensionStatus?.toLowerCase() === 'eligible'
        // );

        // // ── Combine both provision grids and find minimum due date ──
        // const allEligibleProvisionRows = [
        //   ...(eligibleLicenseProvisionRows || []),
        //   ...(eligibleEndorsementProvisionRows || [])
        // ];

        // let endExpiredDate: string | null = null;
        // if (allEligibleProvisionRows.length > 0) {
        //   const parsedProvisionDates = allEligibleProvisionRows
        //     .map((row: any) => this.parseLicenseDateValue(row.dueDate))
        //     .filter((d): d is Date => d !== null);

        //   if (parsedProvisionDates.length > 0) {
        //     const earliestProvisionDate = new Date(Math.min(...parsedProvisionDates.map(d => d.getTime())));
        //     const month = String(earliestProvisionDate.getMonth() + 1).padStart(2, '0');
        //     const day = String(earliestProvisionDate.getDate()).padStart(2, '0');
        //     const year = earliestProvisionDate.getFullYear();
        //     endExpiredDate = `${month}/${day}/${year}`;
        //   }
        // }

        //    // ── License Requirements: earliest due date among eligible rows ──
        // const eligibleLicenseRequirementRows = this.licenseRequirementsGridConfig?.data?.filter(
        //   (row: any) => row.extensionStatus?.toLowerCase() === 'eligible'
        // );

        // let requirementDueDate: string | null = null;
        // if (eligibleLicenseRequirementRows?.length > 0) {
        //   const parsedRequirementDates = eligibleLicenseRequirementRows
        //     .map((row: any) => this.parseLicenseDateValue(row.dueDate))
        //     .filter((d): d is Date => d !== null);

        //   if (parsedRequirementDates.length > 0) {
        //     const earliestRequirementDate = new Date(Math.min(...parsedRequirementDates.map(d => d.getTime())));
        //     const month = String(earliestRequirementDate.getMonth() + 1).padStart(2, '0');
        //     const day = String(earliestRequirementDate.getDate()).padStart(2, '0');
        //     const year = earliestRequirementDate.getFullYear();
        //     requirementDueDate = `${month}/${day}/${year}`;
        //   }
        // }
        const payload = {
          licenseNumber: licenseData?.license_number || this.licenseNumber || '', //'300388', // licenseInfo[0]?.license_number,
          userId: this.personId ? Number(this.personId) : null,
          reverseExpiryDate: reverseExpiryDate,
          applicationNumber: licenseInfo[0]?.application_id,
          license_due_date: null,
          end_expiry_date: null,
          due_date: null
        };

        // TODO: Replace with actual API endpoint
        // const url = `${environment.apiHost}/api/search/reverse-license`;
        this.httpService.patch(API_ENDPOINTS.LICENSE_REVERSE_EXTENSION, payload).subscribe(
          (res: any) => {
            const success = res?.status === 'success' || res?.data?.success;
            if (success) {
              this.alertService.success('License extension reversed successfully.');
              this.requestDashboardRefresh.emit();
              this.showReverseExtersion = false;
              this.getTableAPI();
            } else {
              this.alertService.error('Failed to reverse license extension.');
            }
          },
          (error) => {
            this.alertService.error('Failed to reverse license extension.');
          }
        );
      }
    });
  }


  onAddEndorsement(): void {
    this.showAddEndorsementForm = true;
    this.addEndorsementForm.reset();
  }

  onCancelAddEndorsement(): void {
    this.showAddEndorsementForm = false;
    this.addEndorsementForm.reset();
  }

  onSaveAddEndorsement(): void {
    if (this.addEndorsementForm.invalid) {
      this.addEndorsementForm.markAllAsTouched();
      this.alertService.error('Please fill in all required fields.');
      return;
    }

    // Get existing license data from educator dashboard
    const licenseInfo = this.educatorDashboardRow?.p_license_dtls || [];
    const existingLicense = Array.isArray(licenseInfo) && licenseInfo.length > 0 ? licenseInfo[0] : null;

    if (!existingLicense) {
      this.alertService.warn('No existing license information found. Cannot add endorsement.');
      return;
    }

    const raw = this.addEndorsementForm.value;

    // Build complete payload with user inputs and existing license data
    const payload = {
      // User inputs from form (fields marked with $ in the query)
      cd_license_type: raw.licenseType,
      cd_license_kind: raw.licenseKind,
      cd_endorsement: raw.endorsementGrade,
      endorsement_pathway: raw.endorsementPathway,
      effective_from: raw.effectiveDate,

      // Existing license data from educator-dashboard-data API
      person_id: this.personId ? Number(this.personId) : null,
      license_number: existingLicense.license_number || '',
      authority_name: '', // Not available in current data structure
      issue_date: existingLicense.issue_date || '',
      expiry_date: existingLicense.expiry_date || '',
      notes: '',
      is_active: existingLicense.is_active !== undefined ? existingLicense.is_active : true,
      application_id: existingLicense.application_id || 0,
      cd_state: '',
      out_of_state_licensure: false,
      cd_provision: '',
      additional_text: '',
      due_date: '',
      cd_grade: '',
      end_original_issue_date: '',
      end_issue_date: '',
      end_expiry_date: '',
      old_id: '',
      out_of_state_license_number: '',
      cd_endo_status: '1', // Default status: Pending
      cd_lk_status: '',
      cd_license_status: '',
      license_code: ''
    };

    this.manageProfileService
      .addLicenseEndorsement(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const success = res?.data?.success === true;
          if (success) {
            this.alertService.success(this.lookupToast?.['T0005'] || 'Added Successfully.', true);

            // Auto-dismiss alert after 5 seconds
            setTimeout(() => {
              // Alert will be dismissed automatically by the alert service
            }, 5000);

            this.showAddEndorsementForm = false;
            this.addEndorsementForm.reset();

            // Request parent to refresh educator dashboard data from API
            // When new data arrives via @Input() row setter, buildLicenseGrids() will be called automatically
            this.buildLicenseGrids();
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Failed to add endorsement.');
          }
        },
        error: err => {
          this.alertService.error(err?.error?.message || err?.message || 'Failed to add endorsement.');
        }
      });
  }

  // License Provisions actions
  onLicenseProvisionsDownload(): void {
    const grid = this.licenseProvisionsGridRef;
    if (!grid || typeof grid.exportFilteredDataToExcel !== 'function') {
      this.alertService.warn('Grid is not ready to export.');
      return;
    }
    const cols = this.licenseProvisionsExcelColumns.filter(
      c => this.licenseProvisionsColumnVisibility[c.key] !== false
    );
    grid.exportFilteredDataToExcel(cols, 'LicenseProvisions.xlsx');
  }

  toggleLicenseProvisionsColumnChooser(): void {
    this.showLicenseProvisionsColumnChooser = !this.showLicenseProvisionsColumnChooser;
  }

  closeLicenseProvisionsColumnChooser(): void {
    this.showLicenseProvisionsColumnChooser = false;
  }

  onLicenseProvisionsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.licenseProvisionsColumnVisibility[ev.key] !== false;
    this.licenseProvisionsColumnVisibility = {
      ...this.licenseProvisionsColumnVisibility,
      [ev.key]: !cur
    };
    const prev = this.licenseProvisionsGridConfig;
    this.licenseProvisionsGridConfig = prev.updateConfig({
      visibleColumns: { ...this.licenseProvisionsColumnVisibility },
      _changeKey: Date.now()
    });
  }

  get currentProvisionOptions(): any[] {
    const category = this.addCombinedProvisionForm?.get('category')?.value;
    return category === 'Endorsement' ? this.licenseEndoProvTypedata : this.licenseProvTypedata;
  }

  get currentSourceOptions(): any[] {
    const category = this.addCombinedProvisionForm?.get('category')?.value;
    return category === 'Endorsement' ? this.getEndorsementTypeFilterOptions : this.getlicenseKindFilterOptions;
  }

  get editSourceOptions(): any[] {
    return this.selectedCombinedProvision?.category === 'Endorsement'
      ? this.getEndorsementTypeFilterOptions
      : this.getlicenseKindFilterOptions;
  }

  onAddProvision(): void {
    this.showAddProvisionForm = true;
    this.addCombinedProvisionForm.reset();
    this.loadProvisionTypesWithFallback().catch(err => {
      console.error('Failed to load provision types:', err);
      this.alertService.warn('Unable to load provision options. Please try again.');
    });
  }

  onCancelAddProvision(): void {
    this.showAddProvisionForm = false;
    this.addCombinedProvisionForm.reset();
  }

  onProvisionCategoryChange(event: any): void {
    this.addCombinedProvisionForm.patchValue({
      provision: null,
      source: [],
      dueDate: ''
    });
  }

  onAddCombinedProvisionChange(event: any): void {
    const category = this.addCombinedProvisionForm.get('category')?.value;
    if (category !== 'License Kind') return;
    const provisionValue = this.addCombinedProvisionForm.get('provision')?.value;
    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    if (autoDate) {
      this.addCombinedProvisionForm.get('dueDate')?.setValue(autoDate);
    }
  }

  onCombinedSourceChangeAdd(event: any): void {
    if (this.addCombinedProvisionForm.get('category')?.value === 'Endorsement') {
      const selectedIds = this.addCombinedProvisionForm.get('source')?.value || [];
      const dueDate = this.getMinimumExpirationDate(selectedIds, 'endorsement');
      if (dueDate) {
        this.autoCalculatedRequirementDueDate = dueDate;
        this.addCombinedProvisionForm.get('dueDate')?.setValue(
          moment(dueDate).format('MM/DD/YYYY')
        );
      } else {
        this.autoCalculatedRequirementDueDate = '';
        this.addCombinedProvisionForm.get('dueDate')?.setValue(null);
      }
    }
  }

  onCombinedSourceChangeEdit(event: any): void {
    if (this.selectedCombinedProvision?.category === 'Endorsement') {
      const selectedIds = this.editCombinedProvisionForm.get('source')?.value || [];
      const dueDate = this.getMinimumExpirationDate(selectedIds, 'endorsement');
      if (dueDate) {
        const formatted = moment(dueDate).format('MM/DD/YYYY');
        this.autoCalculatedRequirementDueDate = formatted;
        this.editCombinedProvisionForm.get('dueDate')?.setValue(formatted);
        this.editCombinedProvisionForm.get('dueDate')?.markAsDirty();
      } else {
        this.autoCalculatedRequirementDueDate = '';
        this.editCombinedProvisionForm.get('dueDate')?.setValue(null);
      }
    }
  }

  onSaveAddCombinedProvision(): void {
    const category = this.addCombinedProvisionForm.get('category')?.value;

    // Apply validator for source
    this.addCombinedProvisionForm.get('source')?.setValidators(Validators.required);
    this.addCombinedProvisionForm.get('source')?.updateValueAndValidity();

    if (this.addCombinedProvisionForm.invalid) {
      this.addCombinedProvisionForm.markAllAsTouched();
      this.alertService.error(this.lookupToast?.['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    const raw = this.addCombinedProvisionForm.value;
    const userFromSession = JSON.parse(this.sessionStorage.getItem('user'));

    const payload = category === 'License Kind'
      ? {
          person_id: this.personId ? Number(this.personId) : null,
          cd_license_kind: raw.source,
          cd_license_provision: raw.provision,
          cd_endoresement: null,
          license_additional_text: raw.additionalText,
          license_due_date: this.formatDateLocal(raw.dueDate),
          cd_endor_provision: null,
          endorsement_additional_text: null,
          endorsement_due_date: null,
          license_resolved_date: null,
          endorsement_resolved_date: null,
          cd_status: 'Pending',
          cd_extension_status: null,
          created_by: userFromSession.id
        }
      : {
          person_id: this.personId ? Number(this.personId) : null,
          cd_license_kind: null,
          cd_license_provision: null,
          cd_endoresement: raw.source,
          license_additional_text: null,
          license_due_date: null,
          cd_endor_provision: raw.provision,
          endorsement_additional_text: raw.additionalText,
          endorsement_due_date: this.formatDateLocal(raw.dueDate),
          license_resolved_date: null,
          endorsement_resolved_date: null,
          cd_status: 'Pending',
          cd_extension_status: null,
          created_by: userFromSession.id
        };

    this.manageProfileService.saveLicenseProvisions(payload).subscribe({
      next: (data) => {
        this.getLicenseProvisionByPersonId();
        this.alertService.success(this.lookupToast?.['T0005'] || 'Added Successfully.', true);
        this.showAddProvisionForm = false;
        this.addCombinedProvisionForm.reset();
      },
      error: (err) => {
        this.alertService.error(err?.error?.message || err?.message || 'Failed to add provision.');
      }
    });
  }

  onAddLicenseProvision(): void {
    this.showAddLicenseProvisionForm = true;
    this.addProvisionForm.reset();
    // Try different parameter values to load provision type data
    this.loadProvisionTypesWithFallback().catch(err => {
      console.error('Failed to load provision types:', err);
      this.alertService.warn('Unable to load provision options. Please try again.');
    });
  }

  onCancelAddLicenseProvision(): void {
    this.showAddLicenseProvisionForm = false;
    this.addProvisionForm.reset();
  }

  onSaveAddLicenseProvision(): void {
    console.log('[onSaveAddLicenseProvision] Starting...');

    if (this.addProvisionForm.invalid) {
      console.log('[onSaveAddLicenseProvision] Form is invalid:', this.addProvisionForm.errors);
      this.addProvisionForm.markAllAsTouched();
      this.alertService.error(this.lookupToast?.['T0017'] || 'Missing required information. Complete all fields and documents to continue.');

      return;
    }

    const raw = this.addProvisionForm.value;
    console.log('[onSaveAddLicenseProvision] Form values:', raw);
    console.log('[onSaveAddLicenseProvision] Provision value (should be KEY, not text):', raw.provision);
    console.log('[onSaveAddLicenseProvision] License Kind value:', raw.selectedLicenseKinds);

    const userFromSession = JSON.parse(this.sessionStorage.getItem('user'));
    const persondata = JSON.parse(this.sessionStorage.getItem('personData'));
    const payload = {
      person_id: this.personId ? Number(this.personId) : null,
      cd_license_kind: this.addProvisionForm.controls['selectedLicenseKinds'].value,
      cd_license_provision: this.addProvisionForm.controls['provision'].value,
      cd_endoresement: null, // array (_int4)
      license_additional_text: this.addProvisionForm.controls['additionalText'].value,
      license_due_date: this.formatDateLocal(this.addProvisionForm.controls['dueDate'].value),
      cd_endor_provision: null,
      endorsement_additional_text: null,
      endorsement_due_date: null,
      license_resolved_date: null,  //no need
      endorsement_resolved_date: null,  //no need
      cd_status: 'Pending',  //no need
      cd_extension_status: null,  //no need
      created_by: userFromSession.id
    }

    this.manageProfileService.saveLicenseProvisions(payload).subscribe({
      next: (data) => {
        this.getLicenseProvisionByPersonId();
        console.log('[onSaveAddLicenseProvision] Response received:', data);
        this.alertService.success(this.lookupToast?.['T0005'] || 'Added Successfully.', true);


        // Close form and refresh
        this.showAddLicenseProvisionForm = false;
        this.addProvisionForm.reset();

        // Reload educator dashboard data to show the new provision
        console.log('[onSaveAddLicenseProvision] Requesting dashboard refresh...');
        // this.buildLicenseGrids();
      },
      error: (err) => {
        console.error('[onSaveAddLicenseProvision] HTTP Error:', err);
        console.error('[onSaveAddLicenseProvision] Error details:', {
          status: err?.status,
          statusText: err?.statusText,
          error: err?.error,
          message: err?.message
        });
        this.alertService.error(err?.error?.message || err?.message || 'Failed to add license provision.');
      }
    });
  }

  // Endorsement Provisions actions
  onEndorsementProvisionsDownload(): void {
    const grid = this.endorsementProvisionsGridRef;
    if (!grid || typeof grid.exportFilteredDataToExcel !== 'function') {
      this.alertService.warn('Grid is not ready to export.');
      return;
    }
    grid.exportFilteredDataToExcel(this.licenseProvisionsExcelColumns, 'EndorsementProvisions.xlsx');
  }

  toggleEndorsementProvisionsColumnChooser(): void {
    this.showEndorsementProvisionsColumnChooser = !this.showEndorsementProvisionsColumnChooser;
  }

  closeEndorsementProvisionsColumnChooser(): void {
    this.showEndorsementProvisionsColumnChooser = false;
  }

  onEndorsementProvisionsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.endorsementProvisionsColumnVisibility[ev.key] !== false;
    this.endorsementProvisionsColumnVisibility = {
      ...this.endorsementProvisionsColumnVisibility,
      [ev.key]: !cur
    };
    const prev = this.endorsementProvisionsGridConfig;
    this.endorsementProvisionsGridConfig = prev.updateConfig({
      visibleColumns: { ...this.endorsementProvisionsColumnVisibility },
      _changeKey: Date.now()
    });
  }

  onAddEndorsementProvision(): void {
    this.showAddEndorsementProvisionForm = true;
    this.addEndorsementProvisionForm.reset();
    // Try different parameter values to load provision type data
    this.loadProvisionTypesWithFallback().catch(err => {
      console.error('Failed to load endorsement provision types:', err);
      this.alertService.warn('Unable to load provision options. Please try again.');
    });
  }

  onCancelAddEndorsementProvision(): void {
    this.showAddEndorsementProvisionForm = false;
    this.addEndorsementProvisionForm.reset();
  }

  onSaveAddEndorsementProvision(): void {
    console.log('[onSaveAddEndorsementProvision] Starting...');
    if (this.addEndorsementProvisionForm.invalid) {
      console.log('[onSaveAddEndorsementProvision] Form is invalid:', this.addEndorsementProvisionForm.errors);
      this.addEndorsementProvisionForm.markAllAsTouched();
      this.alertService.error(this.lookupToast?.['T0017'] || 'Please fill in all required fields.');
      // this.alertService.error('Please fill in all required fields.');
      return;
    }

    // Get application_id from license details


    const raw = this.addEndorsementProvisionForm.value;
    console.log('[onSaveAddEndorsementProvision] Form values:', raw);
    console.log('[onSaveAddEndorsementProvision] Provision value (should be KEY, not text):', raw.provision);
    console.log('[onSaveAddEndorsementProvision] Endorsement value:', raw.selectedEndorsements);

    const userFromSession = JSON.parse(this.sessionStorage.getItem('user'));

    const payload = {
      person_id: this.personId ? Number(this.personId) : null,
      cd_license_kind: null,
      cd_license_provision: null,
      cd_endoresement: raw.selectedEndorsements, // array (_int4)
      license_additional_text: null,
      license_due_date: null,
      cd_endor_provision: raw.provision,
      endorsement_additional_text: raw.additionalText || '',
      endorsement_due_date: this.formatDateLocal(raw.dueDate),
      license_resolved_date: null,
      endorsement_resolved_date: null,
      cd_status: 'Pending',
      cd_extension_status: null,
      created_by: userFromSession.id
    };

    this.manageProfileService.saveLicenseProvisions(payload).subscribe({
      next: (data) => {
        this.getLicenseProvisionByPersonId();
        console.log('[onSaveAddEndorsementProvision] Response received:', data);
        // Show success message since data is being saved to database
        this.alertService.success(this.lookupToast?.['T0005'] || 'Added Successfully.', true);
        // this.alertService.success('Endorsement provision added successfully.');
        // Close form and refresh
        this.showAddEndorsementProvisionForm = false;
        this.addEndorsementProvisionForm.reset();
        // Reload educator dashboard data to show the new provision
        console.log('[onSaveAddEndorsementProvision] Requesting dashboard refresh...');
        // this.buildLicenseGrids();
      },
      error: (err) => {
        console.error('[onSaveAddEndorsementProvision] HTTP Error:', err);
        console.error('[onSaveAddEndorsementProvision] Error details:', {
          status: err?.status,
          statusText: err?.statusText,
          error: err?.error,
          message: err?.message
        });
        this.alertService.error(err?.error?.message || err?.message || 'Failed to add Endorsement provision.');
      }
    });
  }

  // Helper method to find key from label
  private findKeyFromLabel(options: any[], label: string): string {
    if (!label || !options || !Array.isArray(options)) {
      console.log('[findKeyFromLabel] Invalid input - label:', label, 'options length:', options?.length);
      return '';
    }

    // Try exact match first
    let found = options.find(opt => opt.label === label);

    // If no exact match, try case-insensitive and trimmed match
    if (!found && typeof label === 'string') {
      const normalizedLabel = label.trim().toLowerCase();
      found = options.find(opt => opt.label?.trim().toLowerCase() === normalizedLabel);
    }

    if (!found) {
      console.log('[findKeyFromLabel] No match found for label:', label);
      console.log('[findKeyFromLabel] Available options:', options.map(opt => opt.label));
    }

    return found ? found.value : '';
  }

  private findLabelFromKey(options: any[], key: string | number): string {
    if (key === null || key === undefined || !options || !Array.isArray(options)) {
      return '';
    }

    // Try exact match first
    const found = options.find(opt => opt.value == key); // Use == to handle string/number comparison

    return found ? found.label : '';
  }

  // Edit/Delete actions for grids
  openEditLicenseKindModal(row: any, index: number): void {
    console.log('[LookupUserLicenseTab] openEditLicenseKindModal called with index:', index, 'row:', row);
    this.selectedLicenseKind = { ...row };
    this.editLicenseKindIndex = index;

    // Populate the edit form with row data - find keys from labels
    this.editLicenseKindForm.patchValue({
      licenseType: this.findKeyFromLabel(this.licenseTypeOptions, row.licenseType),
      licenseKind: this.findKeyFromLabel(this.licenseKindOptions, row.licenseKind),
      endorsementGrade: this.findKeyFromLabel(this.endorsementTypeOptions, row.endorsementGrade),
      endorsementPathway: this.findKeyFromLabel(this.endorsementPathwayOptions, row.endorsementPathway)
    });

    this.editLicenseKindForm.markAsPristine();
    this.showEditLicenseKindModal = true;

    // Open NgbModal
    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'license-kind-modal custom-license-kind-modal'
    };
    this.customModal = this.modalService.open(this.editLicenseKindModal, options);
  }

  closeLicenseKindModal(): void {
    this.selectedLicenseKind = null;
    this.editLicenseKindForm.reset();
    this.editLicenseKindForm.markAsPristine();
    this.editLicenseKindForm.markAsUntouched();
    this.showEditLicenseKindModal = false;
    if (this.customModal) {
      this.customModal.close();
      this.customModal = null;
    }
  }

  onEditLicenseKindReset(): void {
    if (this.selectedLicenseKind) {
      this.editLicenseKindForm.patchValue({
        licenseType: this.findKeyFromLabel(this.licenseTypeOptions, this.selectedLicenseKind.licenseType),
        licenseKind: this.findKeyFromLabel(this.licenseKindOptions, this.selectedLicenseKind.licenseKind),
        endorsementGrade: this.findKeyFromLabel(this.endorsementTypeOptions, this.selectedLicenseKind.endorsementGrade),
        endorsementPathway: this.findKeyFromLabel(this.endorsementPathwayOptions, this.selectedLicenseKind.endorsementPathway)
      });
      this.editLicenseKindForm.markAsPristine();
    }
  }

  updateLicenseKind(): void {
    if (this.editLicenseKindForm.invalid) {
      this.editLicenseKindForm.markAllAsTouched();
      this.alertService.error('Please fill in all required fields.');
      return;
    }

    console.log('[updateLicenseKind] Updating license kind with data:', this.editLicenseKindForm.value);
    console.log('[updateLicenseKind] Selected row data:', this.selectedLicenseKind);

    // Get application_id from license details (fallback if not in row data)
    const licenseInfo = this.educatorDashboardRow?.p_license_dtls || [];
    const applicationId = this.selectedLicenseKind?.applicationId ||
      (Array.isArray(licenseInfo) && licenseInfo.length > 0 ? licenseInfo[0]?.application_id : null);

    if (!applicationId) {
      this.alertService.warn('No application information found. Cannot update license kind.');
      return;
    }

    // Get the primary key (license_kind_id) from the selected row
    const licenseKindId = this.selectedLicenseKind?.licenseKindId || this.selectedLicenseKind?.id;
    if (!licenseKindId) {
      this.alertService.error('Missing license kind ID. Cannot update record.');
      return;
    }

    const raw = this.editLicenseKindForm.value;

    // Build payload with primary key for unique identification
    const payload = {
      license_id: licenseKindId,                         // Primary key (license_id from DB) to identify exact row
      person_id: this.personId ? Number(this.personId) : null,
      application_id: applicationId,
      license_type: raw.licenseType,
      license_kind: raw.licenseKind,
      endorsement_grade: raw.endorsementGrade,
      endorsement_pathway: raw.endorsementPathway
    };

    console.log('[updateLicenseKind] Payload with primary key:', JSON.stringify(payload, null, 2));
    this.manageProfileService
      .updateLicenseEndorsement(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          console.log('[updateLicenseKind] Response:', res);
          this.alertService.success(this.lookupToast?.['T0020'] || 'Updated Successfully.', true);
          this.closeLicenseKindModal();

          // Refresh data from backend
          this.requestDashboardRefresh.emit();
        },
        error: err => {
          console.error('[updateLicenseKind] Error:', err);
          this.alertService.error(err?.error?.message || err?.message || 'Failed to update license kind.');
        }
      });
  }

  onEditLicenseKind(data: any): void {
    console.log('[LookupUserLicenseTab] Edit license kind:', data);
    // Find the index of this row in the grid data
    const index = this.currentLicenseKindsGridConfig.data.findIndex((item: any) => item.id === data.id);
    this.openEditLicenseKindModal(data, index);
  }

  onDeleteLicenseKind(data: any): void {
    console.log('[LookupUserLicenseTab] Delete license kind:', data);
    this.deleteItemData = data;
    this.deleteItemType = 'license-kind';
    this.openDeleteConfirmModal();
  }

  get editCurrentProvisionOptions(): any[] {
    return this.selectedCombinedProvision?.category === 'Endorsement'
      ? this.licenseEndoProvTypedataCopy
      : this.licenseProvTypedataCopy;
  }

  onEditCombinedProvision(data: any, event: Event): void {
    event.stopPropagation();
    const index = this.combinedProvisionsGridConfig.data.findIndex(
      (item: any) => item.id === data.id && item.category === data.category
    );
    this.openEditCombinedProvisionModal(data, index);
  }

  onDeleteCombinedProvision(data: any, event: Event): void {
    event.stopPropagation();
    if (data.category === 'License Kind') {
      this.onDeleteProvision(data);
    } else {
      this.onDeleteEndorsementProvision(data);
    }
  }

  onEditProvision(data: any): void {
    console.log('[LookupUserLicenseTab] Edit license provision:', data);
    const index = this.combinedProvisionsGridConfig.data.findIndex((item: any) => item.id === data.id && item.category === 'License Kind');
    this.openEditCombinedProvisionModal(data, index);
  }

  openEditLicenseProvisionModal(row: any, index: number): void {
    console.log('[LookupUserLicenseTab] openEditLicenseProvisionModal called with index:', index, 'row:', row);
    console.log('[LookupUserLicenseTab] licenseProvTypedata:', this.licenseProvTypedata);
    console.log('[LookupUserLicenseTab] row.provision:', row.provision);

    this.selectedLicenseProvision = { ...row };
    this.editLicenseProvisionIndex = index;

    const provisionKey = this.findKeyFromLabel(this.licenseProvTypedataCopy, row.provision);
    //const licenseKindKey = this.findKeyFromLabel(this.licenseKindOptions, row.licenseKind);
    const licenseKindKey = this.licenseKindOptions
      .filter((opt: any) => row.licenseKind?.includes(opt.label))
      .map((opt: any) => opt.value);

    console.log('[LookupUserLicenseTab] Found provision key:', provisionKey);
    console.log('[LookupUserLicenseTab] Found licenseKind key:', licenseKindKey);

    this.editLicenseProvisionForm.patchValue({
      provision: provisionKey,
      licenseKind: licenseKindKey,
      dueDate: row.dueDate || '',
      additionalText: row.additionalText || ''
    });

    this.editLicenseProvisionForm.markAsPristine();
    this.showEditLicenseProvisionModal = true;

    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'license-provision-modal custom-license-provision-modal'
    };
    this.licenseProvisionModal = this.modalService.open(this.editLicenseProvisionModal, options);
  }

  closeLicenseProvisionModal(): void {
    this.selectedLicenseProvision = null;
    this.editLicenseProvisionForm.reset();
    this.editLicenseProvisionForm.markAsPristine();
    this.editLicenseProvisionForm.markAsUntouched();
    this.showEditLicenseProvisionModal = false;
    if (this.licenseProvisionModal) {
      this.licenseProvisionModal.close();
      this.licenseProvisionModal = null;
    }
  }

  onEditLicenseProvisionReset(): void {
    if (this.selectedLicenseProvision) {
      const licenseKindValues = this.licenseKindOptions
        .filter((opt: any) =>
          this.selectedLicenseProvision.licenseKind?.includes(opt.label)
        )
        .map((opt: any) => opt.value);
      this.editLicenseProvisionForm.patchValue({
        provision: this.findKeyFromLabel(this.licenseProvTypedata, this.selectedLicenseProvision.provision),
        licenseKind: licenseKindValues, dueDate: this.selectedLicenseProvision.dueDate || '',
        additionalText: this.selectedLicenseProvision.additionalText || ''
      });
      this.editLicenseProvisionForm.markAsPristine();
    }
  }

  updateLicenseProvision(): void {
    if (this.editLicenseProvisionForm.invalid) {
      this.editLicenseProvisionForm.markAllAsTouched();
      this.alertService.error(this.lookupToast?.['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    console.log('[updateLicenseProvision] Updating license provision with data:', this.editLicenseProvisionForm.value);
    console.log('[updateLicenseProvision] Selected row data:', this.selectedLicenseProvision);

    // Get the primary key (provision_id) from the selected row
    const provisionId = this.selectedLicenseProvision?.person_license_provision_id;
    if (!provisionId) {
      this.alertService.error('Missing provision ID. Cannot update record.');
      return;
    }

    const raw = this.editLicenseProvisionForm.value;
    const userFromSession = JSON.parse(this.sessionStorage.getItem('user'));
    // Build payload with primary key for unique identification
    const payload = {

      person_id: this.personId ? Number(this.personId) : null,
      license_additional_text: raw.additionalText || '',
      license_due_date: this.formatDateLocal(raw.dueDate),
      cd_license_provision: raw.provision,
      cd_license_kind: Array.isArray(raw.licenseKind)
        ? raw.licenseKind
          .filter((id: any) => id != null && id !== '')
          .map((id: any) => Number(id))
        : [],
      license_resolved_date: null,
      cd_status: this.selectedLicenseKind?.status || null,
      cd_extension_status: null,
      created_by: userFromSession.id,
      person_license_provision_id: provisionId,
      // license_end_provision_id: null,
      // endorsement_additional_text: null,
      // endorsement_due_date: null,
      // endorsement_resolved_date: null,
    };

    console.log('[updateLicenseProvision] Payload with primary key:', JSON.stringify(payload, null, 2));

    this.manageProfileService.updateLicenseProvisions(payload).subscribe({
      next: (data) => {
        console.log('[updateLicenseProvision] Response:', data);
        this.getLicenseProvisionByPersonId();
        // this.alertService.success('License provision updated successfully.');
        this.alertService.success(this.lookupToast?.['T0020'] || 'Updated Successfully.', true);
        this.closeLicenseProvisionModal();

        // Wait a moment for backend to commit, then refresh parent data
      },
      error: err => {
        console.error('[updateLicenseProvision] Error:', err);
        this.alertService.error(err?.error?.message || err?.message || 'Failed to update license provision.');
      }
    });
  }

  onDeleteProvision(data: any): void {
    console.log('[LookupUserLicenseTab] Delete license provision:', data);
    this.deleteItemData = data;
    this.deleteItemType = 'license-provision';
    this.openDeleteConfirmModal();
  }

  onEditEndorsementProvision(data: any): void {
    console.log('[LookupUserLicenseTab] Edit endorsement provision:', data);
    const index = this.combinedProvisionsGridConfig.data.findIndex((item: any) => item.id === data.id && item.category === 'Endorsement');
    this.openEditCombinedProvisionModal(data, index);
  }

  openEditEndorsementProvisionModal(row: any, index: number): void {
    console.log('[LookupUserLicenseTab] openEditEndorsementProvisionModal called with index:', index, 'row:', row);
    console.log('[LookupUserLicenseTab] licenseEndoProvTypedata:', this.licenseEndoProvTypedata);
    console.log('[LookupUserLicenseTab] row.provision:', row.provision);

    this.selectedEndorsementProvision = { ...row };
    this.editEndorsementProvisionIndex = index;

    const provisionKey = this.findKeyFromLabel(this.licenseEndoProvTypedataCopy, row.provision);
    const endorsementKey = this.endorsementTypeOptions
      .filter((opt: any) => row.endorsement?.includes(opt.label))
      .map((opt: any) => opt.value);

    console.log('[LookupUserLicenseTab] Found provision key:', provisionKey);
    console.log('[LookupUserLicenseTab] Found endorsement key:', endorsementKey);

    this.editEndorsementProvisionForm.patchValue({
      provision: provisionKey,
      endorsement: endorsementKey,
      dueDate: row.dueDate || '',
      additionalText: row.additionalText || ''
    });

    this.editEndorsementProvisionForm.markAsPristine();
    this.showEditEndorsementProvisionModal = true;

    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'endorsement-provision-modal custom-endorsement-provision-modal'
    };
    this.endorsementProvisionModal = this.modalService.open(this.editEndorsementProvisionModal, options);
  }

  closeEndorsementProvisionModal(): void {
    this.selectedEndorsementProvision = null;
    this.editEndorsementProvisionForm.reset();
    this.editEndorsementProvisionForm.markAsPristine();
    this.editEndorsementProvisionForm.markAsUntouched();
    this.showEditEndorsementProvisionModal = false;
    if (this.endorsementProvisionModal) {
      this.endorsementProvisionModal.close();
      this.endorsementProvisionModal = null;
    }
  }

  onEditEndorsementProvisionReset(): void {
    if (this.selectedEndorsementProvision) {
      const endorsementValues = this.endorsementTypeOptions
        .filter((opt: any) =>
          this.selectedEndorsementProvision.endorsement?.includes(opt.label)
        )
        .map((opt: any) => opt.value);
      this.editEndorsementProvisionForm.patchValue({
        provision: this.findKeyFromLabel(this.licenseEndoProvTypedata, this.selectedEndorsementProvision.provision),
        endorsement: endorsementValues,
        dueDate: this.selectedEndorsementProvision.dueDate || '',
        additionalText: this.selectedEndorsementProvision.additionalText || ''
      });
      this.editEndorsementProvisionForm.markAsPristine();
    }
  }

  updateEndorsementProvision(): void {
    if (this.editEndorsementProvisionForm.invalid) {
      this.editEndorsementProvisionForm.markAllAsTouched();
      this.alertService.error(this.lookupToast?.['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    console.log('[updateEndorsementProvision] Updating endorsement provision with data:', this.editEndorsementProvisionForm.value);
    console.log('[updateEndorsementProvision] Selected row data:', this.selectedEndorsementProvision);

    // Get the primary key (provision_id) from the selected row
    const provisionId = this.selectedEndorsementProvision?.provisionId || this.selectedEndorsementProvision?.id;
    if (!provisionId) {
      this.alertService.error('Missing provision ID. Cannot update record.');
      return;
    }

    const raw = this.editEndorsementProvisionForm.value;
    const userFromSession = JSON.parse(this.sessionStorage.getItem('user'));
    // Build payload with primary key for unique identification
    const payload = {
      person_id: this.personId ? Number(this.personId) : null,
      endorsement_additional_text: raw.additionalText || '',
      endorsement_due_date: this.formatDateLocal(raw.dueDate),
      license_resolved_date: null,
      endorsement_resolved_date: null,
      cd_status: this.selectedEndorsementProvision?.status || null,
      cd_extension_status: null,
      created_by: userFromSession.id,
      person_license_provision_id: provisionId,
      cd_endoresement: Array.isArray(raw.endorsement)
        ? raw.endorsement
          .filter((id: any) => id != null && id !== '')
          .map((id: any) => Number(id))
        : [],
      cd_endor_provision: raw.provision
      // license_additional_text: null,
      // license_due_date: null,
      // cd_license_provision: null,
      // cd_license_kind: null,
    };

    this.manageProfileService.updateLicenseProvisions(payload).subscribe({
      next: (data) => {
        this.getLicenseProvisionByPersonId();
        this.alertService.success(this.lookupToast?.['T0020'] || 'Updated Successfully.', true);
        // this.alertService.success('Endorsement provision updated successfully.');
        this.closeEndorsementProvisionModal();

        // Refresh data from backend
        // this.requestDashboardRefresh.emit();
      },
      error: err => {
        console.error('[updateEndorsementProvision] Error:', err);
        this.alertService.error(err?.error?.message || err?.message || 'Failed to update endorsement provision.');
      }
    });
  }

  onDeleteEndorsementProvision(data: any): void {
    console.log('[LookupUserLicenseTab] Delete endorsement provision:', data);
    this.deleteItemData = data;
    this.deleteItemType = 'endorsement-provision';
    this.openDeleteConfirmModal();
  }

  openEditCombinedProvisionModal(row: any, index: number): void {
    this.selectedCombinedProvision = { ...row };

    if (row.category === 'License Kind') {
      const provisionKey = this.findKeyFromLabel(this.licenseProvTypedataCopy, row.provision);
      const licenseKindKeys = this.licenseKindOptions
        .filter((opt: any) => opt.label && row.source?.split(', ').includes(opt.label))
        .map((opt: any) => opt.value)
        .filter((v: any) => this.editSourceOptions.some((o: any) => o.value === v));
      this.editCombinedProvisionForm.patchValue({
        category: 'License Kind',
        provision: provisionKey,
        source: licenseKindKeys,
        dueDate: row.dueDate || '',
        additionalText: row.additionalText || ''
      });
      this.editCombinedProvisionForm.get('source')?.setValidators(Validators.required);
    } else {
      const provisionKey = this.findKeyFromLabel(this.licenseEndoProvTypedataCopy, row.provision);
      const endorsementKeys = this.endorsementTypeOptions
        .filter((opt: any) => opt.label && row.source?.split(', ').includes(opt.label))
        .map((opt: any) => opt.value)
        .filter((v: any) => this.editSourceOptions.some((o: any) => o.value === v));
      this.editCombinedProvisionForm.patchValue({
        category: 'Endorsement',
        provision: provisionKey,
        source: endorsementKeys,
        dueDate: row.dueDate || '',
        additionalText: row.additionalText || ''
      });
      this.editCombinedProvisionForm.get('source')?.setValidators(Validators.required);
    }
    this.editCombinedProvisionForm.get('source')?.updateValueAndValidity();
    this.editCombinedProvisionForm.markAsPristine();

    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'license-requirement-modal custom-license-req-modal'
    };
    this.combinedProvisionModal = this.modalService.open(this.editCombinedProvisionModal, options);
  }

  closeEditCombinedProvisionModal(): void {
    this.selectedCombinedProvision = null;
    this.editCombinedProvisionForm.reset();
    this.editCombinedProvisionForm.markAsPristine();
    this.editCombinedProvisionForm.markAsUntouched();
    if (this.combinedProvisionModal) {
      this.combinedProvisionModal.close();
      this.combinedProvisionModal = null;
    }
  }

  onEditCombinedProvisionReset(): void {
    if (!this.selectedCombinedProvision) return;
    const row = this.selectedCombinedProvision;
    if (row.category === 'License Kind') {
      const licenseKindKeys = this.licenseKindOptions
        .filter((opt: any) => row.licenseKind?.includes(opt.label))
        .map((opt: any) => opt.value);
      this.editCombinedProvisionForm.patchValue({
        provision: this.findKeyFromLabel(this.licenseProvTypedataCopy, row.provision),
        source: licenseKindKeys,
        dueDate: row.dueDate || '',
        additionalText: row.additionalText || ''
      });
    } else {
      const endorsementKeys = this.endorsementTypeOptions
        .filter((opt: any) => row.endorsement?.includes(opt.label))
        .map((opt: any) => opt.value);
      this.editCombinedProvisionForm.patchValue({
        provision: this.findKeyFromLabel(this.licenseEndoProvTypedataCopy, row.provision),
        source: endorsementKeys,
        dueDate: row.dueDate || '',
        additionalText: row.additionalText || ''
      });
    }
    this.editCombinedProvisionForm.markAsPristine();
  }

  onEditCombinedProvisionChange(event: any): void {
    const provisionValue = this.editCombinedProvisionForm.get('provision')?.value;
    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    if (autoDate) {
      this.editCombinedProvisionForm.get('dueDate')?.setValue(autoDate);
      this.editCombinedProvisionForm.get('dueDate')?.markAsDirty();
    }
  }

  updateCombinedProvision(): void {
    if (this.editCombinedProvisionForm.invalid) {
      this.editCombinedProvisionForm.markAllAsTouched();
      this.alertService.error(this.lookupToast?.['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    const category = this.selectedCombinedProvision?.category;
    const provisionId = this.selectedCombinedProvision?.person_license_provision_id || this.selectedCombinedProvision?.provisionId;
    if (!provisionId) {
      this.alertService.error('Missing provision ID. Cannot update record.');
      return;
    }

    const raw = this.editCombinedProvisionForm.getRawValue();
    const userFromSession = JSON.parse(this.sessionStorage.getItem('user'));

    const payload = category === 'License Kind'
      ? {
          person_id: this.personId ? Number(this.personId) : null,
          cd_license_provision: raw.provision,
          license_due_date: this.formatDateLocal(raw.dueDate),
          license_additional_text: raw.additionalText || '',
          cd_license_kind: Array.isArray(raw.source)
            ? raw.source.filter((id: any) => id != null && id !== '').map((id: any) => Number(id))
            : [],
          license_resolved_date: null,
          cd_status: this.selectedCombinedProvision?.status || null,
          cd_extension_status: null,
          created_by: userFromSession.id,
          person_license_provision_id: provisionId
        }
      : {
          person_id: this.personId ? Number(this.personId) : null,
          endorsement_additional_text: raw.additionalText || '',
          endorsement_due_date: this.formatDateLocal(raw.dueDate),
          license_resolved_date: null,
          endorsement_resolved_date: null,
          cd_status: this.selectedCombinedProvision?.status || null,
          cd_extension_status: null,
          created_by: userFromSession.id,
          person_license_provision_id: provisionId,
          cd_endoresement: Array.isArray(raw.source)
            ? raw.source.filter((id: any) => id != null && id !== '').map((id: any) => Number(id))
            : [],
          cd_endor_provision: raw.provision
        };

    this.manageProfileService.updateLicenseProvisions(payload).subscribe({
      next: (data) => {
        this.getLicenseProvisionByPersonId();
        this.alertService.success(this.lookupToast?.['T0020'] || 'Updated Successfully.', true);
        this.closeEditCombinedProvisionModal();
      },
      error: err => {
        this.alertService.error(err?.error?.message || err?.message || 'Failed to update provision.');
      }
    });
  }

  // Delete modal methods
  private openDeleteConfirmModal(): void {
    const options: NgbModalOptions = { backdrop: 'static', size: 'md', centered: true, windowClass: 'custom-delete-record-modal' };
    const modalRef = this.modalService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Delete', code: 'Y', btnOutline: false }
      ],
      heading: 'Delete Record',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to delete this record? This action cannot be undone.',
      modalContentClass: 'actdelcontent',
      hasTemplate: true,
      isDangerButton: true,
      panelClass: 'lookup-act-delete-modal'
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.confirmDelete();
      } else {
        this.closeDeleteModal();
      }
    });
  }

  closeDeleteModal(): void {
    this.deleteItemData = null;
    this.deleteItemType = null;
  }

  confirmDelete(): void {
    if (!this.deleteItemData || !this.deleteItemType) {
      return;
    }

    console.log('[confirmDelete] Type:', this.deleteItemType, 'Data:', this.deleteItemData);

    switch (this.deleteItemType) {
      case 'license-kind':
        // this.deleteLicenseKind();
        this.deleteLicenseKindNew();
        break;
      case 'license-provision':
        this.deleteLicenseProvision();
        break;
      case 'endorsement-provision':
        this.deleteEndorsementProvision();
        break;
      case 'license-requirement':
        this.deleteLicenseRequirement();
        break;
    }
  }

  private deleteLicenseKind(): void {
    const licenseKindId = this.deleteItemData?.licenseKindId || this.deleteItemData?.id;
    const applicationId = this.deleteItemData?.applicationId;

    if (!licenseKindId) {
      this.alertService.error('Missing license kind ID. Cannot delete record.');
      this.closeDeleteModal();
      return;
    }

    const payload = {
      license_id: licenseKindId,
      person_id: this.personId ? Number(this.personId) : null,
      application_id: applicationId
    };

    console.log('[deleteLicenseKind] Payload:', JSON.stringify(payload, null, 2));
    this.manageProfileService
      .deleteLicenseEndorsement(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          console.log('[deleteLicenseKind] Response:', res);
          this.alertService.success(this.lookupToast?.['T0040'] || 'Deleted Successfully.', true);
          this.closeDeleteModal();
          this.requestDashboardRefresh.emit();
        },
        error: err => {
          console.error('[deleteLicenseKind] Error:', err);
          this.alertService.error(err?.error?.message || err?.message || 'Failed to delete license kind.');
          this.closeDeleteModal();
        }
      });
  }

  private deleteLicenseProvision(): void {
    const provisionId = this.deleteItemData?.provisionId || this.deleteItemData?.person_license_provision_id || this.deleteItemData?.id;
    // console.log("this.deleteItemData: ",this.deleteItemData);
    console.log("provisionId: ", provisionId);
    if (!provisionId) {
      this.alertService.error('Missing provision ID. Cannot delete record.');
      this.closeDeleteModal();
      return;
    }

    const payload = {
      person_license_provision_id: provisionId,
      // person_id: this.personId ? Number(this.personId) : null,
      // application_id: applicationId
    };

    this.manageProfileService.deleteLicenseProvisions(payload).subscribe({
      next: (data) => {
        this.getLicenseProvisionByPersonId();
        this.getLicKindDeletedProvisions();
        console.log('[deleteLicenseProvisions] Response received:', data);
        this.alertService.success(this.lookupToast?.['T0040'] || 'Deleted Successfully.', true);
        // this.alertService.success('License provision deleted successfully.');
        // Close form and refresh
        // this.showAddEndorsementProvisionForm = false;
        // this.addProvisionForm.reset();
        // Reload educator dashboard data to show the new provision
        console.log('[deleteLicenseProvisions] Requesting dashboard refresh...');
        this.closeDeleteModal();
      },
      error: (err) => {
        console.error('[deleteLicenseProvisions] HTTP Error:', err);
        console.error('[deleteLicenseProvisions] Error details:', {
          status: err?.status,
          statusText: err?.statusText,
          error: err?.error,
          message: err?.message
        });
        this.alertService.error(err?.error?.message || err?.message || 'Failed to delete license provision.');
        this.closeDeleteModal();
      }
    });
  }

  private deleteEndorsementProvision(): void {
    console.log("EndorsementData", this.EndorsementData);
    const provisionId = this.deleteItemData?.provisionId || this.deleteItemData?.person_license_provision_id || this.deleteItemData?.id;
    // const provisionId = this.ProvisionData?.person_license_provision_id || this.deleteItemData?.id;
    console.log("provisionId", provisionId);
    if (!provisionId) {
      this.alertService.error('Missing provision ID. Cannot delete record.');
      this.closeDeleteModal();
      return;
    }

    const payload = {
      person_license_provision_id: provisionId,
      // person_id: this.personId ? Number(this.personId) : null,
      // application_id: applicationId
    };
    this.manageProfileService.deleteLicenseProvisions(payload).subscribe({
      next: (data) => {
        this.getLicenseProvisionByPersonId();
        this.getEndoDeletedProvisions();
        console.log('[deleteEndorsementProvision] Response received:', data);
        this.alertService.success(this.lookupToast?.['T0040'] || 'Deleted Successfully.', true);
        // this.alertService.success('Endorsement provision deleted successfully.');
        this.closeDeleteModal();
      },
      error: err => {
        console.error('[deleteEndorsementProvision] Error:', err);
        this.alertService.error(err?.error?.message || err?.message || 'Failed to delete endorsement provision.');
        this.closeDeleteModal();
      }
    });
  }

  // License Requirements actions
  onLicenseRequirementsDownload(): void {
    const grid = this.licenseRequirementsGridRef;
    if (!grid || typeof grid.exportFilteredDataToExcel !== 'function') {
      this.alertService.warn('Grid is not ready to export.');
      return;
    }
    const cols = this.licenseRequirementsExcelColumns.filter(
      c => this.licenseRequirementsColumnVisibility[c.key] !== false
    );
    grid.exportFilteredDataToExcel(cols, 'LicenseRequirements.xlsx');
  }

  toggleLicenseRequirementsColumnChooser(): void {
    this.showLicenseRequirementsColumnChooser = !this.showLicenseRequirementsColumnChooser;
  }

  closeLicenseRequirementsColumnChooser(): void {
    this.showLicenseRequirementsColumnChooser = false;
  }

  onLicenseRequirementsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.licenseRequirementsColumnVisibility[ev.key] !== false;
    this.licenseRequirementsColumnVisibility = {
      ...this.licenseRequirementsColumnVisibility,
      [ev.key]: !cur
    };
    const prev = this.licenseRequirementsGridConfig;
    this.licenseRequirementsGridConfig = prev.updateConfig({
      visibleColumns: { ...this.licenseRequirementsColumnVisibility },
      _changeKey: Date.now()
    });
  }

  onAddLicenseRequirement(): void {
    this.showAddLicenseRequirementForm = true;
    this.addRequirementForm.reset();
    // Reset visibility flags - both fields hidden until Type is selected
    this.showLicenseKindField = false;
    this.showEndorsementField = false;
    this.autoCalculatedRequirementDueDate = '';  // Clear auto-calculated date
  }

  onCancelAddLicenseRequirement(): void {
    this.showAddLicenseRequirementForm = false;
    this.addRequirementForm.reset();
    // Reset visibility flags
    this.showLicenseKindField = false;
    this.showEndorsementField = false;
    this.autoCalculatedRequirementDueDate = '';  // Clear auto-calculated date
    this.requirementOptions = [...this.allRequirementOptions];
  }

  onSaveAddLicenseRequirement(): void {
    if (this.addRequirementForm.invalid) {
      this.addRequirementForm.markAllAsTouched();
      // this.alertService.error('Please fill in all required fields.');
      this.alertService.error(this.lookupToast?.['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    // Get application_id from license details
    const licenseInfo = this.educatorDashboardRow?.p_license_dtls || [];
    const applicationId = Array.isArray(licenseInfo) && licenseInfo.length > 0 ? licenseInfo[0]?.application_id : null;

    // if (!applicationId) {
    //   this.alertService.warn('No application information found. Cannot add license requirement.');
    //   return;
    // }
    if (!this.personId) {
      this.alertService.warn('No person information found. Cannot add license requirement.');
      return;
    }

    const raw = this.addRequirementForm.value;
    const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const selectedRequirement = this.requirementOptions.find(
      (item: any) => item.value == raw.requirement
    );
    const payload = {
      person_id: this.personId ? Number(this.personId) : null,
      cd_requirement: raw.requirement || '',
      due_date: raw.dueDate,
      cd_status: 'Pending',
      created_by: sessionUser?.id ? Number(sessionUser.id) : null,
      le_type: raw.type === 'LICENSE' ? 'License Kind' : raw.type || '',
      le_source: raw.type === 'ENDORSEMENT' ? raw.endorsement : raw.licenseKind || [],
      resolved_date: '',
      additional_text: raw.additionalText || ''

    };

    this.manageProfileService
      .saveLicenseRequirement(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const success = res?.data?.success === true;
          if (success) {
            this.alertService.success(this.lookupToast?.['T0005'] || 'Added Successfully.', true);

            // Auto-dismiss alert after 5 seconds
            setTimeout(() => {
              // Alert will be dismissed automatically by the alert service
            }, 5000);

            this.showAddLicenseRequirementForm = false;
            this.addRequirementForm.reset();

            // Reset visibility flags
            this.showLicenseKindField = false;
            this.showEndorsementField = false;
            this.autoCalculatedRequirementDueDate = '';  // Clear auto-calculated date

            // Request parent to refresh educator dashboard data from API
            this.fetchLicenseRequirementsByPersonId();
            this.requestDashboardRefresh.emit();
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Failed to add license requirement.');
          }
        },
        error: err => {
          this.alertService.error(err?.error?.message || err?.message || 'Failed to add license requirement.');
        }
      });
  }

  onEditLicenseRequirement(data: any): void {
    console.log('[LookupUserLicenseTab] Edit license requirement:', data);
    const index = this.licenseRequirementsGridConfig.data.findIndex((item: any) => item.id === data.id);
    this.openEditLicenseRequirementModal(data, index);
  }

  openEditLicenseRequirementModal(row: any, index: number): void {
    this.selectedLicenseRequirement = { ...row };
    this.editLicenseRequirementIndex = index;
    let rawLeType = (row.leType || '').toUpperCase();
    // Normalize 'License Kind' / 'LICENSE KIND' to 'LICENSE'
    if (rawLeType === 'LICENSE KIND') rawLeType = 'LICENSE';
    const typeKey: string = rawLeType
      || this.findKeyFromLabel(this.requirementTypeOptions, row.type);

    const requirementKey = row.cdRequirement
      || this.allRequirementOptions.find((opt: any) => opt.label === row.requirement)?.value
      || this.findKeyFromLabel(this.allRequirementOptions, row.requirement);

    this.showLicenseKindField = typeKey === 'LICENSE';
    this.showEndorsementField = typeKey === 'ENDORSEMENT';

    const rawSource: any[] = row.leSource || [];

    let licenseKindArray: any[] = [];
    let endorsementArray: any[] = [];

    // if (typeKey === 'LICENSE') {
    //   licenseKindArray = rawSource
    //     .map((id: any) => {
    //       const strId = String(id);
    //       const match = this.licenseKindOptions.find(
    //         (opt: any) => String(opt.value) === strId
    //       );
    //       return match ? match.value : strId;
    //     })
    //     .filter(Boolean);
    // } else if (typeKey === 'ENDORSEMENT') {
    //   endorsementArray = rawSource
    //     .map((id: any) => {
    //       const strId = String(id);
    //       const match = this.endorsementTypeOptions.find(
    //         (opt: any) => String(opt.value) === strId
    //       );
    //       return match ? match.value : strId;
    //     })
    //     .filter(Boolean);
    // }

    if (typeKey === 'LICENSE') {
      licenseKindArray = rawSource.map((id: any) => {
        const strId = String(id);
        // Try exact string match first
        const exactMatch = this.licenseKindOptions.find(
          (opt: any) => String(opt.value) === strId
        );
        if (exactMatch) return exactMatch.value;
        // Try numeric match
        const numMatch = this.licenseKindOptions.find(
          (opt: any) => Number(opt.value) === Number(id)
        );
        if (numMatch) return numMatch.value;
        // No match found — return null so it gets filtered out
        return null;
      }).filter((v: any) => v !== null && v !== undefined && v !== '');

      console.log('[openEditLicenseRequirementModal] licenseKindOptions:', this.licenseKindOptions);
      console.log('[openEditLicenseRequirementModal] rawSource:', rawSource);
      console.log('[openEditLicenseRequirementModal] resolved licenseKindArray:', licenseKindArray);
    } else if (typeKey === 'ENDORSEMENT') {
      endorsementArray = rawSource.map((id: any) => {
        const strId = String(id);
        const exactMatch = this.endorsementTypeOptions.find(
          (opt: any) => String(opt.value) === strId
        );
        if (exactMatch) return exactMatch.value;
        const numMatch = this.endorsementTypeOptions.find(
          (opt: any) => Number(opt.value) === Number(id)
        );
        if (numMatch) return numMatch.value;
        // No match found — return null so it gets filtered out
        return null;
      }).filter((v: any) => v !== null && v !== undefined && v !== '');

      console.log('[openEditLicenseRequirementModal] endorsementTypeOptions:', this.endorsementTypeOptions);
      console.log('[openEditLicenseRequirementModal] rawSource:', rawSource);
      console.log('[openEditLicenseRequirementModal] resolved endorsementArray:', endorsementArray);
    }

    this.editLicenseRequirementForm.patchValue({
      requirement: requirementKey,
      type: typeKey,
      dueDate: row.dueDate || '',
      licenseKind: licenseKindArray,
      endorsement: endorsementArray
    });

    this.onEditRequirementTypeChange();

    // Re-patch source values after onEditRequirementTypeChange (which clears them)
    if (typeKey === 'LICENSE') {
      this.editLicenseRequirementForm.get('licenseKind')?.setValue(licenseKindArray);
    } else if (typeKey === 'ENDORSEMENT') {
      this.editLicenseRequirementForm.get('endorsement')?.setValue(endorsementArray);
    }

    // Set autoCalculatedRequirementDueDate so due date warning can trigger on edit
    if (typeKey === 'LICENSE') {
      this.autoCalculatedRequirementDueDate = this.getMinimumExpirationDate(licenseKindArray, 'licenseKind');
    } else if (typeKey === 'ENDORSEMENT') {
      this.autoCalculatedRequirementDueDate = this.getMinimumExpirationDate(endorsementArray, 'endorsement');
    } else {
      this.autoCalculatedRequirementDueDate = '';
    }

    this.editLicenseRequirementForm.markAsPristine();
    this.showEditLicenseRequirementModal = true;

    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'license-requirement-modal custom-license-req-modal'
    };
    this.licenseRequirementModal = this.modalService.open(this.editLicenseRequirementModal, options);
  }

  closeLicenseRequirementModal(): void {
    this.selectedLicenseRequirement = null;
    this.editLicenseRequirementForm.reset();
    this.editLicenseRequirementForm.markAsPristine();
    this.editLicenseRequirementForm.markAsUntouched();
    this.showEditLicenseRequirementModal = false;
    if (this.licenseRequirementModal) {
      this.licenseRequirementModal.close();
      this.licenseRequirementModal = null;
    }
  }

  onEditLicenseRequirementReset(): void {
    if (this.selectedLicenseRequirement) {
      this.editLicenseRequirementForm.patchValue({
        requirement: this.findKeyFromLabel(this.requirementOptions, this.selectedLicenseRequirement.requirement),
        type: this.findKeyFromLabel(this.requirementTypeOptions, this.selectedLicenseRequirement.type),
        licenseKind: this.selectedLicenseRequirement.licenseKind || [],
        endorsement: this.selectedLicenseRequirement.endorsement || [],
        dueDate: this.selectedLicenseRequirement.dueDate || ''
      });
      this.editLicenseRequirementForm.markAsPristine();
      // Re-trigger visibility logic
      this.onEditRequirementTypeChange();
    }
  }

  onEditRequirementTypeChange(): void {
    const typeValue = this.editLicenseRequirementForm.get('type')?.value;
    console.log('[onEditRequirementTypeChange] Type changed to:', typeValue);

    // Show/hide fields based on type
    this.showLicenseKindField = (typeValue === 'LICENSE');
    this.showEndorsementField = (typeValue === 'ENDORSEMENT');

    // Set/clear validators based on visibility
    const licenseKindControl = this.editLicenseRequirementForm.get('licenseKind');
    const endorsementControl = this.editLicenseRequirementForm.get('endorsement');

    if (this.showLicenseKindField) {
      licenseKindControl?.setValidators([Validators.required]);
      endorsementControl?.clearValidators();
      endorsementControl?.setValue([]);
    } else if (this.showEndorsementField) {
      endorsementControl?.setValidators([Validators.required]);
      licenseKindControl?.clearValidators();
      licenseKindControl?.setValue([]);
    } else {
      licenseKindControl?.clearValidators();
      endorsementControl?.clearValidators();
      licenseKindControl?.setValue([]);
      endorsementControl?.setValue([]);
    }

    licenseKindControl?.updateValueAndValidity();
    endorsementControl?.updateValueAndValidity();

    console.log('[onEditRequirementTypeChange] showLicenseKindField:', this.showLicenseKindField);
    console.log('[onEditRequirementTypeChange] showEndorsementField:', this.showEndorsementField);
  }

  updateLicenseRequirement(): void {
    if (this.editLicenseRequirementForm.invalid) {
      this.editLicenseRequirementForm.markAllAsTouched();
      this.alertService.error(this.lookupToast?.['T0017'] || 'Missing required information. Complete all fields and documents to continue.');
      return;
    }

    const raw = this.editLicenseRequirementForm.value;

    const requirementId =
      this.selectedLicenseRequirement?.requirementId ||
      this.selectedLicenseRequirement?.id;

    if (!requirementId) {
      this.alertService.error('Missing requirement ID. Cannot update record.');
      return;
    }

    const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');

    const selectedRequirement = this.requirementOptions.find(
      (x: any) => x.value == raw.requirement
    );
    let leSource: any[] = [];
    if (raw.type === 'LICENSE') {
      leSource = (raw.licenseKind || []).map((item: any) => {
        const match = this.licenseKindOptions.find(
          (x: any) => x.value == item || x.label == item
        );
        return match ? match.value : item;
      });
    } else if (raw.type === 'ENDORSEMENT') {
      leSource = (raw.endorsement || []).map((item: any) => {
        const match = this.endorsementTypeOptions.find(
          (x: any) => x.value == item || x.label == item
        );
        return match ? match.value : item;
      });
    }

    console.log('[updateLicenseRequirement] le_source (labels):', leSource);


    const payload: any = {
      person_license_requirement_id: requirementId,
      person_id: this.personId ? Number(this.personId) : null,
      cd_requirement: selectedRequirement?.value || '',
      due_date: new Date(raw.dueDate).toISOString(),
      cd_status: 'Pending',
      created_by: sessionUser?.id || null,
      le_type: raw.type === 'LICENSE' ? 'License Kind' : raw.type || '',
      le_source: leSource,
      resolved_date: '',
      additional_text: this.selectedLicenseRequirement?.additionalText || ''
    };

    this.manageProfileService
      .updateLicenseRequirement(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.alertService.success(this.lookupToast?.['T0020'] || 'Updated Successfully.', true);
          this.closeLicenseRequirementModal();
          this.fetchLicenseRequirementsByPersonId();
          this.requestDashboardRefresh.emit();
        },
        error: err => {
          this.alertService.error(
            err?.error?.message || err?.message || 'Failed to update license requirement.'
          );
        }
      });
  }

  onDeleteLicenseRequirement(data: any): void {
    console.log('[LookupUserLicenseTab] Delete license requirement:', data);
    this.deleteItemData = data;
    this.deleteItemType = 'license-requirement';
    this.openDeleteConfirmModal();
  }

  private deleteLicenseRequirement(): void {
    const requirementId = this.deleteItemData?.requirementId || this.deleteItemData?.id;
    const applicationId = this.deleteItemData?.applicationId;

    if (!requirementId) {
      this.alertService.error('Missing requirement ID. Cannot delete record.');
      this.closeDeleteModal();
      return;
    }

    const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const updatedBy = sessionUser?.id ? Number(sessionUser.id) : null;

    const payload = {
      // application_license_requirement_id: requirementId,
      // person_id: this.personId ? Number(this.personId) : null,
      // application_id: applicationId
      person_license_requirement_id: requirementId,
      updated_by: updatedBy

    };

    console.log('[deleteLicenseRequirement] Payload:', JSON.stringify(payload, null, 2));
    this.manageProfileService
      .deleteLicenseRequirement(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          console.log('[deleteLicenseRequirement] Response:', res);
          this.alertService.success(this.lookupToast?.['T0040'] || 'Deleted Successfully.', true);
          this.closeDeleteModal();
          this.fetchLicenseRequirementsByPersonId();
          this.requestDashboardRefresh.emit();
        },
        error: err => {
          console.error('[deleteLicenseRequirement] Error:', err);
          this.alertService.error(err?.error?.message || err?.message || 'Failed to delete license requirement.');
          this.closeDeleteModal();
        }
      });
  }

  private async loadProvisionTypesWithFallback(): Promise<void> {
    // Try different parameter values until one works
    const parametersToTry = ["1", 1, "", "all", 414, 415];

    for (const param of parametersToTry) {
      try {
        console.log(`Trying provision API with parameter: ${param}`);
        await this.provtypeOnChange(param);

        // Check if we got any data
        if (this.licenseProvTypedata.length > 0 || this.licenseEndoProvTypedata.length > 0) {
          console.log(`Success with parameter: ${param}`);
          return;
        } else {
          console.log(`No data returned with parameter: ${param}`);
        }
      } catch (error) {
        console.log(`Failed with parameter: ${param}`, error);
        continue;
      }
    }

    // If all parameters fail, throw error
    throw new Error('Unable to load provision data with any parameter');
  }

  // ── Due Date auto-calculation and validation ──────────────────────────────

  private strictUsDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') return null;
    const d = new Date(value);
    // if (Number.isNaN(d.getTime())) return { invalidDate: true };
    // const year = d.getFullYear();
    // if (year < 1900 || year > 2100) return { invalidYear: true };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return { pastDateNotAllowed: true };
    return null;
  }

  private calculateProvisionBasedDueDate(provisionValue: any): string | null {
    const v = String(provisionValue ?? '').trim();
    console.log('[calculateProvisionBasedDueDate] Input value:', provisionValue, '=> normalized:', v);

    if (!v) return null;
    const now = new Date();
    if (v === '2') {
      //now.setDate(now.getDate() + 120);
      console.log('[calculateProvisionBasedDueDate] Provision 2 matched => today + 120 days');
      return moment().add(120, 'd').format('MM/DD/YYYY'); // YYYY-MM-DD for <input type="date">
    }
    if (['3', '4', '5'].includes(v)) {
      //now.setFullYear(now.getFullYear() + 3);
      console.log('[calculateProvisionBasedDueDate] Provision 3/4/5 matched => today + 3 years');
      return moment().add(3, 'y').format('MM/DD/YYYY');
    }
    console.log('[calculateProvisionBasedDueDate] No match found for value:', v);
    return null;
  }

  // Called when Provision dropdown changes in Add License/Endorsement Provision forms
  onAddProvisionChange(): void {
    const provisionValue = this.addProvisionForm.controls['provision'].value;
    console.log('[onAddProvisionChange] Provision selected:', provisionValue);
    console.log('[onAddProvisionChange] showAddLicenseProvisionForm:', this.showAddLicenseProvisionForm);
    console.log('[onAddProvisionChange] showAddEndorsementProvisionForm:', this.showAddEndorsementProvisionForm);

    if (this.showAddLicenseProvisionForm) {
      console.log('[onAddProvisionChange] License Provision data available:', this.licenseProvTypedata?.length, 'items');
    } else if (this.showAddEndorsementProvisionForm) {
      console.log('[onAddProvisionChange] Endorsement Provision data available:', this.licenseEndoProvTypedata?.length, 'items');
    }

    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    this.expirationDate = autoDate;
    console.log('[onAddProvisionChange] Auto-calculated date:', autoDate);

    this.addProvisionForm.controls['dueDate'].setValue(autoDate ? moment(autoDate).format('MM/DD/YYYY') : null);
    console.log('[onAddProvisionChange] Due Date field set to:', this.addProvisionForm.controls['dueDate'].value);
  }

  onAddProvisionEndorsementChange(): void {
    const provisionValue = this.addEndorsementProvisionForm.controls['provision'].value;
    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    this.addEndorsementProvisionForm.controls['dueDate'].setValue(autoDate ? new Date(autoDate) : null);
  }

  // Called when Due Date is manually changed in Add License/Endorsement Provision forms
  onAddProvisionDueDateChange(event: any): void {
    const selectedDate = this.addProvisionForm.get('dueDate')?.value;
    const provisionValue = this.addProvisionForm.controls['provision'].value;
    const systemDate = this.calculateProvisionBasedDueDate(provisionValue);

    // Skip warning modal for Endorsement Provisions (matches decision-role-page behavior)
    if (this.showAddEndorsementProvisionForm) {
      console.log('[onAddProvisionDueDateChange] Skipping warning for Endorsement Provision');
      return;
    }

    // Format the selected date to yyyy-MM-dd for comparison
    const formattedDate = selectedDate instanceof Date
      ? selectedDate.toISOString().split('T')[0]
      : selectedDate;

    if (formattedDate && systemDate && formattedDate !== systemDate) {
      console.log('[onAddProvisionDueDateChange] Date mismatch - showing warning modal');
      this.showDueDateWarningModal = true;
    }
  }
  formatDateToLocal(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Called when Provision dropdown changes in Edit License Provision modal
  onEditLicenseProvisionChange(): void {
    const provisionValue = this.editLicenseProvisionForm.controls['provision'].value;
    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    if (autoDate) {
      // calculateProvisionBasedDueDate already returns MM/DD/YYYY via moment
      this.editLicenseProvisionForm.controls['dueDate'].setValue(autoDate);
      this.editLicenseProvisionForm.controls['dueDate'].markAsDirty();
    }
  }

  // Called when Due Date is manually changed in Edit License Provision modal
  onEditLicenseProvisionDueDateChange(event: any): void {
    const selectedDate = this.editLicenseProvisionForm.get('dueDate')?.value;
    const provisionValue = this.editLicenseProvisionForm.controls['provision'].value;
    const systemDate = this.calculateProvisionBasedDueDate(provisionValue);

    // Format the selected date to yyyy-MM-dd for comparison
    const formattedDate = selectedDate instanceof Date
      ? selectedDate.toISOString().split('T')[0]
      : selectedDate;

    if (formattedDate && systemDate && formattedDate !== systemDate) {
      this.showDueDateWarningModal = true;
    }
  }

  // Called when Provision dropdown changes in Edit Endorsement Provision modal
  onEditEndorsementProvisionChange(): void {
    const provisionValue = this.editEndorsementProvisionForm.controls['provision'].value;
    console.log('[onEditEndorsementProvisionChange] Provision selected:', provisionValue);

    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    console.log('[onEditEndorsementProvisionChange] Auto-calculated date:', autoDate);

    if (autoDate) {
      this.editEndorsementProvisionForm.controls['dueDate'].setValue(autoDate);
      this.editEndorsementProvisionForm.controls['dueDate'].markAsDirty();
    } else {
      this.editEndorsementProvisionForm.controls['dueDate'].setValue(null);
    }
  }



  closeDueDateWarningModal(): void {
    this.showDueDateWarningModal = false;
  }

  // ── License Requirement due date auto-calculation ────────────────────────────

  // Called when Type field changes in Add Requirement form
  onRequirementTypeChange(): void {
    const typeValue = this.addRequirementForm.get('type')?.value;
    console.log('[onRequirementTypeChange] Type changed to:', typeValue);

      const typeLabel = this.getRequirementTypeLabel(typeValue);
  const usedLabels = new Set(
    (this.licenseRequirementsGridConfig?.data || [])
      .filter((row: any) => row.type === typeLabel)
      .map((row: any) => row.requirement)
  );
  this.requirementOptions = this.allRequirementOptions.filter((opt: any) => !usedLabels.has(opt.label));

    // Reset field values when type changes
    this.addRequirementForm.controls['licenseKind'].setValue([]);
    this.addRequirementForm.controls['endorsement'].setValue([]);
    this.addRequirementForm.controls['dueDate'].setValue('');

    // Show/hide fields based on type selection
    if (typeValue === 'LICENSE') {
      this.showLicenseKindField = true;
      this.showEndorsementField = false;
      // Update validators: License Kind required, Endorsement not required
      this.addRequirementForm.controls['licenseKind'].setValidators([Validators.required]);
      this.addRequirementForm.controls['endorsement'].clearValidators();
    } else if (typeValue === 'ENDORSEMENT') {
      this.showLicenseKindField = false;
      this.showEndorsementField = true;
      // Update validators: Endorsement required, License Kind not required
      this.addRequirementForm.controls['endorsement'].setValidators([Validators.required]);
      this.addRequirementForm.controls['licenseKind'].clearValidators();
    } else {
      // No type selected - hide both
      this.showLicenseKindField = false;
      this.showEndorsementField = false;
      this.addRequirementForm.controls['licenseKind'].clearValidators();
      this.addRequirementForm.controls['endorsement'].clearValidators();
    }

    // Update validation state
    this.addRequirementForm.controls['licenseKind'].updateValueAndValidity();
    this.addRequirementForm.controls['endorsement'].updateValueAndValidity();

    console.log('[onRequirementTypeChange] License Kind visible:', this.showLicenseKindField);
    console.log('[onRequirementTypeChange] Endorsement visible:', this.showEndorsementField);
  }

  // Called when License Kind multiselect changes in Add Requirement form
  onRequirementLicenseKindChange(event: any): void {
    // Get selected values from the form control directly (more reliable with reactive forms)
    const selectedIds = this.addRequirementForm.get('licenseKind')?.value || [];
    console.log('[onRequirementLicenseKindChange] Event:', event);
    console.log('[onRequirementLicenseKindChange] Selected License Kind IDs from form control:', selectedIds);
    console.log('[onRequirementLicenseKindChange] Current grid data length:', this.currentLicenseKindsGridConfig?.data?.length || 0);

    const dueDate = this.getMinimumExpirationDate(selectedIds, 'licenseKind');
    console.log('[onRequirementLicenseKindChange] Calculated due date:', dueDate);

    if (dueDate) {
      this.autoCalculatedRequirementDueDate = dueDate;  // Store for comparison
      this.addRequirementForm.controls['dueDate'].setValue(dueDate ? moment(dueDate).format('MM/DD/YYYY') : null);
      console.log('[onRequirementLicenseKindChange] Due date field value set to:', dueDate);
    } else {
      this.autoCalculatedRequirementDueDate = '';  // Clear if no date calculated
      this.addRequirementForm.controls['dueDate'].setValue(null);
      console.warn('[onRequirementLicenseKindChange] No due date calculated - no matching licenses found or no expiration dates available');
    }
  }

  // Called when Endorsement multiselect changes in Add Requirement form
  onRequirementEndorsementChange(event: any): void {
    // Get selected values from the form control directly (more reliable with reactive forms)
    const selectedIds = this.addRequirementForm.get('endorsement')?.value || [];
    console.log('[onRequirementEndorsementChange] Event:', event);
    console.log('[onRequirementEndorsementChange] Selected Endorsement IDs from form control:', selectedIds);
    console.log('[onRequirementEndorsementChange] Current grid data length:', this.currentLicenseKindsGridConfig?.data?.length || 0);

    const dueDate = this.getMinimumExpirationDate(selectedIds, 'endorsement');
    console.log('[onRequirementEndorsementChange] Calculated due date:', dueDate);

    if (dueDate) {
      this.autoCalculatedRequirementDueDate = dueDate;  // Store for comparison
      this.addRequirementForm.controls['dueDate'].setValue(dueDate ? moment(dueDate).format('MM/DD/YYYY') : null);
      console.log('[onRequirementEndorsementChange] Due date field value set to:', dueDate);
    } else {
      this.autoCalculatedRequirementDueDate = '';  // Clear if no date calculated
      this.addRequirementForm.controls['dueDate'].setValue(null);
      console.warn('[onRequirementEndorsementChange] No due date calculated - no matching endorsements found or no expiration dates available');
    }
  }

  // Called when Due Date is manually changed in Add License Requirement form
  onRequirementDueDateChange(event: any): void {
    const selectedDate = this.addRequirementForm.get('dueDate')?.value;

    // Format the selected date to yyyy-MM-dd for comparison
    const formattedDate = selectedDate instanceof Date
      ? selectedDate.toISOString().split('T')[0]
      : selectedDate;

    console.log('[onRequirementDueDateChange] Manual date selected:', formattedDate);
    console.log('[onRequirementDueDateChange] Auto-calculated date:', this.autoCalculatedRequirementDueDate);

    // Show warning if user manually enters a date that differs from the auto-calculated date
    if (formattedDate && this.autoCalculatedRequirementDueDate && formattedDate !== this.autoCalculatedRequirementDueDate) {
      console.log('[onRequirementDueDateChange] Date mismatch - showing warning modal');
      this.showDueDateWarningModal = true;
    }
  }

  private getMinimumExpirationDate(selectedIds: any[], type: 'licenseKind' | 'endorsement'): string {
    if (!selectedIds || selectedIds.length === 0) return '';

    const normalizedIds = selectedIds.map(String);
    console.log('[getMinimumExpirationDate] Normalized IDs (lookup keys):', normalizedIds, 'Type:', type);

    // Convert lookup keys to their display labels for matching
    let labelsToMatch: string[] = [];
    if (type === 'licenseKind') {
      console.log('[getMinimumExpirationDate] licenseKindOptions available:', this.licenseKindOptions.length, this.licenseKindOptions);
      labelsToMatch = normalizedIds.map(key => {
        const option = this.licenseKindOptions.find(opt => opt.value === key);
        console.log('[getMinimumExpirationDate] Looking for key:', key, 'Found option:', option);
        return option ? option.label : key;
      });
      console.log('[getMinimumExpirationDate] License Kind labels to match:', labelsToMatch);
    } else {
      console.log('[getMinimumExpirationDate] endorsementTypeOptions available:', this.endorsementTypeOptions.length, this.endorsementTypeOptions);
      labelsToMatch = normalizedIds.map(key => {
        const option = this.endorsementTypeOptions.find(opt => opt.value === key);
        console.log('[getMinimumExpirationDate] Looking for key:', key, 'Found option:', option);
        return option ? option.label : key;
      });
      console.log('[getMinimumExpirationDate] Endorsement labels to match:', labelsToMatch);
    }

    // Get current license kinds data from the grid
    const licenseKinds = this.getLicenseKindEndorsementsDataGrid() || [];
    console.log('[getMinimumExpirationDate] Available license kinds:', licenseKinds.length);

    // Filter matching licenses and get their expiration dates
    const matchingLicenses = licenseKinds.filter((license: any) => {
      if (type === 'licenseKind') {
        // Match by licenseKind display text
        const licenseKindValue = String(license.licenseKind || '').trim();
        const isMatch = labelsToMatch.some(label =>
          String(label).trim().toLowerCase() === licenseKindValue.toLowerCase()
        );
        console.log('[getMinimumExpirationDate] Checking license:', licenseKindValue, 'Match:', isMatch);
        return isMatch;
      } else {
        // Match by endorsement (endorsementGrade field) display text
        const endorsementValue = String(license.endorsementGrade || '').trim();
        const isMatch = labelsToMatch.some(label =>
          String(label).trim().toLowerCase() === endorsementValue.toLowerCase()
        );
        console.log('[getMinimumExpirationDate] Checking endorsement:', endorsementValue, 'Match:', isMatch);
        return isMatch;
      }
    });

    console.log('[getMinimumExpirationDate] Matching licenses found:', matchingLicenses.length);

    // Extract valid expiration dates (grid may show MM/DD/YYYY)
    const expirationDates = matchingLicenses
      .map((license: any) => this.parseLicenseDateValue(license.expirationDate))
      .filter((d): d is Date => d != null);

    console.log('[getMinimumExpirationDate] Valid expiration dates:', expirationDates.length);

    if (expirationDates.length === 0) return '';

    // Find the earliest (minimum) expiration date
    const earliestDate = new Date(Math.min(...expirationDates.map(d => d.getTime())));
    return this.toLocalIsoDate(earliestDate);
    console.log('[getMinimumExpirationDate] Earliest expiration date:', earliestDate);

    // Return in YYYY-MM-DD format for <input type="date">
    return earliestDate.toISOString().slice(0, 10);
  }

  // ── Extend License ───────────────────────────────────────────────

  private loadExtensionDuration(): void {
    this.manageProfileService.getBusinessRuleByKey('Licensing', 'license_extension_duration')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.extensionDuration = response.data.data.intValue;
            // Rebuild grids so extensionStatus is recalculated with the loaded duration
            if (this.educatorDashboardRow) {
              this.buildLicenseGrids();
            }
          }
        },
        error: (error) => {
          console.error('[LookupUserLicenseTab] Error fetching extension duration rule:', error);
        }
      });
  }

  private getLicenseData(): any {
    const licenseDetails = this.getLicenseKindEndorsementsData || [];
    if (!Array.isArray(licenseDetails) || licenseDetails.length === 0) return null;
    return licenseDetails.find((row: any) => row?.is_extended === true) || licenseDetails[0];
  }

  get shouldShowExtendButton(): boolean {
    const licenseData = this.getLicenseData();
    if (!licenseData) return false;
    if (this.isLicenseExpired(licenseData.expiration_date)) return false;
    if (this.getExtensionEligibilityStatus(licenseData.expiration_date) !== 'Eligible') return false
    return true;
  }

  get isExtendButtonEnabled(): boolean {
    const licenseData = this.getLicenseData();
    if (!licenseData) return false;
    return this.getExtensionEligibilityStatus(licenseData.expiration_date) === 'Eligible';
  }

  get extendButtonTooltip(): string {
    return this.isExtendButtonEnabled
      ? (this.lookupGeneric?.['G0051'] || '')
      : (this.lookupGeneric?.['G0062'] || '');
  }

  onExtendLicense(): void {
    const licenseData = this.getLicenseData();
    if (!licenseData) {
      this.alertService.warn('No license information available.');
      return;
    }
    const newExpirationDate = licenseData.intLicenseExtensionDuration || this.extensionDuration || '';
    const parsedFee = Number(this.licenseFee);
    console.log("FEES:", parsedFee, this.licenseFee);
    const licenseFee = Number.isFinite(parsedFee) ? parsedFee : 0;
    this.showExtendLicenseModal(newExpirationDate, licenseFee);
  }

  private showExtendLicenseModal(newExpirationDate: string, licenseFee: number): void {
    const options: any = { backdrop: 'static', centered: true, size: 'md', windowClass: 'custom-license-extend-modal' };
    this.extendLicenseModal = this.modalService.open(ModalComponent, options);

    const userData = this.commonService.getCurrentUserData();
    const userFullName = `${userData?.firstname || ''} ${userData?.lastname || ''}`.trim();

    const currentLicenseKindData = this.currentLicenseKindsGridConfig.data || [];
    const LicenseProvisionsData = this.licenseProvisionsGridConfig.data || [];
    const endorsementProvisionsData = this.endorsementProvisionsGridConfig.data || [];
    const licenseRequirementsData = this.licenseRequirementsGridConfig.data || [];
    const licenseKindsLabel = currentLicenseKindData.every((row: any) =>row?.extensionStatus === 'Not Eligible') ? 'License Kinds' : '';
    const endorsementsLabel = endorsementProvisionsData.some((row: any) =>row?.extensionStatus === 'Not Eligible') ? 'Endorsements' : '';
    const provisionsLabel = LicenseProvisionsData.some((row: any) =>row?.extensionStatus === 'Not Eligible') ? 'Provisions' : '';
    const licenseRequirementsLabel = licenseRequirementsData.some((row: any) =>row?.extensionStatus === 'Not Eligible') ? 'License Requirements' : '';
    const modalConfig: ModalConfig = {
      buttons: [
        { text: modalButtonTitles.cancel, code: 'N', btnOutline: true },
        { text: modalButtonTitles.extend, code: 'Y', btnOutline: false }
      ],
      heading: modalHeading.extendLicense,
      modalContent: modalContent.educatorLicenseContent(
        newExpirationDate,
        licenseFee,
        licenseKindsLabel,
        endorsementsLabel,
        provisionsLabel,
        licenseRequirementsLabel
      ),
      hasTemplate: true,
      scrollable: false,
      modalContentClass: 'info-box',
      modalInputArray: [
        {
          label: 'Signed by Staff on behalf of Educator <span class="text-danger">*</span>',
          fieldName: 'signedByStaff',
          value: false,
          type: 'checkbox',
        },
        {
          label: 'Signature ',
          fieldName: 'strFullName',
          value: '',
          type: 'text',
          tooltip: undefined,
          // placeholder: 'Enter First and Last Name',
          // tooltip: 'First and last name only. Do not include middle name or suffix.',
          // content: 'Please sign using the approved name on record even if you have changed your name.',
        }
      ]
    };

    this.extendLicenseModal.componentInstance.modalConfig = modalConfig;
    this.extendLicenseModal.componentInstance.userFullName = userFullName;
    this.extendLicenseModal.componentInstance.lookupMessages = this.lookupGeneric;
    setTimeout(() => {
      const modalEl = document.querySelector('.custom-license-extend-modal .modal-body');
      const checkboxEl = modalEl?.querySelector('input[type="checkbox"]') as HTMLInputElement;
      const signatureEl = modalEl?.querySelector('input[type="text"]') as HTMLInputElement;

      if (checkboxEl && signatureEl) {
        signatureEl.setAttribute('readonly', 'true');
        signatureEl.style.backgroundColor = '#F1F4F6';
        signatureEl.style.cursor = 'not-allowed';
        checkboxEl.addEventListener('change', () => {
          signatureEl.value = checkboxEl.checked ? 'Signed by Staff on behalf of Educator' : '';
          signatureEl.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }

      checkboxEl.addEventListener('change', () => {
        this.extendLicenseModal.componentInstance.userFullName = checkboxEl.checked ? 'Signed by Staff on behalf of Educator' : userFullName; // ✅ add this
        signatureEl.value = checkboxEl.checked ? 'Signed by Staff on behalf of Educator' : '';
        signatureEl.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }, 150);
    this.extendLicenseModal.result.then(
      (result) => {
        if (result && result.code === 'Y') {
          this.handleExtendLicenseSubmit(result.data);
        }
      },
      () => { /* dismissed */ }
    );
  }

  private handleExtendLicenseSubmit(data: any): void {
    const signature = data?.strFullName?.trim();
    if (!signature) {
      this.alertService.error('Signature is required');
      return;
    }

    this.loaderService.show();
    const licenseData = this.getLicenseData();
    const payload = {
      licenseNumber: licenseData?.license_number || this.licenseNumber || '',
      userId: this.personId ? Number(this.personId) : null,
      applicationNumber: licenseData?.application_id || '',
      expiryDate: licenseData?.intLicenseExtensionDuration || this.extensionDuration || '',
      signature: signature
    };

    this.httpService.patch(API_ENDPOINTS.EXTEND_LICENSE_EDUCATORPROFILE, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        async (response: any) => {
          this.loaderService.hide();
          if (response?.data?.success) {
            this.getTableAPI()
            this.alertService.success(this.lookupToast?.['T0053'] || 'License extended', true);
            await this.requestDashboardRefresh.emit();
            this.showReverseExtersion = this.educatorDashboardRow?.p_license_dtls?.some((dtl: any) => dtl.showreversebtn == true) ?? false;
            this.showCurrentLKExtendBtn = false;
          } else {
            this.alertService.error(response?.data?.message || 'Failed to extend license');
          }
        },
        (_error) => {
          this.loaderService.hide();
          this.alertService.error('An error occurred while extending the license');
        }
      );
  }

  private isLicenseExpired(expirationDate: string | Date | null | undefined): boolean {
    if (!expirationDate) return false;
    let parsedDate: Date;
    if (expirationDate instanceof Date) {
      parsedDate = new Date(expirationDate);
    } else {
      const raw = String(expirationDate).trim();
      if (!raw) return false;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
        const [month, day, year] = raw.split('/').map(Number);
        parsedDate = new Date(year, month - 1, day);
      } else {
        parsedDate = new Date(raw);
      }
    }
    if (Number.isNaN(parsedDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate.getTime() < today.getTime();
  }

  private getExtensionEligibilityStatus(expirationDate: string | Date | null | undefined, windowDays?: number): 'Eligible' | 'Not Eligible' {
    if (!expirationDate) return 'Not Eligible';
    const resolvedWindowDays = this.getResolvedExtensionWindowDays(windowDays);
    if (resolvedWindowDays <= 0) return 'Not Eligible';

    let parsedDate: Date;
    if (expirationDate instanceof Date) {
      parsedDate = new Date(expirationDate);
    } else {
      const raw = String(expirationDate).trim();
      if (!raw) return 'Not Eligible';
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
        const [month, day, year] = raw.split('/').map(Number);
        parsedDate = new Date(year, month - 1, day);
      } else {
        parsedDate = new Date(raw);
      }
    }
    if (Number.isNaN(parsedDate.getTime())) return 'Not Eligible';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsedDate.setHours(0, 0, 0, 0);
    const diffMs = parsedDate.getTime() - today.getTime();
    const daysUntilExpiration = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return daysUntilExpiration >= 0 && daysUntilExpiration <= resolvedWindowDays ? 'Eligible' : 'Not Eligible';
  }

  private getResolvedExtensionWindowDays(windowDays?: number): number {
    const explicitWindow = Number(windowDays);
    if (Number.isFinite(explicitWindow) && explicitWindow > 0) return explicitWindow;
    const licenseData = this.getLicenseData();
    const configuredWindow = Number(this.extensionDuration ?? licenseData?.intLicenseExtensionDuration);
    if (Number.isFinite(configuredWindow) && configuredWindow > 0) return configuredWindow;
    return 0;
  }

  private getExtensionFees() {
    // Fetch maximum OTP attempts
    this.manageProfileService.getBusinessRuleByKey('Licensing', 'license_expiration_fee')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log("RESPONSE:", response);
          if (response?.data?.data?.intValue) {
            this.licenseFee = response.data.data.intValue;
          }
        },
        error: (error) => {
          console.error('Error fetching maximum OTP attempts rule:', error);
        }
      });
  }


  private areAllRelevantStatusesIneligible(): boolean {
    const normalizeStatus = (value: any): string => String(value ?? '').trim().toLowerCase();
    const statuses: string[] = [];

    const licenseKinds = this.educatorDashboardRow?.p_licensekind || [];
    if (Array.isArray(licenseKinds)) {
      licenseKinds.forEach((row: any) => {
        const status = normalizeStatus(row?.extension_status);
        if (status === 'eligible' || status === 'Not Eligible') statuses.push(status);
      });
    }

    const licenseRequirements = this.educatorDashboardRow?.p_license_requirements || [];
    if (Array.isArray(licenseRequirements)) {
      licenseRequirements.forEach((row: any) => {
        const status = normalizeStatus(row?.extension_status);
        if (status === 'eligible' || status === 'Not Eligible') statuses.push(status);
      });
    }

    const licenseData = this.getLicenseData();
    const licenseStatus = normalizeStatus(this.getExtensionEligibilityStatus(licenseData?.expiry_date));
    if (licenseStatus === 'eligible' || licenseStatus === 'Not Eligible') statuses.push(licenseStatus);

    if (!statuses.length) return false;
    return statuses.every((status: string) => status === 'Not Eligible');
  }
  handleDateInputKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ];

    if (this.isAllowedKey(event, allowedKeys)) {
      this.handleAllowedKeys(event);
      return;
    }

    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart || 0;

    if (this.isLengthExceeded(input.value.length, selectionStart)) {
      event.preventDefault();
      return;
    }

    if (this.isSlashAllowed(event, selectionStart)) {
      return;
    }

    if (this.isInvalidInput(event)) {
      event.preventDefault();
      return;
    }

    if (this.isInvalidMonth(event, input, selectionStart)) {
      event.preventDefault();
      return;
    }

    if (this.isInvalidDay(event, input, selectionStart)) {
      event.preventDefault();
      return;
    }

    // this.handleDueDateChange(this.addProvisionForm)

    // const selectedDate = input.value;
    // const formattedDate = selectedDate instanceof Date
    //   ? selectedDate.toISOString().split('T')[0]
    //   : selectedDate;

    // if (formattedDate && this.autoCalculatedRequirementDueDate && formattedDate !== this.autoCalculatedRequirementDueDate
    // ) {
    //   this.showDueDateWarningModal = true;
    // }

    this.insertSlashIfNeeded(input, selectionStart, event);
  }
  private isAllowedKey(event: KeyboardEvent, allowedKeys: string[]): boolean {
    return allowedKeys.includes(event.key);
  }

  private handleAllowedKeys(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    console.log("suspect 1");
    if ((event.key === 'Backspace' || event.key === 'Delete') && input.value.length <= 10) {
      // this.addProvisionForm.patchValue({ dateOfBirth: newValue });
    }
  }

  private isLengthExceeded(valueLength: number, selectionStart: number): boolean {
    return valueLength >= 10 && selectionStart === valueLength;
  }

  private isSlashAllowed(event: KeyboardEvent, selectionStart: number): boolean {
    return event.key === '/' && (selectionStart === 2 || selectionStart === 5);
  }

  private isInvalidInput(event: KeyboardEvent): boolean {
    return !/^[0-9]$/.test(event.key);
  }

  private isInvalidMonth(event: KeyboardEvent, input: HTMLInputElement, selectionStart: number): boolean {
    if (selectionStart === 0) {
      return !['0', '1'].includes(event.key);
    } else if (selectionStart === 1) {
      const firstChar = input.value[0];
      return (firstChar === '0' && event.key === '0') || (firstChar === '1' && !['0', '1', '2'].includes(event.key));
    }
    return false;
  }

  private isInvalidDay(event: KeyboardEvent, input: HTMLInputElement, selectionStart: number): boolean {
    if (selectionStart === 3) {
      const month = input.value.slice(0, 2);
      return (
        (month === '02' && !['0', '1', '2'].includes(event.key)) ||
        (month !== '02' && !['0', '1', '2', '3'].includes(event.key))
      );
    } else if (selectionStart === 4) {
      const month = input.value.slice(0, 2);
      const dayFirstDigit = input.value[3];
      return (
        (['04', '06', '09', '11'].includes(month) && dayFirstDigit === '3' && event.key !== '0') ||
        (dayFirstDigit === '3' && !['0', '1'].includes(event.key)) ||
        (dayFirstDigit === '0' && event.key === '0')
      );
    }
    return false;
  }

  private insertSlashIfNeeded(input: HTMLInputElement, selectionStart: number, event: KeyboardEvent) {
    if ((selectionStart === 2 || selectionStart === 5) && input.value[selectionStart] !== '/') {
      const newValue = input.value.slice(0, selectionStart) + '/' + input.value.slice(selectionStart);
      input.value = newValue;
      this.addProvisionForm.patchValue({ dateOfBirth: newValue });
      input.setSelectionRange(selectionStart + 1, selectionStart + 1);
      event.preventDefault();
    }
  }

  private getLicenseProvisionByPersonId(): Promise<void> {
    return new Promise((resolve, reject) => {

      this.manageProfileService.getLicenseProvisionByPersonId(this.personId).subscribe({
        next: (res) => {
          resolve();
          console.log('lic provdata', res);
          this.ProvisionData = res?.data?.data?.data.filter(item => item.cd_license_provision !== null) || [];
          this.EndorsementData = res?.data?.data?.data.filter(item => item.cd_endor_provision !== null) || [];
          this.buildLicenseGrids();
        },
        error: (err) => {
          console.error('Error loading license details', err);
          reject(err);
        }
      });
    });
  }

  private fetchLicenseRequirementsByPersonId(): void {
    if (!this.personId) {
      console.warn('[LookupUserLicenseTab] No personId available, skipping fetchLicenseRequirementsByPersonId');
      return;
    }

    this.manageProfileService
      .getLicenseRequirementByPersonId(this.personId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          console.log('[LookupUserLicenseTab] fetchLicenseRequirementsByPersonId response:', res);
          const data = res?.data?.data?.data || res?.data?.data || res?.data || res?.rows || res || [];
          const rows = Array.isArray(data) ? data : [];
          this.buildLicenseRequirementsGridFromData(rows);
          const licenseInfo = this.educatorDashboardRow?.p_license_dtls || [];
          let parsedDate: Date;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          parsedDate = new Date(licenseInfo[0]?.expiry_date);
          if (parsedDate < today) {
            this.showCurrentLKExtendBtn = false;
            return;
          }
          this.showCurrentLKExtendBtn =
            (this.licenseRequirementsGridConfig.data?.some(
              (f) =>
                this.getExtensionEligibilityStatus(f.dueDate) === 'Eligible'
            ) || false) ||

            (this.currentLicenseKindsGridConfig.data?.some(
              (f) =>
                this.getExtensionEligibilityStatus(f.expirationDate) === 'Eligible'
            ) || false) ||

            (this.licenseProvisionsGridConfig.data?.some(
              (f) =>
                this.getExtensionEligibilityStatus(f.dueDate) === 'Eligible'
            ) || false) ||
            (this.endorsementProvisionsGridConfig.data?.some(
              (f) =>
                this.getExtensionEligibilityStatus(f.dueDate) === 'Eligible'
            ) || false) ||

            this.getExtensionEligibilityStatus(licenseInfo[0]?.expiry_date) === 'Eligible';
        },
        error: (err) => {
          console.error('[LookupUserLicenseTab] Error fetching license requirements:', err);
          // this.buildLicenseRequirementsGrid();

        }
      });
  }



  private deleteLicenseKindNew(): void {
    const user = JSON.parse(this.sessionStorage.getItem('user') || '{}');
    const userId = user?.id || user?.user_id || user?.userId;
    const payload = {
      person_license_endorsement_id: this.deleteItemData?.id,
      updated_by: userId ? Number(userId) : null
    };
    console.log('[deleteLicenseKind] Payload:', JSON.stringify(payload, null, 2));
    this.manageProfileService.deleteLicenseKindEndorsements(payload).pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          console.log('[deleteLicenseKind] Response:', res);
          this.alertService.success(this.lookupToast?.['T0040'] || 'Deleted Successfully.', true);
          this.closeDeleteModal();
          this.requestDashboardRefresh.emit();
          this.getLicenseKindEndorsementsByPersonId();
        },
        error: err => {
          console.error('[deleteLicenseKind] Error:', err);
          this.alertService.error(err?.error?.message || err?.message || 'Failed to delete license kind.');
          this.closeDeleteModal();
        }
      });
  }

  getDropdownlabel(value: any, result: any[]): string {
    if (value == null) {
      return '';
    }
    const status = result.find(
      (item: any) => item.value === value
    );
    return status ? status.label : '';
  }

private getLicenseKindEndorsementsByPersonId(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.manageProfileService.getLicenseKindEndorsementsByPersonId(this.personId).subscribe({
        next: (res) => {
          resolve();
          console.log('getLicenseKindEndorsementsByPersonId', res);
          this.getLicenseKindEndorsementsData = res?.data?.data?.data || [];
          // this.getLicenseKindEndorsementsData.map((item: any) => {
          //   //  const expirationDate = this.normalizeToDateOnly(item.expiration_date)
          //   const today = new Date();
          //   today.setHours(0, 0, 0, 0);

          //   const dueDateObj = new Date(item.expiration_date);
          //   dueDateObj.setHours(0, 0, 0, 0);
          //   item.endorsement_status = dueDateObj < today ? 'Inactive' : item.endorsement_status.toLowerCase() === 'active' ? 'Active' : 'Pending'

          // })
          const licenseInfo = this.educatorDashboardRow?.p_license_dtls || [];
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // ✅ Expired if ANY license's expiry_date has crossed today
          const isLicenseExpired = licenseInfo.some((lic: any) => {
            const expiryDate = new Date(lic.expiry_date);
            expiryDate.setHours(0, 0, 0, 0);
            return expiryDate < today;
          });

          this.getLicenseKindEndorsementsData.map((item: any) => {
            if (isLicenseExpired) {
              item.endorsement_status = 'Inactive';
              item.extension_status = 'Not Eligible';
              this.showReverseExtersion = false;
              this.showCurrentLKExtendBtn = false;
              return;
            }
            const dueDateObj = new Date(item.expiration_date);
            dueDateObj.setHours(0, 0, 0, 0);

              item.endorsement_status = item.endorsement_status?.toLowerCase() === 'inactive'
          ? 'Inactive'
          : (dueDateObj < today ? 'Inactive' : item.endorsement_status?.toLowerCase() === 'active' ? 'Active' : 'Pending');
        item.extension_status =  item.endorsement_status?.toLowerCase() === 'inactive' ? 'Not Eligible' : this.getExtensionEligibilityStatus(item.expiration_date)
   
            
    //         item.endorsement_status = dueDateObj < today
    //           ? 'Inactive'
    //           : item.endorsement_status.toLowerCase() === 'active'
    //             ? 'Active'
    //             : 'Pending';
    //              item.extension_status =  dueDateObj < today
    // ? 'Not Eligible'
    // : this.getExtensionEligibilityStatus(item.expiration_date);
          });


          // Active records
          const activeList = this.getLicenseKindEndorsementsData.filter((item: any) =>
            item?.endorsement_status?.toLowerCase() === 'active'
          );
          // Pending / Inactive / Others
          const historyList = this.getLicenseKindEndorsementsData.filter((item: any) =>
            !item?.endorsement_status ||
            item?.endorsement_status?.toLowerCase() !== 'active'
          );

          // Active records
          // const activeList = this.getLicenseKindEndorsementsData.filter((item: any) =>
          //   item?.endorsement_status?.toLowerCase() === 'active'
          // );
          // // Pending / Empty / Others
          // const historyList = this.getLicenseKindEndorsementsData.filter((item: any) =>
          //   !item?.endorsement_status ||
          //   item?.endorsement_status?.toLowerCase() !== 'active'
          // );
          // Bind to grids
          this.buildCurrentLicenseKindsGridNew(activeList);
          this.buildLicenseKindsHistoryGridNew(historyList);
        },
        error: (err) => {
          console.error('Error loading license details', err);
          reject(err);
        }
      });
    });
  }

  onSaveAddEndorsementNew(): void {
    if (this.addEndorsementForm.invalid) {
      this.addEndorsementForm.markAllAsTouched();
      this.alertService.error('Please fill in all required fields.');
      return;
    }

    // Get existing license data from educator dashboard
    const licenseInfo = this.educatorDashboardRow?.p_license_dtls || [];
    const existingLicense = Array.isArray(licenseInfo) && licenseInfo.length > 0 ? licenseInfo[0] : null;

    if (!existingLicense) {
      this.alertService.warn('No existing license information found. Cannot add endorsement.');
      return;
    }
    const raw = this.addEndorsementForm.value;
    const user = JSON.parse(this.sessionStorage.getItem('user') || '{}');
    const userId = user?.id || user?.user_id || user?.userId;
    const payload = {
      // User inputs from form
      person_id: this.personId ? Number(this.personId) : null,
      cd_license_type: raw.licenseType,
      cd_license_kind: raw.licenseKind,
      cd_endorsement_grade: raw.endorsementGrade,
      cd_endorsement_pathway: raw.endorsementPathway,
      original_effective_date: null,
      expiration_date: null,
      effective_date: this.formatDateForGrid(raw.effectiveDate),
      endorsement_status: 'Pending',
      extension_status: '',
      user_id: userId ? Number(userId) : null
    }

    this.manageProfileService.saveLicenseKindEndorsements(payload).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: res => {
        const success = res?.data?.success === true;
        if (success) {
          this.alertService.success(this.lookupToast?.['T0005'] || 'Added Successfully.', true);
          // Auto-dismiss alert after 5 seconds
          setTimeout(() => {
            // Alert will be dismissed automatically by the alert service
          }, 5000);
          this.showAddEndorsementForm = false;
          this.addEndorsementForm.reset();
          // Request parent to refresh educator dashboard data from API
          // When new data arrives via @Input() row setter, buildLicenseGrids() will be called automatically
          this.requestDashboardRefresh.emit();
          this.getLicenseKindEndorsementsByPersonId();
        } else {
          this.alertService.error(res?.data?.message || res?.message || 'Failed to add endorsement.');
        }
      },
      error: err => {
        this.alertService.error(err?.error?.message || err?.message || 'Failed to add endorsement.');
      }
    });
  }

  updateLicenseKindNew(): void {
    if (this.editLicenseKindForm.invalid) {
      this.editLicenseKindForm.markAllAsTouched();
      this.alertService.error('Please fill in all required fields.');
      return;
    }
    console.log('[updateLicenseKind] Updating license kind with data:', this.editLicenseKindForm.value);
    console.log('[updateLicenseKind] Selected row data:', this.selectedLicenseKind);
    const raw = this.editLicenseKindForm.value;
    const user = JSON.parse(this.sessionStorage.getItem('user') || '{}');
    const userId = user?.id || user?.user_id || user?.userId;
    // Build payload with primary key for unique identification
    const payload = {
      person_id: this.personId ? Number(this.personId) : null,
      cd_license_type: raw.licenseType,
      cd_license_kind: raw.licenseKind,
      cd_endorsement_grade: raw.endorsementGrade,
      cd_endorsement_pathway: raw.endorsementPathway,
      person_license_endorsement_id: this.selectedLicenseKind?.id,  // person_license_endorsement_id,
      original_effective_date: '',
      effective_date: '',
      expiration_date: '',
      endorsement_status: 'Pending',
      extension_status: '',
      user_id: userId ? Number(userId) : null
    };

    this.manageProfileService.postLicenseKindEndorsements(payload).pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          console.log('[updateLicenseKind] Response:', res);
          this.alertService.success(this.lookupToast?.['T0020'] || 'Updated Successfully.', true);
          this.closeLicenseKindModal();
          // Refresh data from backend
          this.requestDashboardRefresh.emit();
          this.getLicenseKindEndorsementsByPersonId();
        },
        error: err => {
          console.error('[updateLicenseKind] Error:', err);
          this.alertService.error(err?.error?.message || err?.message || 'Failed to update license kind.');
        }
      });
  }



  private buildCurrentLicenseKindsGridNew(licenseKindEndorsements: any): void {
    const headers = this.licenseMetadataHeaders['currentLicenseKinds'] || [];
    console.log('[DEBUG] buildCurrentLicenseKindsGridNew headers[2].dataField:', headers[2]?.dataField);
    console.log('[DEBUG] raw licenseKindEndorsements:', JSON.stringify(licenseKindEndorsements?.map((i: any) => ({ endorsement: i.endorsement, cd_endorsement: i.cd_endorsement, cd_license_kind: i.cd_license_kind }))));
    console.log('[DEBUG] endorsementTypeOptions count:', this.endorsementTypeOptions?.length);

    const rows = licenseKindEndorsements && licenseKindEndorsements.map((item: any, index: number) => {
      const expirationDate = this.normalizeToDateOnly(item.expiration_date)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDateObj = new Date(item.expiration_date);
      dueDateObj.setHours(0, 0, 0, 0);
      const endorsementGrade = item.endorsement || this.getDropdownlabel(item.cd_endorsement, this.endorsementTypeOptions) || this.getDropdownlabel(Number(item.cd_endorsement), this.endorsementTypeOptions);

      return {
        id: item.person_license_endorsement_id || item.id || index + 1,
        licenseType: this.getDropdownlabel(item.cd_license_type, this.licenseTypeOptions),
        licenseKind: this.getDropdownlabel(item.cd_license_kind, this.licenseKindOptions),
        endorsementGrade,
        endorsementGradeArray: endorsementGrade ? [endorsementGrade] : [],
        endorsementPathway: this.getDropdownlabel(item.endorsement_pathway, this.endorsementPathwayOptions) || this.getDropdownlabel(Number(item.endorsement_pathway), this.endorsementPathwayOptions),
        originalEffectiveDate: this.formatDateForGrid(item.original_effective_date || ''),
        effectiveDate: this.formatDateForGrid(item.effective_date || ''),
        expirationDate: this.formatDateForGrid(item.expiration_date || ''),
        // endorsementStatus: dueDateObj < today ? 'Inactive' : item.endorsement_status.toLowerCase() === 'active' ? 'Active' : 'Pending',
        endorsementStatus: item.endorsement_status || '',
        extensionStatus: item.extension_status || ''
      };
    });

    // this.showCurrentLKExtendBtn = rows.some(
    //   f => f.extensionStatus === 'Eligible'
    // );
    const licenseInfo = this.educatorDashboardRow?.p_license_dtls || [];

    // this.showCurrentLKExtendBtn = rows.some(
    //   (f) =>
    //     this.getExtensionEligibilityStatus(licenseInfo[0]?.expiry_date) === 'Eligible' ||
    //     this.getExtensionEligibilityStatus(f.expirationDate) === 'Eligible'
    // );

    this.currentLicenseKindsGridConfig = this.currentLicenseKindsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.currentLicenseKindsColumnVisibility,
      _changeKey: Date.now()
    });

    // Populate Provisions and Requirements dropdowns from ACTIVE licenses only
    // This ensures these dropdowns only show values from current active licenses
    // this.populateProvisionAndRequirementDropdowns(rows);

  
  }

  private buildLicenseKindsHistoryGridNew(licenseKindEndorsements: any[]): void {
    const headers = this.licenseMetadataHeaders['licenseKindsHistory'] || [];

    const rows = licenseKindEndorsements && licenseKindEndorsements.map((item: any, index: number) => {
      const expirationDate = this.normalizeToDateOnly(item.expiration_date)
      return {
        id: item.person_license_endorsement_id || item.id || index + 1,
        licenseType: this.getDropdownlabel(item.cd_license_type, this.licenseTypeOptions),
        licenseKind: this.getDropdownlabel(item.cd_license_kind, this.licenseKindOptions),
        endorsementGrades: this.getDropdownlabel(item.cd_endorsement, this.endorsementTypeOptions),
        endorsementPathway: this.getDropdownlabel(item.endorsement_pathway, this.endorsementPathwayOptions),
        originalEffectiveDate: this.formatDateForGrid(item.original_effective_date || ''),
        effectiveDate: this.formatDateForGrid(item.effective_date || ''),
        expirationDate: this.formatDateForGrid(item.expiration_date || ''),
        endorsementStatus: item.endorsement_status || '',
        status: item.endorsement_status == 'Inactive' ? 'Inactive' : 'Pending',
        extensionStatus: item.extension_status || ''
      };
    });

    this.licenseKindsHistoryGridConfig = this.licenseKindsHistoryGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.licenseKindsHistoryColumnVisibility,
      _changeKey: Date.now()
    });
  }

  openSendLicenseByEmailModal() {
    // Open NgbModal
    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'md',
      windowClass: 'license-kind-modal custom-license-kind-modal'
    };
    this.customModal = this.modalService.open(this.sendLicenseByEmailModal, options);

    const person = this.firstPersonInfo();
    this.sendLicenseByEmailForm.patchValue({
      cansendtootheremailstext: person?.primary_email || ''
    });
  }

  closeSendLicenseByEmailModal() {
    if (this.customModal) {
      this.customModal.close();
      this.customModal = null;
    }
  }

  sentLicenseByEmail(): Promise<void> {
    console.log(this.sendLicenseByEmailForm.value);
    const formValues = this.sendLicenseByEmailForm.value;
    return new Promise((resolve, reject) => {
      this.manageProfileService.sendLicenseEmail(this.personId, formValues?.cansendtootheremailstext).subscribe({
        next: (res) => {
          resolve();
          console.log('send License Email', res);
          this.closeSendLicenseByEmailModal();
          this.alertService.success('Email Sent Successfully.', true);
        },
        error: (err) => {
          console.error('Error loading send License Email', err);
          this.alertService.error('Error sending email.', true);
          reject(err);
        }
      });
    });

  }

  private firstPersonInfo(): any | null {
    const list = this.educatorDashboardRow?.p_person_info;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0];
  }

  private firstLicense(): any | null {
    const list = this.educatorDashboardRow?.p_license_dtls;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0];
  }

  get hasValidLicense(): boolean {
    return this.licenseNumber !== this.overviewEnDash;
  }

  get showLicenseSentByEmailButton(): boolean {
    if (!this.hasValidLicense) return false;

    const lic = this.firstLicense();
    if (!lic) return false;

    // ✅ Check status
    const isActive =
      typeof lic.is_active === 'boolean'
        ? lic.is_active
        : String(lic.license_type).toLowerCase() === 'active';

    if (!isActive) return false;

    const validStatuses = [
      'Partially Approved + BG Approved',
      'Partially Approved',
      'Approved',
      'Analyst Approved + BG Approved'
    ];


    const application = this.educatorDashboardRow.p_application[0];

    // const isActive1 =
    //   validStatuses.includes(application?.app_status) &&
    //   application?.background_check_status === 'Approved' || 'null';

    let isActive1 = false;
    if (application?.bg_required === true) {
      isActive1 =
        validStatuses.includes(application?.app_status) &&
        (
          application?.background_check_status === 'Approved' || application?.background_check_status == 'Clear' ||
          application?.background_check_status === 'null'
        );
    } else {
      isActive1 = validStatuses.includes(application?.app_status);
    }

    if (!isActive1) return false;

    // ✅ Check expiry date
    const raw = lic.expiry_date;
    if (!raw) return false;

    const expiryDate = new Date(raw);
    if (Number.isNaN(expiryDate.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return expiryDate > today;
  }

  licensekindName(value: any): string {
    if (value == null) {
      return '';
    }
    const status = this.licenseKindOptions.find(
      (item: any) => Number(item.value) === Number(value)
    );
    return status ? status.label : '';
  }

  endorsementOptions(value: any): string {
    if (value == null) {
      return '';
    }
    const status = this.endorsementTypeOptions.find(
      (item: any) => Number(item.value) === Number(value)
    );
    return status ? status.label : '';
  }

  formatDateLocal(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split('T')[0]; // "YYYY-MM-DD"
  }

  normalizeToDateOnly(input: string | Date): string {
    const d = new Date(input);

    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  get licenseNumber(): string {
    const list = this.educatorDashboardRow?.p_license_dtls;
    if (!Array.isArray(list) || list.length === 0) {
      return this.overviewEnDash;
    }
    const nums: string[] = [];
    for (const row of list) {
      if (row?.license_number == null) continue;
      const n = String(row.license_number).trim();
      if (n.length > 0) {
        nums.push(n);
      }
    }
    if (nums.length === 0) {
      return this.overviewEnDash;
    }
    return [...new Set(nums)].join(', ');
  }


  getLicenseKindEndorsementsDataGrid() {

    return this.getLicenseKindEndorsementsData.map((item: any, index: number) => {
      const expirationDate = this.normalizeToDateOnly(item.expiration_date)
      return {
        id: item.person_license_endorsement_id || item.id || index + 1,
        licenseType: this.getDropdownlabel(item.cd_license_type, this.licenseTypeOptions),
        licenseKind: this.getDropdownlabel(item.cd_license_kind, this.licenseKindOptions),
        endorsementGrade: this.getDropdownlabel(item.cd_endorsement, this.endorsementTypeOptions),
        endorsementPathway: this.getDropdownlabel(item.endorsement_pathway, this.endorsementPathwayOptions),
        originalEffectiveDate: this.formatDateForGrid(item.original_effective_date || ''),
        effectiveDate: this.formatDateForGrid(item.effective_date || ''),
        expirationDate: this.formatDateForGrid(item.expiration_date || ''),
        endorsementStatus: item.endorsement_status.toLowerCase() === 'active' ? 'Active' : 'Pending',
        extensionStatus: expirationDate ? this.getExtensionEligibilityStatus(expirationDate) : ''
      };
    });
  }

  onRequirementLicenseKindChangeEdit(): void {
    this.handleMultiSelectChange(this.editLicenseRequirementForm, 'licenseKind', 'licenseKind');
  }

  onRequirementEndorsementChangeEdit(): void {
    this.handleMultiSelectChange(this.editLicenseRequirementForm, 'endorsement', 'endorsement');
  }

  onRequirementDueDateChangeEdit(): void {
    this.handleDueDateChange(this.editLicenseRequirementForm);
  }

  // onEndorsementProvisionEndorsementChangeAdd(): void {
  //   this.handleMultiSelectChange(this.addEndorsementProvisionForm, 'selectedEndorsements', 'endorsement');
  // }

  onEndorsementProvisionDueDateChangeAdd(): void {
    this.handleDueDateChange(this.addEndorsementProvisionForm);
  }

  // onEndorsementProvisionEndorsementChangeEdit(): void {
  //   this.handleMultiSelectChange(this.editEndorsementProvisionForm, 'endorsement', 'endorsement');
  // }

  onEndorsementProvisionDueDateChangeEdit(): void {
    this.handleDueDateChange(this.editEndorsementProvisionForm);
  }

  onProvisionEndorsementChangeAdd(): void {
    this.handleMultiSelectChange(this.addProvisionForm, 'selectedLicenseKinds', 'licenseKind');
  }

  onProvisionDueDateChangeAdd(): void {
    this.handleDueDateChange(this.addProvisionForm);
  }

  onProvisionEndorsementChangeEdit(): void {
    this.handleMultiSelectChange(this.editLicenseProvisionForm, 'licenseKind', 'licenseKind');
  }

  onProvisionDueDateChangeEdit(): void {
    this.handleDueDateChange(this.editLicenseProvisionForm);
  }


  // ================= COMMON METHODS =================

  // ✅ Common multi-select handler
  private handleMultiSelectChange(form: FormGroup, controlName: string, type: 'endorsement' | 'licenseKind'): void {
    const control = form.get(controlName);
    const selectedIds = control?.value || [];
    const dueDate = this.getMinimumExpirationDate(selectedIds, type);
    if (dueDate) {
      this.autoCalculatedRequirementDueDate = dueDate;
      form.get('dueDate')?.setValue(moment(dueDate).format('MM/DD/YYYY'));
      form.get('dueDate')?.markAsDirty();
    } else {
      this.autoCalculatedRequirementDueDate = '';
      form.get('dueDate')?.setValue(null);
      form.get('dueDate')?.markAsDirty();
    }

  }

  // ✅ Common due date handler
  private handleDueDateChange(form: FormGroup): void {
    const selectedDate = form.get('dueDate')?.value;
    const formattedDate = selectedDate instanceof Date
      ? selectedDate.toISOString().split('T')[0]
      : selectedDate;

    if (formattedDate && this.autoCalculatedRequirementDueDate && formattedDate !== this.autoCalculatedRequirementDueDate
    ) {
      this.showDueDateWarningModal = true;
    }
  }

  getSelectLabel(
    val: any,
    options: { label: string; value: any }[]
  ): string {
    if (!val || !options?.length) return '';

    const found = options.find(o => String(o.value) === String(val));
    if (!found?.label) return '';

    const trimmedLabel = found.label.trim();
    const charCount = trimmedLabel.length;

    return charCount > 30 ? found.label : '';
  }

  getReqTypeLabel(
    val: any,
    options: { label: string; value: any }[]
  ): string {
    if (!val || !options?.length) return '';

    const found = options.find(o => String(o.value) === String(val));
    if (!found?.label) return '';

    const trimmedLabel = found.label.trim();
    const charCount = trimmedLabel.length;

    return charCount > 19 ? found.label : '';
  }

  getTableAPI() {
    this.getLicenseKindEndorsementsByPersonId()
    this.getLicenseProvisionByPersonId();
    this.fetchLicenseRequirementsByPersonId();
  }

  get getlicenseKindFilterOptions() {
    const tableKeys = new Set(
      this.getLicenseKindEndorsementsData.map(
        (item: any) => item.cd_license_kind
      )
    );
    const filteredLicenseKindOptions = this.licenseKindOptions.filter(
      option => tableKeys.has(option.value)
    );

    return filteredLicenseKindOptions
  }

  get getEndorsementTypeFilterOptions() {
    const tableKeys = new Set(
      this.getLicenseKindEndorsementsData.map(
        (item: any) => item.cd_endorsement
      )
    );
    const filteredEndorsementTypeOptions = this.endorsementTypeOptions.filter(
      option => tableKeys.has(option.value)
    );

    return filteredEndorsementTypeOptions
  }

  onProvDueDateChange(
  value: any,
  licenseIndex?: number | null,
  context?: 'prolickind' | 'editprolickind' | 'prolicendo' | 'editprolicendo' | 'prolicreq' | 'lickind' | null
): void {
  void licenseIndex;
  const selectedDueDate = this.normalizeDateToUsFormat(value);

  if (context === 'prolicreq') {
    if (!this.autoCalculatedRequirementDueDate) return;
    const systemDate = moment(this.autoCalculatedRequirementDueDate).format('MM/DD/YYYY');
    if (selectedDueDate && selectedDueDate !== systemDate) {
      this.showDueDateWarningModal = true;
    }
    return;
  }

  if (context === 'prolicendo') {
    if (!this.autoCalculatedRequirementDueDate) return;
    const systemDate = moment(this.autoCalculatedRequirementDueDate).format('MM/DD/YYYY');
    if (selectedDueDate && selectedDueDate !== systemDate) {
      this.showDueDateWarningModal = true;
    }
    return;
  }

  if (context === 'editprolicendo') {
    if (!this.autoCalculatedRequirementDueDate) return;
    const systemDate = moment(this.autoCalculatedRequirementDueDate).format('MM/DD/YYYY');
    if (selectedDueDate && selectedDueDate !== systemDate) {
      this.showDueDateWarningModal = true;
    }
    return;
  }

  const systemDueDate = this.getSystemCalculatedDueDate(context);

  if (context === 'editprolickind') {
    const provisionValue = this.editCombinedProvisionForm?.controls['provision']?.value;
    if (provisionValue == null || provisionValue === '') return;
    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    if (!autoDate) return;
    if (selectedDueDate && selectedDueDate !== autoDate) {
      this.showDueDateWarningModal = true;
    }
    return;
  }

  if (context === 'prolickind') {
    const provisionValue = this.addCombinedProvisionForm?.controls['provision']?.value;
    if (provisionValue == null || provisionValue === '') return;
    const autoDate = this.calculateProvisionBasedDueDate(provisionValue);
    if (!autoDate) return;
    if (selectedDueDate && selectedDueDate !== autoDate) {
      this.showDueDateWarningModal = true;
    }
    return;
  }

  const provisionValue = this.addProvisionForm?.controls['provision']?.value;

  if (provisionValue == '' || provisionValue == null)
    return;

  if (this.expirationDate == selectedDueDate) {
    return;
  }

  this.showDueDateWarningModal = true;
}

  private normalizeDateToUsFormat(value: any): string | null {
    const resolved = value?.target?.value ?? value?.value ?? value;
    if (!resolved) {
      return null;
    }

    if (resolved instanceof Date && !isNaN(resolved.getTime())) {
      return moment(resolved).format('MM/DD/YYYY');
    }

    const raw = String(resolved).trim();
    if (!raw) {
      return null;
    }

    const strictUs = moment(raw, 'MM/DD/YYYY', true);
    if (strictUs.isValid()) {
      return strictUs.format('MM/DD/YYYY');
    }

    const parsedDate = moment(raw, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (parsedDate.isValid()) {
      return parsedDate.format('MM/DD/YYYY');
    }

    return null;
  }

  formatDateInput(event: any, control?: AbstractControl | null) {
    const domEvent = event?.originalEvent || event;
    const target = domEvent?.target || event?.target;
    if (!target) {
      return;
    }

    const inputType = String(domEvent?.inputType || '');
    const isDeleteAction = inputType.startsWith('delete');
    const currentValue = String(target.value || '');

    let value = '';
    if (isDeleteAction) {
      // Keep user deletion as-is; do not re-insert "/" while backspacing.
      value = currentValue.replace(/[^\d/]/g, '').slice(0, 10);
    } else {
      const digitsOnly = currentValue.replace(/\D/g, '').slice(0, 8);
      if (digitsOnly.length <= 2) {
        value = digitsOnly;
      } else if (digitsOnly.length <= 4) {
        value = `${digitsOnly.substring(0, 2)}/${digitsOnly.substring(2)}`;
      } else {
        value = `${digitsOnly.substring(0, 2)}/${digitsOnly.substring(2, 4)}/${digitsOnly.substring(4)}`;
      }
    }

    target.value = value;
    if (control) {
      control.setValue(value, { emitEvent: false });
      control.markAsDirty();
      control.updateValueAndValidity({ emitEvent: false });
    }
  }



  private getSystemCalculatedDueDate(
    context?: 'prolickind' | 'editprolickind' | 'prolicendo' | 'editprolicendo' | 'prolicreq' | 'lickind' | null
  ): string | null {
    switch (context) {
      case 'prolickind':
        return this.calculateProvisionBasedDueDate(this.addProvisionForm?.get('provision')?.value);
        return null;
    }
  }

  openDatePickerForControl(
    event?: Event,
    control?: AbstractControl | null,
    inputElementId?: string,
    maxDateIso?: string,
    dueDateContext?: 'prolickind' | 'editprolickind' | 'prolicendo' | 'editprolicendo' | 'prolicreq' | 'lickind' | null,
    index?: null
  ): void {
    if (event) {
      event.stopPropagation();
    }

    const anchorInput = inputElementId ? document.getElementById(inputElementId) : null;
    if (!anchorInput) return;

    const rect = anchorInput.getBoundingClientRect();

    const openModal = document.querySelector('ngb-modal-window .modal-dialog') as HTMLElement
      || document.querySelector('.modal.show .modal-dialog') as HTMLElement
      || document.querySelector('.modal-dialog') as HTMLElement;

    let modalRect = {
      left: 0,
      right: window.innerWidth,
      top: 0,
      bottom: window.innerHeight,
      width: window.innerWidth
    };

    if (openModal) {
      const mr = openModal.getBoundingClientRect();
      if (mr.width > 0 && mr.height > 0) {
        modalRect = {
          left: mr.left,
          right: mr.right,
          top: mr.top,
          bottom: mr.bottom,
          width: mr.width
        };
      }
    }

    const calendarHeight = 320;
    const calendarWidth = Math.min(280, modalRect.width - 20);

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let topPos: number;
    if (spaceBelow >= calendarHeight) {
      topPos = rect.bottom;
    } else if (spaceAbove >= calendarHeight) {
      topPos = rect.top - calendarHeight;
    } else {
      topPos = spaceBelow >= spaceAbove
        ? rect.bottom
        : rect.top - calendarHeight;
    }

    let leftPos = rect.left;
    if (leftPos + calendarWidth > modalRect.right - 5) {
      leftPos = modalRect.right - calendarWidth - 5;
    }
    if (leftPos < modalRect.left + 5) {
      leftPos = modalRect.left + 5;
    }

    const dateInput = document.createElement('input');
    dateInput.type = 'date';
    dateInput.style.cssText = `
      position: fixed;
      top: ${topPos}px;
      left: ${leftPos}px;
      width: ${rect.width}px;
      height: 1px;
      opacity: 0.01;
      border: none;
      padding: 0;
      margin: 0;
      z-index: 999999;
      pointer-events: auto;
      font-size: 16px;
    `;

    dateInput.min = new Date().toISOString().split('T')[0];
    if (maxDateIso) {
      dateInput.max = maxDateIso;
    }

    const currentValue = control?.value;
    if (typeof currentValue === 'string' && currentValue.includes('/')) {
      const parts = currentValue.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        dateInput.value = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    }

    document.body.appendChild(dateInput);

    const cleanup = () => {
      if (document.body.contains(dateInput)) {
        dateInput.removeEventListener('change', changeHandler);
        dateInput.removeEventListener('blur', blurHandler);
        document.body.removeChild(dateInput);
      }
    };

    const changeHandler = (changeEvent: any) => {
      const selectedDate = changeEvent.target.value;
      if (selectedDate && control) {
        const [year, month, day] = selectedDate.split('-');
        const formattedDate = `${month}/${day}/${year}`;
        control.setValue(formattedDate);
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
        if (dueDateContext) {
          this.onProvDueDateChange(formattedDate, index, dueDateContext);
        }
      }
      cleanup();
    };

    const blurHandler = () => setTimeout(cleanup, 150);

    dateInput.addEventListener('change', changeHandler);
    dateInput.addEventListener('blur', blurHandler);

    setTimeout(() => {
      dateInput.focus();
      try {
        const pickerFn = (dateInput as any).showPicker;
        if (typeof pickerFn === 'function') {
          pickerFn.call(dateInput);
        } else {
          (dateInput as HTMLInputElement).click();
        }
      } catch {
        (dateInput as HTMLInputElement).click();
      }
    }, 0);
  }

  private getLicKindDeletedProvisions(): void {
    const headers = this.licenseMetadataHeaders['licenseProvisions'] || [];

    // Get real data from p_license_provision
    const licenseProvisions = this.ProvisionData || [];

    console.log('[LookupUserLicenseTab] License Provisions data:', licenseProvisions);
    console.log('[LookupUserLicenseTab] License Provisions count:', Array.isArray(licenseProvisions) ? licenseProvisions.length : 'Not an array');

    // Map API data to   grid rows
    const rows = Array.isArray(licenseProvisions) ? licenseProvisions.map((item: any, index: number) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDateObj = new Date(item.license_due_date);
      dueDateObj.setHours(0, 0, 0, 0);

      return {
        id: item.person_license_provision_id || index + 1,  // Use actual DB primary key from API
        provisionId: item.person_license_provision_id,      // Store primary key separately
        person_license_provision_id: item.person_license_provision_id,
        applicationId: item.old_id,              // Store application_id
        provision: this.licenseProvTypedataCopy?.find((opt: any) => opt.value == item.cd_license_provision)?.label || '',
        licenseKind: item.cd_license_kind
          ?.map((id: number) => this.licensekindName(id))
          .join(', '),
        dueDate: this.formatDateForGrid(item.license_due_date || ''),
        resolvedDate: this.formatDateForGrid(item.license_resolved_date || ''),
        additionalText: item.license_additional_text || item.additional_text || '',
        //  status: item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending',
        status: dueDateObj < today ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending',
        extensionStatus: this.getExtensionEligibilityStatus(item.license_due_date)
      }
    }) : [];

    if (rows?.length > 0) {
      const provisionSet = new Set(
        rows.map(a2 => a2.provision)
      );

      this.licenseProvTypedata = this.licenseProvTypedataCopy.filter(a1 =>
        !provisionSet.has(a1.label)
      );

    }
    else {
      this.licenseProvTypedata = this.licenseProvTypedataCopy;
    }


    this.licenseProvisionsGridConfig = this.licenseProvisionsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.licenseProvisionsColumnVisibility,
      _changeKey: Date.now()
    });
  }

  private getEndoDeletedProvisions(): void {
    const headers = this.licenseMetadataHeaders['endorsementProvisions'] || [];

    // Get real data from p_end_provision
    const endorsementProvisions = this.EndorsementData || [];

    // Map API data to grid rows
    const rows = Array.isArray(endorsementProvisions) ? endorsementProvisions.map((item: any, index: number) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDateObj = new Date(item.endorsement_due_date);
      dueDateObj.setHours(0, 0, 0, 0);
      return {
        id: item.person_license_provision_id || index + 1,  // Use actual DB primary key from API
        provisionId: item.person_license_provision_id,           // Store primary key separately
        applicationId: item.application_id,
        person_license_provision_id: item.person_license_provision_id,
        provision: this.licenseEndoProvTypedataCopy?.find((opt: any) => opt.value == item.cd_endor_provision)?.label || '',
        endorsement: item.cd_endoresement
          ?.map((id: number) => this.endorsementOptions(id.toString()))
          .join(', '),
        endorsementIds: item.cd_endoresement || [],
        dueDate: this.formatDateForGrid(item.endorsement_due_date || ''),
        resolvedDate: this.formatDateForGrid(item.endorsement_resolved_date || ''),
        additionalText: item.endorsement_additional_text || item.additional_text || '',
        //  status: item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending',
        status: dueDateObj < today ? 'Inactive' : item.cd_status?.toLowerCase() === 'active' ? 'Active' : 'Pending',
        extensionStatus: this.getExtensionEligibilityStatus(item.endorsement_due_date)
      }
    }) : [];


    if (rows?.length > 0) {
      const provisionSet = new Set(
        rows.map(a2 => a2.provision)
      );

      this.licenseEndoProvTypedata = this.licenseEndoProvTypedataCopy.filter(a1 =>
        !provisionSet.has(a1.label)
      );

    }
    else {
      this.licenseEndoProvTypedata = this.licenseEndoProvTypedataCopy;
    }


    this.endorsementProvisionsGridConfig = this.endorsementProvisionsGridConfig.updateConfig({
      customHeaders: headers,
      data: rows,
      visibleColumns: this.endorsementProvisionsColumnVisibility,
      _changeKey: Date.now()
    });
  }

  private toLocalIsoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

onEndorsementProvisionEndorsementChangeAdd(): void {
  const selectedIds = this.addEndorsementProvisionForm.get('selectedEndorsements')?.value || [];
  const dueDate = this.getMinimumExpirationDate(selectedIds, 'endorsement');
  if (dueDate) {
    this.autoCalculatedRequirementDueDate = dueDate;
    this.addEndorsementProvisionForm.get('dueDate')?.setValue(
      moment(dueDate).format('MM/DD/YYYY')
    );
  } else {
    this.autoCalculatedRequirementDueDate = '';
    this.addEndorsementProvisionForm.get('dueDate')?.setValue(null);
  }
}

onEndorsementProvisionEndorsementChangeEdit(): void {
  const selectedIds = this.editEndorsementProvisionForm.get('endorsement')?.value || [];
  const dueDate = this.getMinimumExpirationDate(selectedIds, 'endorsement');
  if (dueDate) {
    const formatted = moment(dueDate).format('MM/DD/YYYY');
    this.autoCalculatedRequirementDueDate = formatted;
    this.editEndorsementProvisionForm.get('dueDate')?.setValue(formatted);
    this.editEndorsementProvisionForm.get('dueDate')?.markAsDirty();
  } else {
    this.autoCalculatedRequirementDueDate = '';
    this.editEndorsementProvisionForm.get('dueDate')?.setValue(null);
  }
}

readonly licenseKindStatusColorOverrides: Record<string, string> = {
    'Active': 'status-badge-green',
    'pending': 'status-badge-yellow',
    'inactive': 'status-badge-red',
    'not eligible': 'status-badge-red',
    'Not Eligible': 'status-badge-red'

  };

  private formatStatusText(value: string | undefined): string {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  // private applyCorrelationLogic(): void {
  //   if (!Array.isArray(this.licenseGridRows) || !Array.isArray(this.licenseKindGridRows)) {
  //     return;
  //   }

  //   // For each license kind, find matching endorsement and apply correlation
  //   this.licenseKindGridRows.forEach((kindRow: any) => {
  //     const matchingEndo = this.licenseGridRows.find((endoRow: any) =>
  //       endoRow.licenseType === kindRow.licenseType &&
  //       endoRow.licenseKind === kindRow.licenseKind &&
  //       endoRow.endorsementGrade === kindRow.endorsementGrade
  //     );
  //     if (matchingEndo && (matchingEndo.endorsementStatus || '').toLowerCase() === 'inactive') {
  //       kindRow.licenseKindStatus = 'Inactive';
  //       kindRow.extensionStatus = 'Ineligible';
  //     }
  //   });

  //   // For each endorsement, find matching kind and apply correlation
  //   this.licenseGridRows.forEach((endoRow: any) => {
  //     const matchingKind = this.licenseKindGridRows.find((kindRow: any) =>
  //       kindRow.licenseType === endoRow.licenseType &&
  //       kindRow.licenseKind === endoRow.licenseKind &&
  //       kindRow.endorsementGrade === endoRow.endorsementGrade
  //     );
  //     if (matchingKind && (matchingKind.licenseKindStatus || '').toLowerCase() === 'inactive') {
  //       endoRow.endorsementStatus = 'Inactive';
  //       endoRow.extensionStatus = 'Ineligible';
  //     }
  //   });
  // }

  // /**
  //  * Calculate final license status based on grid data
  //  * Logic: License is 'Inactive' ONLY if ALL endorsements AND ALL license kinds are 'Inactive'
  //  * Otherwise: 'Active'
  //  */
  // private calculateLicenseStatus(gridLicenseRows: any[], gridLicenseKindRows: any[]): 'Active' | 'Inactive' {
  //   // Check if ALL endorsements are Inactive
  //   const allEndorsementsInactive = Array.isArray(gridLicenseRows) && gridLicenseRows.length > 0
  //     ? gridLicenseRows.every((row: any) => (row?.endorsementStatus || '').toLowerCase() === 'inactive')
  //     : true;

  //   // Check if ALL license kinds are Inactive
  //   const allLicenseKindsInactive = Array.isArray(gridLicenseKindRows) && gridLicenseKindRows.length > 0
  //     ? gridLicenseKindRows.every((row: any) => (row?.licenseKindStatus || '').toLowerCase() === 'inactive')
  //     : true;

  //   // License is Inactive ONLY if ALL items in both arrays are Inactive
  //   return (allEndorsementsInactive && allLicenseKindsInactive) ? 'Inactive' : 'Active';
  // }

  // /**
  //  * Perform all status calculations and update grid data
  //  * Called after grids are built with data
  //  */
  // private performStatusCalculations(): void {
  //   // Step 1: Apply correlation logic between kinds and endorsements
  //   this.applyCorrelationLogic();

  //   // Step 2: Calculate overall license status
  //   this.calculatedLicenseStatus = this.calculateLicenseStatus(
  //     this.licenseGridRows,
  //     this.licenseKindGridRows
  //   );

  //   // Step 3: Emit calculated status to parent component
  //   this.licenseStatusChanged.emit(this.calculatedLicenseStatus);

  //   console.log('[LookupUserLicenseTab] Status Calculations Complete:',
  //     { calculatedLicenseStatus: this.calculatedLicenseStatus });
  // }


}
